/**
 * Lightweight i18n translations for the B2C storefront.
 * Supports Vietnamese (vi) and English (en).
 */
import type { Locale } from "./locale";

const translations: Record<string, Record<Locale, string>> = {
  // ─── Announcements ───
  freeShipping: {
    vi: "MIỄN PHÍ VẬN CHUYỂN ĐƠN HÀNG TRÊN {amount}",
    en: "COMPLIMENTARY SHIPPING ON ORDERS OVER {amount}",
  },
  freeShippingAlways: {
    vi: "MIỄN PHÍ VẬN CHUYỂN MỌI ĐƠN HÀNG — ĐẶC QUYỀN DIOLO REWARDS",
    en: "COMPLIMENTARY SHIPPING ON EVERY ORDER — DIOLO REWARDS PERK",
  },
  newArrivals: {
    vi: "HÀNG MỚI VỀ — XUÂN / HÈ 2026",
    en: "NEW ARRIVALS — SPRING / SUMMER 2026",
  },
  premiumQuality: {
    vi: "CHẤT LƯỢNG CAO · ĐỔI TRẢ DỄ DÀNG · THIẾT KẾ TẠI VIỆT NAM",
    en: "PREMIUM QUALITY · EASY RETURNS · DESIGNED IN VIETNAM",
  },
  joinRewards: {
    vi: "GIẢM 10% ĐƠN ĐẦU TIÊN — THAM GIA DIOLO REWARDS",
    en: "10% OFF YOUR FIRST ORDER — JOIN DIOLO REWARDS",
  },

  // ─── Navigation ───
  shop: { vi: "Cửa hàng", en: "Shop" },
  shopAll: { vi: "Tất cả sản phẩm", en: "Shop All" },
  collections: { vi: "Bộ sưu tập", en: "Collections" },
  about: { vi: "Về chúng tôi", en: "About" },
  wishlist: { vi: "Yêu thích", en: "Wishlist" },
  wishlistEmpty: { vi: "Danh sách yêu thích trống", en: "Your Wishlist is Empty" },
  wishlistEmptySubtext: {
    vi: "Bắt đầu lưu những sản phẩm bạn yêu thích.",
    en: "Start adding items you love.",
  },
  browseProducts: { vi: "Khám phá sản phẩm", en: "Browse Products" },
  account: { vi: "Tài khoản", en: "Account" },
  search: { vi: "Tìm kiếm", en: "Search" },

  // ─── Product ───
  addToCart: { vi: "Thêm vào giỏ hàng", en: "Add to Cart" },
  soldOut: { vi: "Hết hàng", en: "Sold Out" },
  size: { vi: "Kích thước", en: "Size" },
  color: { vi: "Màu sắc", en: "Color" },
  quantity: { vi: "Số lượng", en: "Quantity" },
  sizeGuide: { vi: "Hướng dẫn chọn size", en: "Size Guide" },
  description: { vi: "Mô tả", en: "Description" },
  fabricCare: { vi: "Chất liệu & Bảo quản", en: "Fabric & Care" },
  sale: { vi: "Giảm giá", en: "Sale" },
  new: { vi: "Mới", en: "New" },
  reviews: { vi: "Đánh giá", en: "Reviews" },
  noReviews: { vi: "Chưa có đánh giá", en: "No reviews yet" },

  // ─── Product listing ───
  allProducts: { vi: "Tất cả sản phẩm", en: "All Products" },
  sortBy: { vi: "Sắp xếp theo", en: "Sort by" },
  newest: { vi: "Mới nhất", en: "Newest" },
  priceLowHigh: { vi: "Giá tăng dần", en: "Price: Low to High" },
  priceHighLow: { vi: "Giá giảm dần", en: "Price: High to Low" },
  bestSelling: { vi: "Bán chạy", en: "Best Selling" },
  filter: { vi: "Bộ lọc", en: "Filter" },
  noProductsFound: { vi: "Không tìm thấy sản phẩm", en: "No products found" },
  activity: { vi: "Hoạt động", en: "Activity" },
  clearAll: { vi: "Xoá tất cả", en: "Clear All" },
  products: { vi: "Sản phẩm", en: "Products" },

  // ─── Cart ───
  cart: { vi: "Giỏ hàng", en: "Cart" },
  checkout: { vi: "Thanh toán", en: "Checkout" },
  subtotal: { vi: "Tạm tính", en: "Subtotal" },
  emptyCart: { vi: "Giỏ hàng trống", en: "Your cart is empty" },
  continueShopping: { vi: "Tiếp tục mua sắm", en: "Continue Shopping" },
  remove: { vi: "Xoá", en: "Remove" },
  youMayAlsoLike: { vi: "Có thể bạn cũng thích", en: "You May Also Like" },
  othersAlsoBought: { vi: "Người khác cũng mua", en: "Others Also Bought" },
  freeShippingProgress: {
    vi: "Mua thêm {amount} để được miễn phí vận chuyển",
    en: "Add {amount} more for free shipping",
  },
  freeShippingUnlocked: {
    vi: "Bạn đã được miễn phí vận chuyển với Diolo Rewards!",
    en: "You've unlocked free shipping with Diolo Rewards!",
  },

  // ─── Shipping / Delivery ───
  freeShippingLabel: { vi: "Miễn phí vận chuyển", en: "Free Shipping" },
  shippingInfo: {
    vi: "Miễn phí vận chuyển đơn hàng trên {amount}",
    en: "Free shipping on orders over {amount}",
  },
  estimatedDelivery: {
    vi: "Giao hàng dự kiến: 2-5 ngày",
    en: "Estimated delivery: 2-5 days",
  },

  // ─── Footer ───
  help: { vi: "Hỗ trợ", en: "Help" },
  faqs: { vi: "Câu hỏi thường gặp", en: "FAQs" },
  shipping: { vi: "Vận chuyển", en: "Shipping" },
  returns: { vi: "Đổi trả", en: "Returns" },
  company: { vi: "Công ty", en: "Company" },
  ourStory: { vi: "Câu chuyện", en: "Our Story" },
  careers: { vi: "Tuyển dụng", en: "Careers" },
  sustainability: { vi: "Bền vững", en: "Sustainability" },
  pressMedia: { vi: "Báo chí", en: "Press & Media" },
  connect: { vi: "Kết nối", en: "Connect" },
  stayUpdated: { vi: "Nhận thông tin mới nhất", en: "Stay updated" },
  emailPlaceholder: { vi: "Nhập email của bạn", en: "Enter your email" },
  subscribe: { vi: "Đăng ký", en: "Subscribe" },
  allRightsReserved: { vi: "Đã đăng ký bản quyền", en: "All rights reserved" },
  privacyPolicy: { vi: "Chính sách bảo mật", en: "Privacy Policy" },
  termsOfService: { vi: "Điều khoản dịch vụ", en: "Terms of Service" },

  // ─── Product Detail Extras ───
  addedToCart: { vi: "Đã thêm vào giỏ hàng!", en: "Added to cart!" },
  viewCart: { vi: "Xem giỏ hàng", en: "View Cart" },
  selectSize: { vi: "Chọn kích thước", en: "Select a size" },
  selectColor: { vi: "Chọn màu sắc", en: "Select a color" },
  outOfStock: { vi: "Hết hàng", en: "Out of Stock" },
  lowStock: { vi: "Sắp hết hàng", en: "Low Stock" },
  inStock: { vi: "Còn hàng", en: "In Stock" },

  // ─── Product listing extras ───
  view: { vi: "Xem", en: "View" },
  loadMore: { vi: "Xem thêm", en: "Load More" },
  viewAllProducts: { vi: "Xem tất cả sản phẩm", en: "View All Products" },
  newArrivalsTitle: { vi: "Hàng mới về", en: "New Arrivals" },
  resultsFor: { vi: "Kết quả cho", en: "Results for" },
  collection: { vi: "Bộ sưu tập", en: "Collection" },
  product: { vi: "sản phẩm", en: "product" },
  showProducts: { vi: "Hiển thị {count} sản phẩm", en: "Show {count} Products" },
  showProduct: { vi: "Hiển thị {count} sản phẩm", en: "Show {count} Product" },
  fabric: { vi: "Chất liệu", en: "Fabric" },
  preOrder: { vi: "Đặt Trước", en: "Pre-Order" },
  quickView: { vi: "Xem nhanh", en: "Quick View" },
  addToBag: { vi: "Thêm vào giỏ", en: "Add to Bag" },
  titleAZ: { vi: "A-Z", en: "A-Z" },

  // ─── Product detail extras ───
  home: { vi: "Trang chủ", en: "Home" },
  selectColour: { vi: "Chọn màu", en: "Select Colour" },
  selectSizeLabel: { vi: "Chọn kích thước", en: "Select Size" },
  sizeFitGuide: { vi: "Hướng dẫn chọn size", en: "Size & Fit Guide" },
  sizeNotInStock: { vi: "Hết size bạn cần?", en: "Size Not In Stock?" },
  addedToCartMsg: { vi: "✓ Đã thêm vào giỏ", en: "✓ Added to Cart" },
  addToCartLabel: { vi: "Thêm vào giỏ hàng", en: "Add to Cart" },
  selectASizeLabel: { vi: "Chọn kích thước", en: "Select a Size" },
  freeShippingOver: { vi: "Miễn phí vận chuyển đơn trên {amount}", en: "Free Shipping Over {amount}" },
  limitedStock: { vi: "Số lượng có hạn", en: "Limited Stock" },
  productDetails: { vi: "Chi tiết sản phẩm", en: "Product Details" },
  shippingReturns: { vi: "Vận chuyển & Đổi trả", en: "Shipping & Returns" },
  benefits: { vi: "Ưu đãi", en: "Benefits" },
  freeShippingOverAmount: { vi: "Miễn phí vận chuyển đơn trên {amount}", en: "Free shipping over {amount}" },
  freeReturns30: { vi: "Đổi trả miễn phí trong 30 ngày", en: "Free returns within 30 days" },
  needAssistance: { vi: "Cần hỗ trợ?", en: "Need assistance?" },
  sizeAndFit: { vi: "Kích thước và form", en: "Size and fit" },
  composition: { vi: "Thành phần", en: "Composition" },
  care: { vi: "Bảo quản", en: "Care" },
  shippingStandard: { vi: "Giao hàng tiêu chuẩn: 3-5 ngày làm việc", en: "Standard delivery: 3-5 business days" },
  shippingExpress: { vi: "Giao hàng nhanh: 1-2 ngày làm việc ({price})", en: "Express delivery: 1-2 business days ({price})" },
  returnsExchanges: { vi: "Đổi trả & Hoàn tiền", en: "Returns & Exchanges" },
  freeReturns30Full: { vi: "Đổi trả miễn phí trong 30 ngày kể từ ngày mua", en: "Free returns within 30 days of purchase" },
  itemsUnworn: { vi: "Sản phẩm phải còn nguyên tem mác", en: "Items must be unworn with tags attached" },
  exchangesAvailable: { vi: "Có thể đổi sang size/màu khác", en: "Exchanges available for different sizes/colours" },
  needHelpSize: { vi: "Cần tìm size phù hợp?", en: "Need help finding your size?" },
  customerReviews: { vi: "Đánh giá khách hàng", en: "Customer Reviews" },
  basedOnReviews: { vi: "Dựa trên {count} đánh giá", en: "Based on {count} reviews" },
  basedOnReview: { vi: "Dựa trên {count} đánh giá", en: "Based on {count} review" },
  noReviewsYet: { vi: "Chưa có đánh giá nào.", en: "No reviews yet." },
  writeReview: { vi: "Viết đánh giá", en: "Write a Review" },
  searchReviews: { vi: "Tìm kiếm đánh giá...", en: "Search reviews..." },
  mostRelevant: { vi: "Liên quan nhất", en: "Most Relevant" },
  highestRated: { vi: "Đánh giá cao nhất", en: "Highest Rated" },
  lowestRated: { vi: "Đánh giá thấp nhất", en: "Lowest Rated" },
  verifiedBuyer: { vi: "Người mua xác thực", en: "Verified Buyer" },
  yourName: { vi: "Tên của bạn", en: "Your Name" },
  rating: { vi: "Đánh giá", en: "Rating" },
  reviewTitle: { vi: "Tiêu đề đánh giá", en: "Review Title" },
  yourReview: { vi: "Đánh giá của bạn", en: "Your Review" },
  submitReview: { vi: "Gửi đánh giá", en: "Submit Review" },
  cancel: { vi: "Huỷ", en: "Cancel" },
  clearSearch: { vi: "Xoá tìm kiếm", en: "Clear search" },
  noReviewsMatching: { vi: "Không có đánh giá phù hợp với", en: "No reviews matching" },
  comfort: { vi: "Thoải mái", en: "Comfort" },
  fit: { vi: "Form dáng", en: "Fit" },
  quality: { vi: "Chất lượng", en: "Quality" },
  review: { vi: "đánh giá", en: "review" },
  quickAdd: { vi: "Thêm nhanh", en: "Quick Add" },
  sellingFast: { vi: "Bán chạy", en: "Selling Fast" },
  off: { vi: "Giảm", en: "Off" },
  more: { vi: "thêm", en: "more" },
  colours: { vi: "màu", en: "colours" },
  completeTheLook: { vi: "Mua trọn set", en: "Shop the Look" },
  runsSmall: { vi: "Nhỏ hơn bình thường", en: "Runs Small" },
  trueToSize: { vi: "Đúng size", en: "True to Size" },
  runsLarge: { vi: "Lớn hơn bình thường", en: "Runs Large" },
  beFirstToReview: { vi: "Hãy là người đầu tiên đánh giá sản phẩm này.", en: "Be the first to review this product." },
  pairItWith: { vi: "Phối cùng những sản phẩm này", en: "Pair it with these pieces" },
  similarStyles: { vi: "Phong cách tương tự", en: "Similar styles" },
  added: { vi: "✓ Đã thêm", en: "✓ Added" },

  // ─── Product Comparison ───
  compare: { vi: "So sánh", en: "Compare" },
  compareNow: { vi: "So sánh ngay", en: "Compare Now" },
  compareProducts: { vi: "So sánh sản phẩm", en: "Compare Products" },
  addToCompare: { vi: "So sánh", en: "Compare" },
  removeFromCompare: { vi: "Bỏ so sánh", en: "Remove" },
  addedToCompare: { vi: "✓ Đã thêm", en: "✓ Added" },
  compareFull: { vi: "Tối đa 4 sản phẩm", en: "Max 4 products" },
  compareEmpty: { vi: "Chưa có sản phẩm nào để so sánh", en: "No products to compare" },
  compareEmptySubtext: { vi: "Thêm sản phẩm vào danh sách so sánh từ trang sản phẩm.", en: "Add products to your comparison list from the product pages." },
  compareMinimum: { vi: "Thêm ít nhất 2 sản phẩm để so sánh", en: "Add at least 2 products to compare" },
  compareName: { vi: "Tên sản phẩm", en: "Product" },
  comparePrice: { vi: "Giá", en: "Price" },
  compareRating: { vi: "Đánh giá", en: "Rating" },
  compareSizes: { vi: "Kích thước có sẵn", en: "Available Sizes" },
  compareDescription: { vi: "Mô tả", en: "Description" },
  compareMaterial: { vi: "Chất liệu", en: "Material" },
  lowestPrice: { vi: "Giá tốt nhất", en: "Best Price" },

  // Homepage & navigation
  shopNow: { vi: "Mua ngay", en: "Shop Now" },
  viewCollections: { vi: "Xem bộ sưu tập", en: "View Collections" },
  shopTennis: { vi: "Tennis", en: "Shop Tennis" },
  shopYoga: { vi: "Yoga", en: "Shop Yoga" },
  explore: { vi: "Khám phá", en: "Explore" },
  bestSellers: { vi: "Bán chạy nhất", en: "Best Sellers" },
  shopByActivity: { vi: "Mua theo hoạt động", en: "Shop by Activity" },
  ourCollection: { vi: "Bộ sưu tập", en: "Our Collection" },
  shopCloud: { vi: "Cloud", en: "Shop Cloud" },
  shopSeamless: { vi: "Seamless", en: "Shop Seamless" },
  tennisPickleball: { vi: "Tennis & Pickleball", en: "Tennis & Pickleball" },
  yogaPilates: { vi: "Yoga & Pilates", en: "Yoga & Pilates" },
  loungeEveryday: { vi: "Thường ngày", en: "Lounge & Everyday" },
  // Footer
  wholesale: { vi: "Bán sỉ", en: "Wholesale" },
  contact: { vi: "Liên hệ", en: "Contact" },
  tennis: { vi: "Tennis", en: "Tennis" },
  yoga: { vi: "Yoga", en: "Yoga" },
  lounge: { vi: "Lounge", en: "Lounge" },
  privacy: { vi: "Chính sách bảo mật", en: "Privacy" },
  terms: { vi: "Điều khoản", en: "Terms" },
  thankYouSubscribing: { vi: "Cảm ơn bạn đã đăng ký!", en: "Thank you for subscribing!" },
  // Email popup
  getDiscountOff: { vi: "Giảm 10%", en: "Get 10% Off" },
  signUpExclusive: { vi: "Đăng ký để nhận ưu đãi về sản phẩm mới và khuyến mãi.", en: "Sign up for exclusive access to new arrivals, sales, and more." },
  welcomeEql: { vi: "Chào mừng đến Diolo!", en: "Welcome to Diolo!" },
  checkInbox: { vi: "Kiểm tra hộp thư để nhận mã giảm giá.", en: "Check your inbox for your discount code." },
  unlockDiscount: { vi: "Nhận ưu đãi 10%", en: "Unlock 10% Off" },
  noThanks: { vi: "Không, cảm ơn", en: "No thanks, I'll pay full price" },
  // Cart drawer extras
  notSureWhereToStart: { vi: "Chưa biết chọn gì?", en: "Not Sure Where to Start?" },
  shippingAtCheckout: { vi: "Phí vận chuyển tính khi thanh toán", en: "Shipping calculated at checkout" },
  // Homepage
  noProductsCategory: { vi: "Chưa có sản phẩm trong danh mục này.", en: "No products in this category yet." },
  viewAll: { vi: "Xem tất cả", en: "View All" },
  sortAZ: { vi: "A-Z", en: "A-Z" },

  // ─── Search page ───
  searchForProducts: {
    vi: "Tìm kiếm sản phẩm...",
    en: "Search for products...",
  },
  noProductsFoundSearch: {
    vi: "Không tìm thấy sản phẩm phù hợp với tìm kiếm của bạn.",
    en: "No products found matching your search.",
  },
  noResultsHeading: {
    vi: "Không tìm thấy kết quả",
    en: "No Results Found",
  },
  noResultsSuggestion: {
    vi: "Thử thay đổi từ khóa hoặc khám phá bộ sưu tập của chúng tôi.",
    en: "Try adjusting your search or explore our collection.",
  },
  browseAllProducts: {
    vi: "Xem tất cả sản phẩm",
    en: "Browse All Products",
  },
  result: { vi: "kết quả", en: "result" },
  results: { vi: "kết quả", en: "results" },
  forQuery: { vi: "cho", en: "for" },
  trySearching: {
    vi: "Thử tìm kiếm sản phẩm, chất liệu hoặc hoạt động",
    en: "Try searching for a product, fabric, or activity",
  },
  suggestedSearches: {
    vi: "Gợi ý tìm kiếm",
    en: "Suggested Searches",
  },

  // ─── Cart page ───
  yourCart: { vi: "Giỏ hàng của bạn", en: "Your Cart" },
  yourCartCount: { vi: "Giỏ hàng của bạn ({count})", en: "Your Cart ({count})" },
  cartEmpty: { vi: "Giỏ hàng trống", en: "Your Cart is Empty" },
  cartEmptySubtext: {
    vi: "Có vẻ bạn chưa thêm sản phẩm nào.",
    en: "Looks like you haven't added anything yet.",
  },
  awayFromFreeShipping: {
    vi: "Bạn cần mua thêm {amount} để được miễn phí vận chuyển",
    en: "You're {amount} away from free shipping",
  },
  orderSummary: { vi: "Tóm tắt đơn hàng", en: "Order Summary" },
  free: { vi: "Miễn phí", en: "Free" },
  calculatedAtCheckout: {
    vi: "Tính khi thanh toán",
    en: "Calculated at checkout",
  },
  total: { vi: "Tổng cộng", en: "Total" },
  proceedToCheckout: { vi: "Tiến hành thanh toán", en: "Proceed to Checkout" },
  qty: { vi: "SL", en: "Qty" },

  // ─── Activity labels ───
  gymTraining: { vi: "Gym & Tập luyện", en: "Gym & Training" },

  // ─── Attribute rating labels ───
  veryComfortable: { vi: "Rất thoải mái", en: "Very Comfortable" },
  comfortable: { vi: "Thoải mái", en: "Comfortable" },
  average: { vi: "Trung bình", en: "Average" },
  exceptional: { vi: "Xuất sắc", en: "Exceptional" },
  highQuality: { vi: "Chất lượng cao", en: "High Quality" },
  good: { vi: "Tốt", en: "Good" },

  // ─── Review form placeholders ───
  placeholderName: { vi: "Nguyễn V.", en: "Jane D." },
  placeholderReviewTitle: { vi: "Sản phẩm tuyệt vời!", en: "Great product!" },
  placeholderReviewBody: { vi: "Chia sẻ trải nghiệm của bạn...", en: "Share your experience..." },

  // ─── Checkout ───
  checkoutTitle: { vi: "Thanh toán", en: "Checkout" },
  contactInfo: { vi: "Thông tin liên hệ", en: "Contact Information" },
  contactStep: { vi: "Liên hệ", en: "Contact" },
  shippingStep: { vi: "Vận chuyển", en: "Shipping" },
  paymentStep: { vi: "Thanh toán", en: "Payment" },
  emailAddress: { vi: "Địa chỉ email", en: "Email address" },
  firstName: { vi: "Tên", en: "First name" },
  lastName: { vi: "Họ", en: "Last name" },
  address: { vi: "Địa chỉ", en: "Address" },
  apartment: { vi: "Căn hộ, phòng, v.v. (tuỳ chọn)", en: "Apartment, suite, etc. (optional)" },
  city: { vi: "Thành phố", en: "City" },
  postalCode: { vi: "Mã bưu chính", en: "Postal code" },
  phone: { vi: "Số điện thoại", en: "Phone number" },
  continueToShipping: { vi: "Tiếp tục đến vận chuyển", en: "Continue to Shipping" },
  continueToPayment: { vi: "Tiếp tục đến thanh toán", en: "Continue to Payment" },
  back: { vi: "Quay lại", en: "Back" },
  shippingAddress: { vi: "Địa chỉ giao hàng", en: "Shipping Address" },
  paymentMethod: { vi: "Phương thức thanh toán", en: "Payment Method" },
  creditCard: { vi: "Thẻ tín dụng / Thẻ ghi nợ", en: "Credit / Debit Card" },
  creditCardDesc: { vi: "Visa, Mastercard, Amex", en: "Visa, Mastercard, Amex" },
  bankTransfer: { vi: "Chuyển khoản ngân hàng", en: "Bank Transfer" },
  bankTransferDesc: { vi: "Thanh toán trực tiếp qua ngân hàng", en: "Direct bank payment" },
  cashOnDelivery: { vi: "Thanh toán khi nhận hàng", en: "Cash on Delivery" },
  codDesc: { vi: "Thanh toán khi nhận hàng", en: "Pay when you receive" },
  placeOrder: { vi: "Thanh toán ngay", en: "Pay Now" },
  processing: { vi: "Đang xử lý...", en: "Processing..." },
  orderConfirmed: { vi: "Đơn hàng đã xác nhận", en: "Order Confirmed" },
  thankYouOrder: { vi: "Cảm ơn bạn đã đặt hàng", en: "Thank you for your order" },
  orderNumber: { vi: "Mã đơn hàng", en: "Order Number" },
  paymentLabel: { vi: "Thanh toán", en: "Payment" },
  codPaymentNote: {
    vi: "Thanh toán khi nhận hàng — trả tiền khi bạn nhận đơn hàng",
    en: "Cash on Delivery — pay when you receive your order",
  },
  bankTransferNote: {
    vi: "Chuyển khoản ngân hàng — chúng tôi sẽ gửi thông tin thanh toán qua email",
    en: "Bank Transfer — we will email you payment details",
  },
  paymentPending: { vi: "Đang chờ thanh toán", en: "Payment pending" },
  confirmationEmail: {
    vi: "Email xác nhận sẽ được gửi đến địa chỉ email của bạn.",
    en: "A confirmation email will be sent to your email address.",
  },
  cartEmptyCheckout: { vi: "Giỏ hàng của bạn trống", en: "Your cart is empty" },
  selectPaymentMethod: { vi: "Vui lòng chọn phương thức thanh toán", en: "Please select a payment method" },
  bankTransferQR: { vi: "Chuyển khoản QR", en: "Bank Transfer QR" },
  bankTransferQRDesc: { vi: "Quét mã QR để chuyển khoản — Miễn phí", en: "Scan QR to transfer — Free" },
  domesticCard: { vi: "Thẻ nội địa (ATM/Ghi nợ)", en: "Domestic Card (ATM/Debit)" },
  domesticCardDesc: { vi: "Thẻ ATM ngân hàng Việt Nam", en: "Vietnamese bank ATM card" },
  domesticPayment: { vi: "Chuyển khoản ngân hàng (PayOS)", en: "Bank Transfer (PayOS)" },
  domesticPaymentDesc: { vi: "Chuyển khoản trực tiếp qua mã QR", en: "Direct bank transfer via QR code" },
  momoWallet: { vi: "Ví MoMo", en: "MoMo Wallet" },
  zalopayWallet: { vi: "Ví ZaloPay", en: "ZaloPay Wallet" },
  internationalCard: { vi: "Thẻ quốc tế (Visa/Mastercard)", en: "International Card (Visa/Mastercard)" },
  internationalCardDesc: { vi: "Visa, Mastercard, Amex", en: "Visa, Mastercard, Amex" },
  scanQRToPay: { vi: "Quét mã QR để thanh toán", en: "Scan QR Code to Pay" },
  bankAccountInfo: { vi: "Ngân hàng", en: "Bank" },
  transferAmount: { vi: "Số tiền", en: "Amount" },
  transferReference: { vi: "Nội dung chuyển khoản", en: "Transfer Reference" },
  paymentPendingVerification: { vi: "Thanh toán sẽ được xác nhận sau khi chúng tôi nhận được chuyển khoản", en: "Payment will be confirmed once we receive your transfer" },
  completeYourPayment: { vi: "Hoàn tất thanh toán", en: "Complete Your Payment" },
  scanQrToPay: { vi: "Quét mã QR bên dưới để thanh toán", en: "Scan the QR code below to pay" },
  paymentAutoConfirmed: { vi: "Quét QR để thanh toán — xác nhận tự động trong vài phút", en: "Scan QR to pay — auto-confirmed within minutes" },
  paymentReceived: { vi: "Đã nhận thanh toán!", en: "Payment Received!" },
  orderBeingProcessed: { vi: "Đơn hàng của bạn đang được xử lý", en: "Your order is being processed" },
  paymentExpired: { vi: "Hết thời gian thanh toán. Vui lòng đặt hàng lại.", en: "Payment time expired. Please place a new order." },
  completePaymentWithin: { vi: "Hoàn tất thanh toán trong", en: "Complete payment within" },
  secureCheckout: { vi: "Thanh toán an toàn & bảo mật", en: "Secure & Encrypted Checkout" },

  // ─── Contact form ───
  getInTouch: { vi: "Liên hệ", en: "Get in Touch" },
  contactUs: { vi: "Liên hệ chúng tôi", en: "Contact Us" },
  contactDescription: {
    vi: "Chúng tôi luôn sẵn sàng hỗ trợ bạn. Gửi tin nhắn hoặc liên hệ trực tiếp qua email.",
    en: "We're here to help. Send us a message or reach out directly via email.",
  },
  supportHours: { vi: "Thời gian hỗ trợ", en: "Support Hours" },
  nameRequired: { vi: "Vui lòng nhập tên", en: "Name is required" },
  messageRequired: { vi: "Vui lòng nhập tin nhắn", en: "Message is required" },
  messageSent: { vi: "Đã gửi tin nhắn!", en: "Message Sent!" },
  messageSentDesc: {
    vi: "Chúng tôi đã nhận được tin nhắn và sẽ phản hồi trong thời gian sớm nhất.",
    en: "We've received your message and will get back to you soon.",
  },

  // ─── Form validation ───
  emailRequired: { vi: "Email là bắt buộc", en: "Email is required" },
  invalidEmail: { vi: "Vui lòng nhập địa chỉ email hợp lệ", en: "Please enter a valid email address" },
  firstNameRequired: { vi: "Tên là bắt buộc", en: "First name is required" },
  lastNameRequired: { vi: "Họ là bắt buộc", en: "Last name is required" },
  addressRequired: { vi: "Địa chỉ là bắt buộc", en: "Address is required" },
  cityRequired: { vi: "Thành phố là bắt buộc", en: "City is required" },
  postalCodeRequired: { vi: "Mã bưu chính là bắt buộc", en: "Postal code is required" },

  // ─── Auth ───
  signIn: { vi: "Đăng nhập", en: "Sign In" },
  signUp: { vi: "Đăng ký", en: "Sign Up" },
  signOut: { vi: "Đăng xuất", en: "Sign Out" },
  createAccount: { vi: "Tạo tài khoản", en: "Create Account" },
  forgotPassword: { vi: "Quên mật khẩu?", en: "Forgot Password?" },
  resetPasswordTitle: { vi: "Đặt lại mật khẩu", en: "Reset Password" },
  invalidCredentials: { vi: "Email hoặc mật khẩu không đúng", en: "Invalid email or password" },
  accountLocked: { vi: "Tài khoản bị khóa tạm thời. Vui lòng thử lại sau.", en: "Account temporarily locked. Please try again later." },
  accountDisabled: { vi: "Tài khoản của bạn đã bị vô hiệu hóa. Vui lòng liên hệ hỗ trợ.", en: "Your account has been disabled. Please contact support." },
  emailAlreadyRegistered: { vi: "Email này đã được đăng ký", en: "This email is already registered" },
  passwordMinLength: { vi: "Mật khẩu phải có ít nhất 8 ký tự", en: "Password must be at least 8 characters" },
  // T2.10: stronger password policy requires upper + lower + digit on top of the 8-char minimum
  passwordTooWeak: {
    vi: "Mật khẩu phải có ít nhất 8 ký tự, bao gồm chữ hoa, chữ thường, và số",
    en: "Password must be at least 8 characters with an uppercase letter, a lowercase letter, and a number",
  },
  // Proactive hint shown under password inputs on register / reset-password / profile change-password
  // so customers know the requirements BEFORE submitting (not just as an error). Matches the
  // PASSWORD_REGEX rule on the server: 8+ chars with one upper, one lower, one digit.
  passwordRequirements: {
    vi: "Ít nhất 8 ký tự, gồm 1 chữ hoa, 1 chữ thường và 1 số.",
    en: "At least 8 characters, including 1 uppercase letter, 1 lowercase letter, and 1 number.",
  },
  passwordsDoNotMatch: { vi: "Mật khẩu không khớp", en: "Passwords do not match" },
  // T2.7: generic message sent after registration attempt (existing-account & new-account indistinguishable)
  registerCheckInbox: {
    vi: "Đã gửi email xác thực đến địa chỉ của bạn. Vui lòng kiểm tra hộp thư để tiếp tục.",
    en: "If the email is valid, a confirmation message has been sent. Please check your inbox to continue.",
  },
  resetLinkSent: { vi: "Nếu email tồn tại trong hệ thống, bạn sẽ nhận được link đặt lại mật khẩu.", en: "If an account exists with that email, you'll receive a password reset link." },
  resetLinkExpired: { vi: "Link đặt lại mật khẩu đã hết hạn", en: "Password reset link has expired" },
  passwordResetSuccess: { vi: "Mật khẩu đã được đặt lại thành công", en: "Password has been reset successfully" },
  alreadyHaveAccount: { vi: "Đã có tài khoản?", en: "Already have an account?" },
  dontHaveAccount: { vi: "Chưa có tài khoản?", en: "Don't have an account?" },
  orContinueAsGuest: { vi: "Hoặc tiếp tục không cần tài khoản", en: "Or continue as guest" },
  subscribeNewsletter: { vi: "Nhận thông tin khuyến mãi qua email", en: "Subscribe to newsletter" },

  // ─── Unsubscribe ───
  unsubscribeTitle: { vi: "Huỷ đăng ký", en: "Unsubscribe" },
  unsubscribeConfirm: { vi: "Bạn có chắc muốn huỷ nhận email từ Diolo?", en: "Are you sure you want to unsubscribe from Diolo emails?" },
  unsubscribeSuccess: { vi: "Bạn đã huỷ đăng ký nhận email thành công.", en: "You've been successfully unsubscribed." },
  invalidUnsubscribeLink: { vi: "Link huỷ đăng ký không hợp lệ.", en: "This unsubscribe link is invalid." },
  unsubscribeEmail: { vi: "Email đăng ký", en: "Subscribed email" },
  confirmUnsubscribe: { vi: "Xác nhận huỷ đăng ký", en: "Confirm Unsubscribe" },

  // ─── Account ───
  myAccount: { vi: "Tài khoản của tôi", en: "My Account" },
  myProfile: { vi: "Thông tin cá nhân", en: "My Profile" },
  myOrders: { vi: "Đơn hàng", en: "My Orders" },
  savedAddresses: { vi: "Địa chỉ đã lưu", en: "Saved Addresses" },
  orderHistory: { vi: "Lịch sử đơn hàng", en: "Order History" },
  editProfile: { vi: "Chỉnh sửa thông tin", en: "Edit Profile" },
  changePassword: { vi: "Đổi mật khẩu", en: "Change Password" },
  currentPassword: { vi: "Mật khẩu hiện tại", en: "Current Password" },
  newPassword: { vi: "Mật khẩu mới", en: "New Password" },
  confirmNewPassword: { vi: "Xác nhận mật khẩu mới", en: "Confirm New Password" },
  profileUpdated: { vi: "Thông tin đã được cập nhật", en: "Profile updated successfully" },
  passwordChanged: { vi: "Mật khẩu đã được đổi thành công", en: "Password changed successfully" },
  noOrders: { vi: "Chưa có đơn hàng nào", en: "No Orders Yet" },
  noOrdersSubtext: {
    vi: "Lịch sử đơn hàng của bạn sẽ hiển thị ở đây.",
    en: "Your order history will appear here.",
  },
  startShopping: { vi: "Bắt đầu mua sắm", en: "Start Shopping" },
  viewOrder: { vi: "Xem chi tiết", en: "View Details" },
  orderDate: { vi: "Ngày đặt", en: "Order Date" },
  orderTotal: { vi: "Tổng đơn", en: "Order Total" },
  orderItems: { vi: "Sản phẩm", en: "Items" },
  trackOrder: { vi: "Theo dõi đơn hàng", en: "Track Order" },

  // ─── Addresses ───
  addAddress: { vi: "Thêm địa chỉ", en: "Add Address" },
  editAddress: { vi: "Sửa địa chỉ", en: "Edit Address" },
  deleteAddress: { vi: "Xoá địa chỉ", en: "Delete Address" },
  defaultAddress: { vi: "Địa chỉ mặc định", en: "Default Address" },
  setAsDefault: { vi: "Đặt làm mặc định", en: "Set as Default" },
  noAddresses: { vi: "Bạn chưa lưu địa chỉ nào", en: "You haven't saved any addresses yet" },
  addressLabel: { vi: "Nhãn địa chỉ", en: "Address Label" },
  addressSaved: { vi: "Địa chỉ đã được lưu", en: "Address saved successfully" },
  addressDeleted: { vi: "Địa chỉ đã được xoá", en: "Address deleted" },
  confirmDeleteAddress: { vi: "Bạn có chắc muốn xoá địa chỉ này?", en: "Are you sure you want to delete this address?" },

  // ─── Checkout integration ───
  loggedInAs: { vi: "Đăng nhập với", en: "Logged in as" },
  haveAccount: { vi: "Đã có tài khoản?", en: "Have an account?" },
  loginForFaster: { vi: "Đăng nhập để thanh toán nhanh hơn", en: "Log in for faster checkout" },
  createAccountAfter: { vi: "Tạo tài khoản để theo dõi đơn hàng", en: "Create an account to track your orders" },

  // ─── Order statuses ───
  statusPending: { vi: "Chờ xử lý", en: "Pending" },
  statusPaid: { vi: "Đã thanh toán", en: "Paid" },
  statusProcessing: { vi: "Đang xử lý", en: "Processing" },
  statusShipped: { vi: "Đang giao", en: "Shipped" },
  statusDelivered: { vi: "Đã giao", en: "Delivered" },
  statusCancelled: { vi: "Đã huỷ", en: "Cancelled" },
  statusRefunded: { vi: "Đã hoàn tiền", en: "Refunded" },

  // ─── Discount / Promo Codes ───
  promoCode: { vi: "Mã giảm giá", en: "Promo code" },
  apply: { vi: "Áp dụng", en: "Apply" },
  applying: { vi: "Đang áp dụng...", en: "Applying..." },
  removeCode: { vi: "Xóa", en: "Remove" },
  discountApplied: { vi: "Đã áp dụng mã giảm giá", en: "Discount applied" },
  invalidCode: { vi: "Mã giảm giá không hợp lệ", en: "Invalid promo code" },
  codeExpired: { vi: "Mã giảm giá đã hết hạn", en: "This code has expired" },
  codeNotYetActive: { vi: "Mã giảm giá chưa có hiệu lực", en: "This code is not yet active" },
  codeUsageLimit: { vi: "Mã giảm giá đã hết lượt sử dụng", en: "This code has reached its usage limit" },
  minimumNotMet: { vi: "Đơn hàng chưa đạt giá trị tối thiểu {amount}", en: "Minimum order amount of {amount} not met" },
  minimumQtyNotMet: { vi: "Đơn hàng chưa đạt số lượng tối thiểu", en: "Minimum item quantity not met" },
  discountNotApplicable: { vi: "Mã giảm giá không áp dụng được", en: "This discount is not applicable" },
  discount: { vi: "Giảm giá", en: "Discount" },
  enterPromoCode: { vi: "Nhập mã giảm giá", en: "Enter promo code" },
  // Tracking & status emails
  trackingNumber: { vi: "Mã vận đơn", en: "Tracking Number" },
  trackingUrl: { vi: "Link theo dõi", en: "Tracking URL" },
  orderShipped: { vi: "Đơn hàng đang được giao", en: "Order shipped" },
  orderDelivered: { vi: "Đơn hàng đã giao thành công", en: "Order delivered" },
  orderCancelled: { vi: "Đơn hàng đã bị huỷ", en: "Order cancelled" },
  markAsShipped: { vi: "Đánh dấu đã giao", en: "Mark as Shipped" },
  shippingDetails: { vi: "Thông tin vận chuyển", en: "Shipping Details" },
  optional: { vi: "Không bắt buộc", en: "Optional" },
  confirm: { vi: "Xác nhận", en: "Confirm" },
  verifiedPurchase: { vi: "Đã xác minh mua hàng", en: "Verified Purchase" },
  itemOutOfStock: { vi: "Sản phẩm {title} đã hết hàng", en: "Item {title} is out of stock" },
  // ─── Returns ───
  requestReturn: { vi: "Yêu cầu đổi trả", en: "Request Return" },
  returnReason: { vi: "Lý do đổi trả", en: "Return Reason" },
  selectReturnItems: { vi: "Chọn sản phẩm cần đổi trả", en: "Select items to return" },
  returnQuantity: { vi: "Số lượng trả", en: "Return Quantity" },
  submitReturn: { vi: "Gửi yêu cầu đổi trả", en: "Submit Return Request" },
  returnRequested: { vi: "Đã yêu cầu đổi trả", en: "Return Requested" },
  returnApproved: { vi: "Đổi trả được duyệt", en: "Return Approved" },
  returnRejected: { vi: "Đổi trả bị từ chối", en: "Return Rejected" },
  returnCompleted: { vi: "Đổi trả hoàn tất", en: "Return Completed" },
  onlyDeliveredReturnable: { vi: "Chỉ đơn hàng đã giao mới có thể đổi trả", en: "Only delivered orders can be returned" },
  returnAlreadyExists: { vi: "Đơn hàng này đã có yêu cầu đổi trả", en: "A return request already exists for this order" },
  returnRequestSent: { vi: "Yêu cầu đổi trả đã được gửi thành công", en: "Return request submitted successfully" },
  returnReasonPlaceholder: { vi: "Vui lòng mô tả lý do đổi trả...", en: "Please describe the reason for your return..." },

  // ─── Abandoned Cart ───
  abandonedCartSubject: { vi: "Bạn quên giỏ hàng rồi!", en: "You left something behind!" },
  completeYourOrder: { vi: "Hoàn tất đơn hàng", en: "Complete Your Order" },
  itemsWaiting: { vi: "Sản phẩm đang chờ bạn", en: "Your items are waiting" },

  // Order tracking & filters
  priceRange: { vi: "Khoảng giá", en: "Price Range" },
  minPrice: { vi: "Thấp nhất", en: "Min" },
  maxPrice: { vi: "Cao nhất", en: "Max" },
  applyFilter: { vi: "Áp dụng", en: "Apply" },

  // ─── Filters + sorts (Aritzia-style sidebar + bottom sheet) ───
  filters: { vi: "Bộ lọc", en: "Filters" },
  newIn: { vi: "Mới về", en: "New In" },
  onSale: { vi: "Đang giảm giá", en: "On Sale" },
  category: { vi: "Danh mục", en: "Category" },
  sortFeatured: { vi: "Nổi bật", en: "Featured" },
  sortNewest: { vi: "Mới nhất", en: "Newest" },
  sortBestSellers: { vi: "Bán chạy nhất", en: "Best Sellers" },
  sortRating: { vi: "Đánh giá cao", en: "Highest Rated" },
  showingResults: { vi: "{count} sản phẩm", en: "{count} products" },
  showingResultOne: { vi: "{count} sản phẩm", en: "{count} product" },

  // ─── Google OAuth fallback ───
  googleUnavailable: {
    vi: "Đăng nhập Google tạm thời không khả dụng. Vui lòng đăng nhập bằng email.",
    en: "Google sign-in is temporarily unavailable. Please sign in with email.",
  },

  // ─── Recently Viewed ───
  recentlyViewed: { vi: "Đã xem gần đây", en: "Recently Viewed" },

  // ─── Back in Stock ───
  notifyWhenBackInStock: { vi: "Thông báo khi có hàng", en: "Notify me when back in stock" },
  notifyBackInStockSuccess: { vi: "Chúng tôi sẽ thông báo khi có hàng", en: "We'll notify you when it's back" },
  enterYourEmail: { vi: "Nhập email của bạn", en: "Enter your email" },

  // ─── Cart Upsell ───
  add: { vi: "Thêm", en: "Add" },

  // ─── Newsletter Popup ───
  newsletterHeadline: { vi: "Nhận ưu đãi 10% cho đơn hàng đầu tiên", en: "Get 10% off your first order" },
  newsletterSubtext: { vi: "Đăng ký nhận email để nhận mã giảm giá và cập nhật sản phẩm mới.", en: "Subscribe to receive your discount code and stay updated on new arrivals." },
  newsletterSuccess: { vi: "Cảm ơn bạn đã đăng ký!", en: "Thank you for subscribing!" },
  newsletterSuccessSubtext: { vi: "Kiểm tra hộp thư để nhận mã giảm giá 10%.", en: "Check your inbox for your 10% discount code." },

  // ─── Size Chart ───
  sizeChart: { vi: "Bảng size", en: "Size Chart" },
  sizeChartBust: { vi: "Vòng ngực", en: "Bust" },
  sizeChartWaist: { vi: "Vòng eo", en: "Waist" },
  sizeChartHip: { vi: "Vòng mông", en: "Hip" },
  sizeChartLength: { vi: "Chiều dài", en: "Length" },
  sizeChartNote: { vi: "Đơn vị: cm. Dung sai ±1-2cm.", en: "Measurements in cm. Tolerance ±1-2cm." },
  close: { vi: "Đóng", en: "Close" },

  // ─── Size Guide Modal ───
  sizeGuideTitle: { vi: "Hướng dẫn chọn size", en: "Size Guide" },
  howToMeasure: { vi: "Cách đo", en: "How to Measure" },
  measureChest: { vi: "Vòng ngực: Đo vòng quanh phần đầy nhất của ngực.", en: "Chest: Measure around the fullest part of your chest." },
  measureWaist: { vi: "Vòng eo: Đo vòng quanh phần nhỏ nhất của eo.", en: "Waist: Measure around the narrowest part of your waist." },
  measureHip: { vi: "Vòng mông: Đo vòng quanh phần đầy nhất của mông.", en: "Hip: Measure around the fullest part of your hips." },
  measureLength: { vi: "Chiều dài: Đo từ eo xuống đến chiều dài mong muốn.", en: "Length: Measure from your waist down to the desired length." },

  // ─── Rewards ───
  eqlRewards: { vi: "Diolo Rewards", en: "Diolo Rewards" },
  referrals: { vi: "Giới thiệu", en: "Referrals" },

  // ─── Account Dashboard (Aritzia-style landing) ───
  dashboard: { vi: "Trang chính", en: "Dashboard" },
  myDashboard: { vi: "Trang chính của tôi", en: "My Dashboard" },
  welcomeBack: { vi: "Xin chào, {firstName}", en: "Hi, {firstName}" },
  dashboardGreetingSubtitle: {
    vi: "Chào mừng trở lại tài khoản Diolo của bạn.",
    en: "Welcome back to your Diolo account.",
  },
  recentOrders: { vi: "Đơn hàng gần đây", en: "Recent Orders" },
  viewAllOrders: { vi: "Xem tất cả đơn hàng", en: "View All Orders" },
  noOrdersYet: { vi: "Chưa có đơn hàng", en: "No orders yet" },
  manageAddresses: { vi: "Quản lý địa chỉ", en: "Manage Addresses" },
  noAddressesYet: { vi: "Chưa có địa chỉ", en: "No saved addresses" },
  noRecentlyViewed: { vi: "Chưa có sản phẩm đã xem", en: "Nothing viewed yet" },
  viewWishlist: { vi: "Xem danh sách yêu thích", en: "View Wishlist" },
  manageAll: { vi: "Quản lý tất cả", en: "Manage All" },
  quickActions: { vi: "Thao tác nhanh", en: "Quick Actions" },
  viewBenefits: { vi: "Xem quyền lợi", en: "View Benefits" },
  youAre: { vi: "Bạn đang là", en: "You're" },

  // ─── Communication Preferences ───
  communicationPreferences: { vi: "Tuỳ chọn liên lạc", en: "Communication Preferences" },
  preferences: { vi: "Tuỳ chọn", en: "Preferences" },
  marketingEmails: { vi: "Email tiếp thị", en: "Marketing Emails" },
  marketingEmailsDesc: {
    vi: "Ưu đãi, khuyến mãi và tin tức Diolo",
    en: "Promotions, offers and Diolo news",
  },
  orderUpdates: { vi: "Cập nhật đơn hàng", en: "Order Updates" },
  orderUpdatesDesc: {
    vi: "Xác nhận đơn hàng, giao hàng và trạng thái giao hàng",
    en: "Order confirmations, shipping and delivery updates",
  },
  newArrivalsPref: { vi: "Hàng mới về", en: "New Arrivals" },
  newArrivalsPrefDesc: {
    vi: "Thông báo khi bộ sưu tập mới ra mắt",
    en: "Be the first to know when new collections launch",
  },
  promotions: { vi: "Khuyến mãi", en: "Promotions" },
  promotionsDesc: {
    vi: "Ưu đãi giới hạn thời gian và sự kiện bán hàng",
    en: "Limited-time offers and sale events",
  },
  birthdayGifts: { vi: "Quà sinh nhật", en: "Birthday Gifts" },
  birthdayGiftsDesc: {
    vi: "Ưu đãi đặc biệt vào tháng sinh nhật của bạn",
    en: "Special offers during your birthday month",
  },
  smsUpdates: { vi: "Cập nhật qua SMS", en: "SMS Updates" },
  smsUpdatesDesc: {
    vi: "Cập nhật giao hàng và ưu đãi qua tin nhắn",
    en: "Delivery updates and exclusive offers via text",
  },
  preferredLanguage: { vi: "Ngôn ngữ ưu tiên", en: "Preferred Language" },
  preferredCurrency: { vi: "Tiền tệ ưu tiên", en: "Preferred Currency" },
  vietnamese: { vi: "Tiếng Việt", en: "Vietnamese" },
  english: { vi: "Tiếng Anh", en: "English" },
  preferencesUpdated: {
    vi: "Tuỳ chọn đã được cập nhật thành công",
    en: "Your preferences have been updated successfully",
  },
  savePreferences: { vi: "Lưu tuỳ chọn", en: "Save Preferences" },
  transactionalEmailsNote: {
    vi: "Email giao dịch (xác nhận đơn hàng, biên lai) luôn được gửi.",
    en: "Transactional emails (order confirmations, receipts) are always sent.",
  },

  // ─── Returns / Exchanges ───
  requestExchange: { vi: "Yêu cầu đổi hàng", en: "Request Exchange" },
  exchangeOnlyVietnam: {
    vi: "Chính sách Việt Nam: chỉ đổi hàng (không hoàn tiền) trong 7 ngày kể từ khi nhận hàng.",
    en: "Vietnam policy: exchange only (no refunds) within 7 days of delivery.",
  },
  internationalReturnPolicy: {
    vi: "Khách hàng quốc tế có thể đổi hoặc trả hàng trong vòng 30 ngày.",
    en: "International customers may return or exchange within 30 days.",
  },
  vietnamExchangePolicy: {
    vi: "Chính sách đổi hàng Việt Nam",
    en: "Vietnam Exchange Policy",
  },
  chooseNewSize: { vi: "Chọn kích thước mới", en: "Choose new size" },
  chooseNewColor: { vi: "Chọn màu mới", en: "Choose new color" },
  chooseNewVariant: { vi: "Chọn phiên bản mới", en: "Choose new variant" },
  daysLeftToExchange: {
    vi: "Còn {days} ngày để đổi hàng",
    en: "{days} days left to exchange",
  },
  daysLeftToReturn: {
    vi: "Còn {days} ngày để trả hàng",
    en: "{days} days left to return",
  },
  exchangeReason: { vi: "Lý do đổi hàng", en: "Reason for exchange" },
  exchangeReasonPlaceholder: {
    vi: "Cho chúng tôi biết lý do bạn muốn đổi sản phẩm này...",
    en: "Let us know why you'd like to exchange this item...",
  },
  exchangeWindowClosed: {
    vi: "Đã hết hạn đổi hàng (7 ngày kể từ khi giao hàng).",
    en: "The exchange window has closed (7 days from delivery).",
  },
  returnWindowClosed: {
    vi: "Đã hết hạn trả hàng (30 ngày kể từ khi giao hàng).",
    en: "The return window has closed (30 days from delivery).",
  },
  submitExchange: { vi: "Gửi yêu cầu đổi hàng", en: "Submit Exchange Request" },
  exchangeRequested: {
    vi: "Yêu cầu đổi hàng đã được gửi",
    en: "Exchange request submitted",
  },
  replacementVariant: { vi: "Sản phẩm thay thế", en: "Replacement item" },
  imInVietnam: { vi: "Tôi ở Việt Nam", en: "I'm in Vietnam" },
  imInternational: { vi: "Tôi ở nước ngoài", en: "I'm international" },

  // ─── Loyalty (7-tier spend-based) ───
  eqlStep: { vi: "Diolo Step", en: "Diolo Step" },
  eqlMove: { vi: "Diolo Move", en: "Diolo Move" },
  eqlFlow: { vi: "Diolo Flow", en: "Diolo Flow" },
  eqlElevate: { vi: "Diolo Elevate", en: "Diolo Elevate" },
  eqlGlow: { vi: "Diolo Drive", en: "Diolo Drive" },
  eqlGrace: { vi: "Diolo Peak", en: "Diolo Peak" },
  eqlMuse: { vi: "Diolo Summit", en: "Diolo Summit" },
  yourTier: { vi: "Hạng thành viên", en: "Your Tier" },
  nextTier: { vi: "Hạng tiếp theo", en: "Next Tier" },
  vndToNext: {
    vi: "Chi tiêu thêm {amount} để lên hạng {tier}",
    en: "Spend {amount} more to reach {tier}",
  },
  tierBenefits: { vi: "Quyền lợi hạng thành viên", en: "Tier Benefits" },
  nextTierPreview: { vi: "Quyền lợi hạng kế tiếp", en: "Next Tier Preview" },
  yourBenefits: { vi: "Quyền lợi của bạn", en: "Your Benefits" },
  topTierReached: {
    vi: "Bạn đã đạt hạng cao nhất của Diolo — Diolo Summit.",
    en: "You've reached the highest Diolo tier — Diolo Summit.",
  },
  referAFriend: { vi: "Giới thiệu bạn bè", en: "Refer a Friend" },
  referAFriendDesc: {
    vi: "Chia sẻ mã giới thiệu của bạn. Cả hai sẽ nhận voucher 200.000₫ khi bạn mình đặt hàng lần đầu.",
    en: "Share your referral code. You'll both receive a 200,000₫ voucher when your friend places their first order.",
  },
  referralCode: { vi: "Mã giới thiệu", en: "Referral code" },
  copyCode: { vi: "Sao chép mã", en: "Copy code" },
  copied: { vi: "Đã sao chép", en: "Copied" },
  lifetimeSpend: { vi: "Tổng chi tiêu tích lũy", en: "Lifetime Spend" },
  spendLast365: { vi: "Chi tiêu 365 ngày qua", en: "Spend (Last 365 Days)" },
  tierExpires: {
    vi: "Hạng hiện tại duy trì đến {date}",
    en: "Tier valid until {date}",
  },
  welcomeToTier: { vi: "Chào mừng đến với {tier}", en: "Welcome to {tier}" },
  tierUp: { vi: "Lên hạng", en: "Tier Up" },
  activityHistory: { vi: "Lịch sử hoạt động", en: "Activity History" },

  // Per-tier perk strings (shared across customer + admin)
  perkFreeShippingVN: {
    vi: "Miễn phí vận chuyển trong nước cho đơn từ {amount}",
    en: "Free domestic shipping on orders over {amount}",
  },
  perkFreeShippingVNAlways: { vi: "Luôn miễn phí vận chuyển trong nước", en: "Always free domestic shipping" },
  perkFreeShippingIntl: {
    vi: "Miễn phí vận chuyển quốc tế cho đơn từ {amount}",
    en: "Free international shipping on orders over {amount}",
  },
  perkFreeShippingIntlAlways: { vi: "Luôn miễn phí vận chuyển quốc tế", en: "Always free international shipping" },
  perkFreeExpress: { vi: "Miễn phí vận chuyển hoả tốc", en: "Free express shipping" },
  perkBirthdayGift: {
    vi: "Quà sinh nhật {amount}",
    en: "{amount} birthday gift",
  },
  perkBirthdayGiftWithFreeItem: {
    vi: "Quà sinh nhật {amount} + sản phẩm tặng kèm",
    en: "{amount} birthday gift + complimentary item",
  },
  perkBirthdayGiftCurated: {
    vi: "Quà sinh nhật {amount} + quà tặng riêng",
    en: "{amount} birthday gift + curated gift",
  },
  perkBirthdayGiftBespoke: {
    vi: "Quà sinh nhật được stylist lựa chọn riêng",
    en: "Bespoke birthday gift curated by your stylist",
  },
  perkEarlyAccess: { vi: "Truy cập sớm {hours} giờ", en: "{hours}h early access to new drops" },
  perkEarlyAccessDays: { vi: "Truy cập sớm {days}", en: "Early access {days}" },
  perkEarlyAccessFirst: { vi: "Quyền truy cập đầu tiên", en: "First access to every drop" },
  perkStylist: { vi: "Stylist cá nhân", en: "Personal stylist" },
  perkDedicatedStylist: { vi: "Stylist chuyên trách", en: "Dedicated stylist" },
  perkFreeAlterations: { vi: "Chỉnh sửa miễn phí 1 lần/năm", en: "Free alterations (1/year)" },
  perkUnlimitedAlterations: { vi: "Chỉnh sửa miễn phí không giới hạn", en: "Unlimited free alterations" },
  perkExclusiveEvents: { vi: "Mời tham dự sự kiện độc quyền", en: "Exclusive event invitations" },
  perkPriorityEvents: { vi: "Mời ưu tiên sự kiện độc quyền", en: "Priority exclusive event invitations" },
  perkVIPEvents: { vi: "Sự kiện riêng + VIP", en: "Private VIP events" },

  // ─── Tracking ───
  trackShipment: { vi: "Theo dõi vận chuyển", en: "Track Shipment" },
  noTrackingYet: { vi: "Chưa có mã vận đơn", en: "Tracking not yet available" },

  // ─── Admin rewards ───
  loyaltyOverview: { vi: "Tổng quan chương trình thành viên", en: "Loyalty Overview" },
  customersByTier: { vi: "Khách hàng theo hạng", en: "Customers by Tier" },
  totalLifetimeSpend: { vi: "Tổng chi tiêu tích lũy", en: "Total Lifetime Spend" },
  recentTierUps: { vi: "Lên hạng gần đây", en: "Recent Tier-Ups" },
  tierConfig: { vi: "Cấu hình hạng thành viên", en: "Tier Configuration" },
  manualAdjust: { vi: "Điều chỉnh thủ công", en: "Manual Adjustment" },
  issueCoupon: { vi: "Tạo mã giảm giá", en: "Issue Coupon" },

  // ─── Phase B (Tier 3.6 full migration): duplicated inline ternaries ───
  // These strings appeared 2+ times as `locale === "..."` ternaries across
  // the storefront, which is the same class of bug that caused the
  // "Theo hoat dong" / "Lien ket nhanh" diacritic drift. Now centralized.
  legal: { vi: "Pháp lý", en: "Legal" },
  or: { vi: "hoặc", en: "or" },
  contactShort: { vi: "Liên hệ", en: "Contact Us" },
  eqlOnYou: { vi: "Diolo Trên Bạn", en: "Diolo On You" },
  reviewSingular: { vi: "Đánh giá", en: "Review" },
  reviewPlural: { vi: "Đánh giá", en: "Reviews" },
  hipCm: { vi: "Hông (cm)", en: "Hip (cm)" },
  waistCm: { vi: "Eo (cm)", en: "Waist (cm)" },
  standardDelivery: {
    vi: "Giao hàng tiêu chuẩn: 3-5 ngày làm việc",
    en: "Standard delivery: 3-5 business days",
  },

  // ─── Admin Panel ───
  // Navigation
  "admin.home": { vi: "Trang chủ", en: "Home" },
  "admin.dashboard": { vi: "Bảng điều khiển", en: "Dashboard" },
  "admin.orders": { vi: "Đơn hàng", en: "Orders" },
  "admin.wholesaleOrders": { vi: "Đơn hàng sỉ", en: "Wholesale Orders" },
  "admin.returns": { vi: "Trả hàng", en: "Returns" },
  "admin.shipping": { vi: "Vận chuyển", en: "Shipping" },
  "admin.abandonedCheckouts": { vi: "Đơn bỏ dở", en: "Abandoned Checkouts" },
  "admin.newsletterSubscribers": { vi: "Đăng ký nhận tin", en: "Newsletter Subscribers" },
  "admin.blackCard": { vi: "Black Card", en: "Black Card" },
  "admin.earlyAccess": { vi: "001 Early Access", en: "001 Early Access" },
  "admin.founderNotes": { vi: "Founder Notes", en: "Founder Notes" },
  "admin.launchDigest": { vi: "Launch digest", en: "Launch digest" },
  "admin.products": { vi: "Sản phẩm", en: "Products" },
  "admin.customers": { vi: "Khách hàng", en: "Customers" },
  "admin.marketing": { vi: "Tiếp thị", en: "Marketing" },
  "admin.metaAds": { vi: "Quảng cáo Meta", en: "Meta Ads" },
  "admin.capiImport": { vi: "Omni → Meta (CAPI)", en: "Omni → Meta (CAPI)" },
  "admin.shopee": { vi: "Shopee", en: "Shopee" },
  "admin.stockTakes": { vi: "Kiểm kho", en: "Stocktakes" },
  "admin.codReconciliation": { vi: "Đối soát COD", en: "COD Reconciliation" },
  // Retail order screens (audit follow-through 2026-08-03)
  "admin.status.retail.PENDING": { vi: "Chờ thanh toán", en: "Pending" },
  "admin.status.retail.PAID": { vi: "Đã thanh toán", en: "Paid" },
  "admin.status.retail.PROCESSING": { vi: "Đang xử lý", en: "Processing" },
  "admin.status.retail.SHIPPED": { vi: "Đang giao", en: "Shipped" },
  "admin.status.retail.DELIVERED": { vi: "Giao thành công", en: "Delivered" },
  "admin.status.retail.CANCELLED": { vi: "Đã huỷ", en: "Cancelled" },
  "admin.status.retail.REFUNDED": { vi: "Đã hoàn tiền", en: "Refunded" },
  "admin.status.retail.RETURNED": { vi: "Hoàn về kho", en: "Returned" },
  "admin.status.payment.PENDING": { vi: "Chờ thanh toán", en: "Pending" },
  "admin.status.payment.SUCCEEDED": { vi: "Đã thu tiền", en: "Succeeded" },
  "admin.status.payment.FAILED": { vi: "Thất bại", en: "Failed" },
  "admin.status.payment.REFUNDED": { vi: "Đã hoàn", en: "Refunded" },
  "admin.status.payment.PARTIALLY_REFUNDED": { vi: "Hoàn một phần", en: "Partially refunded" },
  "admin.section.sales": { vi: "Bán hàng", en: "Sales" },
  "admin.section.productsCatalog": { vi: "Sản phẩm", en: "Products" },
  "admin.section.marketingGrowth": { vi: "Marketing", en: "Marketing" },
  "admin.section.finance": { vi: "Tài chính", en: "Finance" },
  "admin.productImport": { vi: "Nhập sản phẩm (CSV)", en: "Import products (CSV)" },
  "admin.navSearchPlaceholder": { vi: "Tìm trang…", en: "Find a page…" },
  "admin.retailOrders.title": { vi: "Đơn bán lẻ", en: "Retail Orders" },
  "admin.retailOrders.orders": { vi: "đơn", en: "orders" },
  "admin.retailOrders.selected": { vi: "đã chọn", en: "selected" },
  "admin.retailOrders.searchPlaceholder": { vi: "Tìm theo mã đơn, tên, SĐT, email…", en: "Search order #, name, phone, email…" },
  "admin.retailOrders.allSalespeople": { vi: "Tất cả nhân viên", en: "All salespeople" },
  "admin.retailOrders.selfServe": { vi: "Khách tự đặt (web)", en: "Online (self-serve)" },
  "admin.retailOrders.markProcessing": { vi: "Chuyển Đang xử lý", en: "Mark Processing" },
  "admin.retailOrders.markShipped": { vi: "Chuyển Đang giao", en: "Mark Shipped" },
  "admin.retailOrders.markDelivered": { vi: "Giao thành công", en: "Mark Delivered" },
  "admin.retailOrders.cancelOrders": { vi: "Huỷ đơn", en: "Cancel" },
  "admin.retailOrders.col.order": { vi: "Mã đơn", en: "Order #" },
  "admin.retailOrders.col.date": { vi: "Ngày", en: "Date" },
  "admin.retailOrders.col.customer": { vi: "Khách hàng", en: "Customer" },
  "admin.retailOrders.col.items": { vi: "SP", en: "Items" },
  "admin.retailOrders.col.total": { vi: "Tổng tiền", en: "Total" },
  "admin.retailOrders.col.payment": { vi: "Thanh toán", en: "Payment" },
  "admin.retailOrders.col.status": { vi: "Trạng thái", en: "Status" },
  "admin.retailOrders.col.soldBy": { vi: "NV bán", en: "Sold by" },
  "admin.retailOrders.col.source": { vi: "Nguồn", en: "Source" },
  "admin.retailOrders.empty": { vi: "Không có đơn nào khớp bộ lọc.", en: "No orders match the current filters." },
  "admin.retailOrders.tab.all": { vi: "Tất cả", en: "All" },
  "admin.retailOrder.items": { vi: "Sản phẩm", en: "Items" },
  "admin.retailOrder.subtotal": { vi: "Tạm tính", en: "Subtotal" },
  "admin.retailOrder.shipping": { vi: "Phí ship", en: "Shipping" },
  "admin.retailOrder.tax": { vi: "Thuế", en: "Tax" },
  "admin.retailOrder.total": { vi: "Tổng cộng", en: "Total" },
  "admin.retailOrder.summary": { vi: "Tóm tắt", en: "Summary" },
  "admin.retailOrder.paymentDetails": { vi: "Thanh toán", en: "Payment Details" },
  "admin.retailOrder.shippingAddress": { vi: "Địa chỉ giao hàng", en: "Shipping Address" },
  "admin.retailOrder.customer": { vi: "Khách hàng", en: "Customer" },
  "admin.retailOrder.notes": { vi: "Ghi chú", en: "Notes" },
  "admin.retailOrder.print": { vi: "In đơn", en: "Print" },
  "admin.retailOrder.refund": { vi: "Hoàn tiền", en: "Refund" },
  "admin.retailOrder.addTracking": { vi: "Nhập mã vận đơn", en: "Add Tracking" },
  "admin.retailOrder.bookVtp": { vi: "Đặt Viettel Post", en: "Book VTP Shipment" },
  "admin.retailOrder.cancelOrder": { vi: "Huỷ đơn", en: "Cancel Order" },
  "admin.discounts": { vi: "Giảm giá", en: "Discounts" },
  "admin.content": { vi: "Nội dung", en: "Content" },
  "admin.navigation": { vi: "Điều hướng", en: "Navigation" },
  "admin.markets": { vi: "Thị trường", en: "Markets" },
  "admin.finance": { vi: "Tài chính", en: "Finance" },
  "admin.analytics": { vi: "Phân tích", en: "Analytics" },
  "admin.retailAnalytics": { vi: "Phân tích bán lẻ", en: "Retail Analytics" },
  "admin.attribution": { vi: "Phân bổ", en: "Attribution" },
  "admin.forecasting": { vi: "Dự báo", en: "Forecasting" },
  "admin.webVitals": { vi: "Web Vitals", en: "Web Vitals" },
  "admin.reports": { vi: "Báo cáo", en: "Reports" },
  "admin.giftCards": { vi: "Thẻ quà tặng", en: "Gift Cards" },
  "admin.buyers": { vi: "Người mua B2B", en: "Buyers" },
  "admin.priceLists": { vi: "Bảng giá", en: "Price Lists" },
  "admin.quotes": { vi: "Báo giá", en: "Quotes" },
  "admin.invoices": { vi: "Hóa đơn", en: "Invoices" },
  "admin.lineSheets": { vi: "Line Sheets", en: "Line Sheets" },
  "admin.applications": { vi: "Đơn đăng ký", en: "Applications" },
  "admin.collections": { vi: "Bộ sưu tập", en: "Collections" },
  "admin.seasons": { vi: "Mùa", en: "Seasons" },
  "admin.reviews": { vi: "Đánh giá", en: "Reviews" },
  "admin.eqlOnYou": { vi: "Ảnh khách hàng", en: "Customer photos" },
  "admin.inventoryLevels": { vi: "Tồn kho", en: "Inventory" },
  "admin.locations": { vi: "Địa điểm", en: "Locations" },
  "admin.inventoryTransfers": { vi: "Chuyển kho", en: "Inventory Transfers" },
  "admin.suppliers": { vi: "Nhà cung cấp", en: "Suppliers" },
  "admin.purchaseOrders": { vi: "Đơn mua hàng", en: "Purchase Orders" },
  "admin.onlineStore": { vi: "Cửa hàng trực tuyến", en: "Online Store" },
  "admin.googleYoutube": { vi: "Google & YouTube", en: "Google & YouTube" },
  "admin.facebookInstagram": { vi: "Facebook & Instagram", en: "Facebook & Instagram" },
  "admin.pointOfSale": { vi: "Điểm bán hàng", en: "Point of Sale" },
  "admin.inbox": { vi: "Hộp thư", en: "Inbox" },
  "admin.salesReps": { vi: "Nhân viên bán hàng", en: "Sales Reps" },
  "admin.staffPermissions": { vi: "Nhân sự & Quyền", en: "Staff & Permissions" },
  "admin.automations": { vi: "Tự động hóa", en: "Automations" },
  "admin.rewardsProgram": { vi: "Chương trình thưởng", en: "Rewards Program" },
  "admin.membership": { vi: "Thẻ thành viên", en: "Membership" },
  "admin.emailTemplates": { vi: "Mẫu email", en: "Email Templates" },
  "admin.notifications": { vi: "Thông báo", en: "Notifications" },
  "admin.redirects": { vi: "Chuyển hướng", en: "Redirects" },
  "admin.auditLog": { vi: "Nhật ký kiểm toán", en: "Audit Log" },
  "admin.settings": { vi: "Cài đặt", en: "Settings" },
  "admin.logOut": { vi: "Đăng xuất", en: "Log out" },
  // Section headers
  "admin.section.analytics": { vi: "Phân tích", en: "Analytics" },
  "admin.section.b2bWholesale": { vi: "Bán buôn B2B", en: "B2B Wholesale" },
  "admin.section.catalog": { vi: "Danh mục", en: "Catalog" },
  "admin.section.inventory": { vi: "Kho hàng", en: "Inventory" },
  "admin.section.purchasing": { vi: "Mua hàng", en: "Purchasing" },
  "admin.section.salesChannels": { vi: "Kênh bán hàng", en: "Sales channels" },
  "admin.section.team": { vi: "Nhóm", en: "Team" },
  // Top bar
  "admin.sapoConnected": { vi: "Đã kết nối Sapo", en: "Sapo connected" },
  "admin.connectSapo": { vi: "Kết nối Sapo →", en: "Connect Sapo →" },
  "admin.viewAllNotifications": { vi: "Xem tất cả thông báo", en: "View All Notifications" },
  "admin.noNotifications": { vi: "Chưa có thông báo", en: "No notifications yet" },
  "admin.newCount": { vi: "{count} mới", en: "{count} new" },
  // Products page
  "admin.products.allStock": { vi: "Tất cả tồn kho", en: "All Stock" },
  "admin.products.soldOut": { vi: "Hết hàng", en: "Sold Out" },
  "admin.products.preorder": { vi: "Đặt trước", en: "Preorder" },
  "admin.products.low": { vi: "Thấp", en: "Low" },
  "admin.products.mid": { vi: "Trung bình", en: "Mid" },
  "admin.products.midHigh": { vi: "Trung bình-Cao", en: "Mid-High" },
  "admin.products.high": { vi: "Cao", en: "High" },
  "admin.products.allStatus": { vi: "Tất cả trạng thái", en: "All Status" },
  "admin.products.active": { vi: "Hoạt động", en: "Active" },
  "admin.products.draft": { vi: "Bản nháp", en: "Draft" },
  "admin.products.archived": { vi: "Đã lưu trữ", en: "Archived" },
  "admin.products.searchPlaceholder": { vi: "Tìm kiếm sản phẩm...", en: "Search products..." },
  "admin.products.allVendors": { vi: "Tất cả nhà cung cấp", en: "All Vendors" },
  "admin.products.allTypes": { vi: "Tất cả loại", en: "All Types" },
  "admin.products.excludeVendor": { vi: "Ẩn nhà cung cấp...", en: "Hide vendor..." },
  "admin.products.excludeType": { vi: "Ẩn loại sản phẩm...", en: "Hide product type..." },
  "admin.products.hide": { vi: "Ẩn", en: "Hide" },
  "admin.products.hiding": { vi: "Đang ẩn", en: "Hiding" },
  "admin.products.all": { vi: "Tất cả", en: "All" },
  "admin.products.syncNow": { vi: "Đồng bộ ngay", en: "Sync Now" },
  "admin.products.product": { vi: "Sản phẩm", en: "Product" },
  "admin.products.status": { vi: "Trạng thái", en: "Status" },
  "admin.products.variants": { vi: "Biến thể", en: "Variants" },
  "admin.products.inventory": { vi: "Tồn kho", en: "Inventory" },
  "admin.products.wholesale": { vi: "Bán buôn", en: "Wholesale" },
  "admin.products.retail": { vi: "Bán lẻ", en: "Retail" },
  // Products page - action buttons & features
  "admin.products.backfillImages": { vi: "Bổ sung ảnh", en: "Backfill Images" },
  "admin.products.exportCsv": { vi: "Xuất CSV", en: "Export CSV" },
  "admin.products.importCsv": { vi: "Nhập CSV", en: "Import CSV" },
  "admin.products.syncFromSapo": { vi: "Đồng bộ từ Sapo", en: "Sync from Sapo" },
  "admin.products.sapoSync": { vi: "Đồng bộ Sapo", en: "Sapo Sync" },
  "admin.products.synced": { vi: "Đã đồng bộ", en: "Synced" },
  "admin.products.syncFailed": { vi: "Đồng bộ thất bại", en: "Sync Failed" },
  "admin.products.syncing": { vi: "Đang đồng bộ...", en: "Syncing..." },
  "admin.products.syncHistory": { vi: "Lịch sử đồng bộ", en: "Sync History" },
  "admin.products.date": { vi: "Ngày", en: "Date" },
  "admin.products.type": { vi: "Loại", en: "Type" },
  "admin.products.vendor": { vi: "Nhà cung cấp", en: "Vendor" },
  "admin.products.stock": { vi: "Tồn kho", en: "Stock" },
  "admin.products.search": { vi: "Tìm kiếm", en: "Search" },
  "admin.products.preOrder": { vi: "Đặt trước", en: "Pre-Order" },
  "admin.products.season": { vi: "Mùa", en: "Season" },
  "admin.products.fabricCare": { vi: "Chất liệu/Bảo quản", en: "Fabric/Care" },
  "admin.products.retailInfo": { vi: "Thông tin bán lẻ", en: "Retail Info" },
  "admin.products.fabricAndCare": { vi: "Chất liệu & Bảo quản", en: "Fabric & Care" },
  "admin.products.fabricContent": { vi: "Thành phần chất liệu", en: "Fabric Content" },
  "admin.products.careInstructions": { vi: "Hướng dẫn bảo quản", en: "Care Instructions" },
  "admin.products.fitGuideTop": { vi: "Hướng dẫn form (Áo)", en: "Fit Guide (Top)" },
  "admin.products.fitGuideBottom": { vi: "Hướng dẫn form (Quần)", en: "Fit Guide (Bottom)" },
  "admin.products.trueToSize": { vi: "Đúng size", en: "True to Size" },
  "admin.products.runsSmall": { vi: "Chạy nhỏ", en: "Runs Small" },
  "admin.products.runsLarge": { vi: "Chạy lớn", en: "Runs Large" },
  "admin.products.retailPriceOverride": { vi: "Giá bán lẻ ghi đè", en: "Retail Price Override" },
  "admin.products.retailPricePlaceholder": { vi: "Để trống để dùng giá biến thể", en: "Leave blank to use variant price" },
  "admin.products.compareAtPrice": { vi: "Giá so sánh", en: "Compare-at Price" },
  "admin.products.compareAtPlaceholder": { vi: "Giá gạch ngang cho huy hiệu khuyến mãi", en: "Strikethrough price for sale badges" },
  "admin.products.activityCategory": { vi: "Danh mục hoạt động", en: "Activity Category" },
  "admin.products.activityCourt": { vi: "Sân (Tennis/Pickleball)", en: "Court (Tennis/Pickleball)" },
  "admin.products.activityStudio": { vi: "Studio (Yoga/Pilates)", en: "Studio (Yoga/Pilates)" },
  "admin.products.activityLounge": { vi: "Thư giãn", en: "Lounge" },
  "admin.products.activityTrain": { vi: "Tập luyện (Gym)", en: "Train (Gym/Training)" },
  "admin.products.activityRun": { vi: "Chạy bộ", en: "Run" },
  "admin.products.fabricType": { vi: "Loại chất liệu", en: "Fabric Type" },
  "admin.products.enablePreOrder": { vi: "Bật đặt trước", en: "Enable Pre-Order" },
  "admin.products.preOrderLeadDesc": {
    vi: "Đặt thời gian chờ cho đặt trước sản phẩm này.",
    en: "Set the lead time for pre-orders on this product.",
  },
  "admin.products.leadDays": { vi: "Ngày chờ", en: "Lead Days" },
  "admin.products.leadDaysHelp": {
    vi: "Số ngày trước khi sản phẩm có sẵn",
    en: "Number of days before product is available",
  },
  "admin.products.bulkPriceAdjustment": { vi: "Điều chỉnh giá hàng loạt", en: "Bulk Price Adjustment" },
  "admin.products.adjustPriceDesc": {
    vi: "Điều chỉnh giá bán lẻ cho {count} sản phẩm đã chọn.",
    en: "Adjust retail prices for {count} selected product(s).",
  },
  "admin.products.adjustmentType": { vi: "Loại điều chỉnh", en: "Adjustment Type" },
  "admin.products.adjustmentPercentage": { vi: "Phần trăm (%)", en: "Percentage (%)" },
  "admin.products.adjustmentFixed": { vi: "Số tiền cố định", en: "Fixed Amount" },
  "admin.products.percentageLabel": {
    vi: "Phần trăm (vd: 10 cho +10%, -20 cho -20%)",
    en: "Percentage (e.g. 10 for +10%, -20 for -20%)",
  },
  "admin.products.amountLabel": {
    vi: "Số tiền (dương để tăng, âm để giảm)",
    en: "Amount (positive to increase, negative to decrease)",
  },
  "admin.products.bulkTagManagement": { vi: "Quản lý thẻ hàng loạt", en: "Bulk Tag Management" },
  "admin.products.addTag": { vi: "Thêm thẻ", en: "Add Tag" },
  "admin.products.removeTag": { vi: "Xóa thẻ", en: "Remove Tag" },
  "admin.products.addTagDesc": {
    vi: "Thêm thẻ vào {count} sản phẩm đã chọn.",
    en: "Add a tag to {count} selected product(s).",
  },
  "admin.products.removeTagDesc": {
    vi: "Xóa thẻ khỏi {count} sản phẩm đã chọn.",
    en: "Remove a tag from {count} selected product(s).",
  },
  "admin.products.action": { vi: "Hành động", en: "Action" },
  "admin.products.tag": { vi: "Thẻ", en: "Tag" },
  "admin.products.tagPlaceholder": { vi: "Nhập tên thẻ", en: "Enter tag name" },
  "admin.products.processed": { vi: "Đã xử lý", en: "Processed" },
  "admin.products.failed": { vi: "Thất bại", en: "Failed" },
  "admin.products.sapoNotConnected": { vi: "Sapo chưa kết nối", en: "Sapo Not Connected" },
  "admin.products.sapoNotConnectedDesc": {
    vi: "Kết nối cửa hàng Sapo trong Cài đặt > Tích hợp để đồng bộ sản phẩm.",
    en: "Connect your Sapo store in Settings > Integrations to sync products.",
  },
  "admin.products.noProductsYet": { vi: "Chưa có sản phẩm nào", en: "No Products Yet" },
  "admin.products.noProductsYetDesc": {
    vi: 'Nhấn "Đồng bộ từ Sapo" để nhập danh mục sản phẩm của bạn.',
    en: 'Click "Sync from Sapo" to import your product catalog.',
  },
  "admin.products.wholesaleEnabled": { vi: "Bán buôn đã bật", en: "Wholesale Enabled" },
  "admin.products.wholesaleDisabled": { vi: "Bán buôn đã tắt", en: "Wholesale Disabled" },
  "admin.products.allRetail": { vi: "Tất cả bán lẻ", en: "All Retail" },
  "admin.products.retailEnabled": { vi: "Bán lẻ đã bật", en: "Retail Enabled" },
  "admin.products.retailDisabled": { vi: "Bán lẻ đã tắt", en: "Retail Disabled" },
  "admin.products.clearAll": { vi: "Xóa tất cả", en: "Clear all" },
  "admin.products.quickActions": { vi: "Hành động nhanh:", en: "Quick actions:" },
  "admin.products.disableRetailOOS": { vi: "Tắt bán lẻ cho hàng hết", en: "Disable Retail for Out of Stock" },
  "admin.products.disableWholesaleOOS": { vi: "Tắt bán buôn cho hàng hết", en: "Disable Wholesale for Out of Stock" },
  "admin.products.disableAllRetail": { vi: "Tắt tất cả bán lẻ", en: "Disable All Retail" },
  "admin.products.enableAllRetail": { vi: "Bật tất cả bán lẻ", en: "Enable All Retail" },
  "admin.products.disableAllWholesale": { vi: "Tắt tất cả bán buôn", en: "Disable All Wholesale" },
  "admin.products.enableAllWholesale": { vi: "Bật tất cả bán buôn", en: "Enable All Wholesale" },
  "admin.products.enableWholesaleInStock": { vi: "Bật bán buôn (còn hàng)", en: "Enable Wholesale (In Stock)" },
  "admin.products.enableRetailInStock": { vi: "Bật bán lẻ (còn hàng)", en: "Enable Retail (In Stock)" },
  "admin.products.generateMissingHandles": { vi: "Tạo handle còn thiếu", en: "Generate Missing Handles" },
  "admin.products.confirmDisableRetailOOS": {
    vi: "Tắt bán lẻ cho tất cả sản phẩm hết hàng? Các sản phẩm có 0 tồn kho sẽ bị ẩn khỏi cửa hàng B2C.",
    en: "Disable retail for all out-of-stock products? Products with 0 inventory will be hidden from the B2C storefront.",
  },
  "admin.products.confirmDisableWholesaleOOS": {
    vi: "Tắt bán buôn cho tất cả sản phẩm hết hàng? Các sản phẩm có 0 tồn kho sẽ bị ẩn khỏi cổng bán buôn.",
    en: "Disable wholesale for all out-of-stock products? Products with 0 inventory will be hidden from the wholesale portal.",
  },
  "admin.products.confirmDisableAllRetail": {
    vi: "Bạn có chắc chắn muốn tắt bán lẻ cho TẤT CẢ {count} sản phẩm? Điều này sẽ ẩn tất cả sản phẩm khỏi cửa hàng B2C.",
    en: "Are you sure you want to disable retail for ALL {count} products? This will hide all products from the B2C storefront.",
  },
  "admin.products.confirmEnableAllRetail": {
    vi: "Bạn có chắc chắn muốn bật bán lẻ cho TẤT CẢ {count} sản phẩm? Điều này sẽ hiển thị tất cả sản phẩm trên cửa hàng B2C.",
    en: "Are you sure you want to enable retail for ALL {count} products? This will make all products visible on the B2C storefront.",
  },
  "admin.products.confirmDisableAllWholesale": {
    vi: "Bạn có chắc chắn muốn tắt bán buôn cho TẤT CẢ {count} sản phẩm? Điều này sẽ ẩn tất cả sản phẩm khỏi cổng người mua bán buôn.",
    en: "Are you sure you want to disable wholesale for ALL {count} products? This will hide all products from the wholesale buyer portal.",
  },
  "admin.products.confirmEnableAllWholesale": {
    vi: "Bạn có chắc chắn muốn bật bán buôn cho TẤT CẢ {count} sản phẩm? Điều này sẽ hiển thị tất cả sản phẩm trên cổng người mua bán buôn.",
    en: "Are you sure you want to enable wholesale for ALL {count} products? This will make all products visible on the wholesale buyer portal.",
  },
  "admin.products.confirmEnableWholesaleInStock": {
    vi: "Bật bán buôn cho tất cả sản phẩm đang có hàng? Sản phẩm có 0 tồn kho sẽ không bị ảnh hưởng.",
    en: "Enable wholesale for all products that currently have stock? Products with 0 inventory will not be affected.",
  },
  "admin.products.confirmEnableRetailInStock": {
    vi: "Bật bán lẻ cho tất cả sản phẩm đang có hàng? Sản phẩm có 0 tồn kho sẽ không bị ảnh hưởng.",
    en: "Enable retail for all products that currently have stock? Products with 0 inventory will not be affected.",
  },
  "admin.products.selected": { vi: "đã chọn:", en: "selected:" },
  "admin.products.bulkActions": { vi: "Hành động hàng loạt ▾", en: "Bulk Actions ▾" },
  "admin.products.adjustPrice": { vi: "Điều chỉnh giá", en: "Adjust Price" },
  "admin.products.manageTags": { vi: "Quản lý thẻ", en: "Manage Tags" },
  "admin.products.enableWholesale": { vi: "Bật bán buôn", en: "Enable Wholesale" },
  "admin.products.disableWholesale": { vi: "Tắt bán buôn", en: "Disable Wholesale" },
  "admin.products.enableRetail": { vi: "Bật bán lẻ", en: "Enable Retail" },
  "admin.products.disableRetail": { vi: "Tắt bán lẻ", en: "Disable Retail" },
  // Status labels (raw DB values)
  "admin.products.statusActive": { vi: "Hoạt động", en: "ACTIVE" },
  "admin.products.statusDraft": { vi: "Nháp", en: "DRAFT" },
  "admin.products.statusArchived": { vi: "Đã lưu trữ", en: "ARCHIVED" },
  // Dashboard & common
  "admin.dashboard.title": { vi: "Bảng điều khiển", en: "Dashboard" },
  "admin.dashboard.today": { vi: "Hôm nay", en: "Today" },
  "admin.dashboard.last7d": { vi: "7 ngày qua", en: "Last 7 days" },
  "admin.dashboard.last30d": { vi: "30 ngày qua", en: "Last 30 days" },
  "admin.dashboard.last90d": { vi: "90 ngày qua", en: "Last 90 days" },
  "admin.dashboard.last7Days": { vi: "7 ngày qua", en: "Last 7 days" },
  "admin.dashboard.last30Days": { vi: "30 ngày qua", en: "Last 30 days" },
  "admin.dashboard.last90Days": { vi: "90 ngày qua", en: "Last 90 days" },
  "admin.dashboard.thisYear": { vi: "Năm nay", en: "This Year" },
  "admin.dashboard.overview": { vi: "Tổng quan hoạt động của bạn", en: "Overview of your operations" },
  "admin.dashboard.sapoNotConnected": { vi: "Sapo chưa kết nối", en: "Sapo not connected" },
  "admin.dashboard.sapoNotConnectedDesc": {
    vi: "Kết nối cửa hàng Sapo để đồng bộ sản phẩm và đơn hàng",
    en: "Connect your Sapo store to sync products and orders",
  },
  "admin.dashboard.connect": { vi: "Kết nối", en: "Connect" },
  "admin.dashboard.needsAttention": { vi: "Cần chú ý", en: "Needs Attention" },
  "admin.dashboard.stuckOrders": { vi: "Đơn hàng bị kẹt (>24h)", en: "Stuck Orders (>24h)" },
  "admin.dashboard.lowStockVariants": { vi: "Biến thể tồn kho thấp", en: "Low Stock Variants" },
  "admin.dashboard.pendingApplications": { vi: "Đơn đăng ký đang chờ", en: "Pending Applications" },
  "admin.dashboard.activeProducts": { vi: "Sản phẩm hoạt động", en: "Active Products" },
  "admin.dashboard.retailOrders": { vi: "Đơn bán lẻ", en: "Retail Orders" },
  "admin.dashboard.customers": { vi: "Khách hàng", en: "Customers" },
  "admin.dashboard.wholesaleOrders": { vi: "Đơn bán buôn", en: "Wholesale Orders" },
  "admin.dashboard.activeBuyers": { vi: "Người mua hoạt động", en: "Active Buyers" },
  "admin.dashboard.activeCampaigns": { vi: "Chiến dịch hoạt động", en: "Active Campaigns" },
  "admin.dashboard.activeDiscounts": { vi: "Giảm giá hoạt động", en: "Active Discounts" },
  "admin.dashboard.publishedPages": { vi: "Trang đã xuất bản", en: "Published Pages" },
  "admin.dashboard.activeMarkets": { vi: "Thị trường hoạt động", en: "Active Markets" },
  "admin.dashboard.revenue30d": { vi: "Doanh thu — 30 ngày qua", en: "Revenue — Last 30 Days" },
  "admin.dashboard.totalRevenue": { vi: "Tổng doanh thu", en: "Total Revenue" },
  "admin.dashboard.lastProductSync": { vi: "Đồng bộ sản phẩm lần cuối", en: "Last Product Sync" },
  "admin.dashboard.recentActivity": { vi: "Hoạt động gần đây", en: "Recent Activity" },
  "admin.dashboard.noRecentActivity": { vi: "Chưa có hoạt động gần đây", en: "No recent activity" },
  "admin.dashboard.recentRetailOrders": { vi: "Đơn bán lẻ gần đây", en: "Recent Retail Orders" },
  "admin.dashboard.recentWholesaleOrders": { vi: "Đơn bán buôn gần đây", en: "Recent Wholesale Orders" },
  "admin.dashboard.noRetailOrders": { vi: "Chưa có đơn bán lẻ", en: "No retail orders yet" },
  "admin.dashboard.noWholesaleOrders": { vi: "Chưa có đơn bán buôn", en: "No wholesale orders yet" },
  "admin.dashboard.revenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.dashboard.ordersCount": { vi: "Số đơn hàng", en: "Orders" },
  "admin.dashboard.avgOrderValue": { vi: "Giá trị đơn TB", en: "Avg Order Value" },
  "admin.dashboard.customersCount": { vi: "Khách hàng", en: "Customers" },
  "admin.dashboard.recentOrders": { vi: "Đơn hàng gần đây", en: "Recent Orders" },
  "admin.dashboard.lowStockAlerts": { vi: "Cảnh báo tồn kho thấp", en: "Low Stock Alerts" },
  "admin.dashboard.noData": { vi: "Chưa có dữ liệu", en: "No data yet" },
  // Orders page
  "admin.orders.title": { vi: "Đơn hàng", en: "Orders" },
  "admin.orders.orderNumber": { vi: "Mã đơn", en: "Order #" },
  "admin.orders.customer": { vi: "Khách hàng", en: "Customer" },
  "admin.orders.total": { vi: "Tổng tiền", en: "Total" },
  "admin.orders.status": { vi: "Trạng thái", en: "Status" },
  "admin.orders.date": { vi: "Ngày", en: "Date" },
  "admin.orders.pending": { vi: "Chờ xử lý", en: "Pending" },
  "admin.orders.approved": { vi: "Đã duyệt", en: "Approved" },
  "admin.orders.processing": { vi: "Đang xử lý", en: "Processing" },
  "admin.orders.completed": { vi: "Hoàn tất", en: "Completed" },
  "admin.orders.cancelled": { vi: "Đã hủy", en: "Cancelled" },
  "admin.orders.all": { vi: "Tất cả", en: "All" },
  "admin.orders.searchPlaceholder": { vi: "Tìm kiếm đơn hàng...", en: "Search orders..." },
  "admin.orders.noOrders": { vi: "Chưa có đơn hàng", en: "No orders yet" },
  // Customers page
  "admin.customers.title": { vi: "Khách hàng", en: "Customers" },
  "admin.customers.name": { vi: "Tên", en: "Name" },
  "admin.customers.email": { vi: "Email", en: "Email" },
  "admin.customers.phone": { vi: "SĐT", en: "Phone" },
  "admin.customers.orders": { vi: "Đơn hàng", en: "Orders" },
  "admin.customers.totalSpent": { vi: "Tổng chi tiêu", en: "Total Spent" },
  "admin.customers.searchPlaceholder": { vi: "Tìm kiếm khách hàng...", en: "Search customers..." },
  "admin.customers.noCustomers": { vi: "Chưa có khách hàng", en: "No customers yet" },
  "admin.customers.countSingle": { vi: "khách hàng", en: "customer" },
  "admin.customers.countPlural": { vi: "khách hàng", en: "customers" },
  "admin.customers.addButton": { vi: "Thêm khách hàng", en: "Add customer" },
  "admin.customers.colCustomer": { vi: "Khách hàng", en: "Customer" },
  "admin.customers.colPhone": { vi: "Điện thoại", en: "Phone" },
  "admin.customers.colLocation": { vi: "Địa điểm", en: "Location" },
  "admin.customers.colOrders": { vi: "Đơn hàng", en: "Orders" },
  "admin.customers.colTotalSpent": { vi: "Tổng chi tiêu", en: "Total Spent" },
  "admin.customers.colTags": { vi: "Thẻ", en: "Tags" },
  "admin.customers.colSubscribed": { vi: "Đã đăng ký", en: "Subscribed" },
  "admin.customers.colCreated": { vi: "Ngày tạo", en: "Created" },
  "admin.customers.allTags": { vi: "Tất cả thẻ", en: "All tags" },
  "admin.customers.allSubscribers": { vi: "Tất cả người đăng ký", en: "All subscribers" },
  "admin.customers.subscribed": { vi: "Đã đăng ký", en: "Subscribed" },
  "admin.customers.notSubscribed": { vi: "Chưa đăng ký", en: "Not subscribed" },
  "admin.customers.addTag": { vi: "Thêm thẻ", en: "Add Tag" },
  "admin.customers.removeTag": { vi: "Xóa thẻ", en: "Remove Tag" },
  "admin.customers.deleteCount": { vi: "Xóa ({count})", en: "Delete ({count})" },
  "admin.customers.clearSelection": { vi: "Bỏ chọn", en: "Clear selection" },
  "admin.customers.confirmDelete": { vi: "Xóa {count} khách hàng? Hành động này không thể hoàn tác.", en: "Delete {count} customer(s)? This cannot be undone." },
  "admin.customers.noMatch": { vi: "Không có khách hàng nào khớp với bộ lọc.", en: "No customers match your filters." },
  "admin.customers.emptyPrompt": { vi: "Thêm khách hàng đầu tiên để bắt đầu.", en: "Add your first customer to get started." },
  "admin.customers.addTagModalTitle": { vi: "Thêm thẻ cho {count} khách hàng", en: "Add Tag to {count} Customer(s)" },
  "admin.customers.removeTagModalTitle": { vi: "Xóa thẻ khỏi {count} khách hàng", en: "Remove Tag from {count} Customer(s)" },
  "admin.customers.tagToAdd": { vi: "Thẻ cần thêm", en: "Tag to add" },
  "admin.customers.tagToRemove": { vi: "Thẻ cần xóa", en: "Tag to remove" },
  "admin.customers.tagPlaceholderAdd": { vi: "ví dụ: VIP, bán buôn, khách quen", en: "e.g., VIP, wholesale, returning" },
  "admin.customers.tagPlaceholderRemove": { vi: "Nhập tên thẻ cần xóa", en: "Enter tag name to remove" },
  "admin.customers.orSelectTag": { vi: "Hoặc chọn một thẻ hiện có:", en: "Or select an existing tag:" },
  "admin.customers.addTagHint": { vi: "Thẻ này sẽ được thêm vào tất cả {count} khách hàng đã chọn. Nếu một khách hàng đã có thẻ này, nó sẽ không bị trùng lặp.", en: "This tag will be added to all {count} selected customer(s). If a customer already has this tag, it will not be duplicated." },
  "admin.customers.firstName": { vi: "Tên", en: "First name" },
  "admin.customers.lastName": { vi: "Họ", en: "Last name" },
  "admin.customers.emailLabel": { vi: "Email", en: "Email" },
  "admin.customers.phoneLabel": { vi: "Điện thoại", en: "Phone" },
  "admin.customers.tagsLabel": { vi: "Thẻ", en: "Tags" },
  "admin.customers.notesLabel": { vi: "Ghi chú", en: "Notes" },
  "admin.customers.tagsHelp": { vi: "Các thẻ ngăn cách bằng dấu phẩy để phân loại", en: "Comma-separated tags for organization" },
  "admin.customers.notesPlaceholder": { vi: "Ghi chú nội bộ về khách hàng này...", en: "Internal notes about this customer..." },
  "admin.customers.subscribedCheckbox": { vi: "Đã đăng ký nhận email marketing", en: "Subscribed to marketing emails" },
  "admin.customers.yes": { vi: "Có", en: "Yes" },
  "admin.customers.no": { vi: "Không", en: "No" },
  // Buyers page
  "admin.buyers.title": { vi: "Người mua B2B", en: "Buyers" },
  "admin.buyers.subtitle": { vi: "Quản lý tài khoản người mua bán buôn", en: "Manage wholesale buyer accounts" },
  "admin.buyers.all": { vi: "Tất cả", en: "All" },
  "admin.buyers.approved": { vi: "Đã duyệt", en: "Approved" },
  "admin.buyers.notApproved": { vi: "Chưa duyệt", en: "Not Approved" },
  "admin.buyers.archived": { vi: "Đã lưu trữ", en: "Archived" },
  "admin.buyers.searchPlaceholder": { vi: "Tìm kiếm người mua...", en: "Search buyers..." },
  "admin.buyers.assignTier": { vi: "Gán hạng", en: "Assign Tier" },
  "admin.buyers.allStatuses": { vi: "Tất cả trạng thái", en: "All statuses" },
  "admin.buyers.invited": { vi: "Đã mời", en: "Invited" },
  "admin.buyers.active": { vi: "Hoạt động", en: "Active" },
  "admin.buyers.suspended": { vi: "Tạm ngừng", en: "Suspended" },
  "admin.buyers.exportCSV": { vi: "Xuất CSV", en: "Export CSV" },
  "admin.buyers.importCSV": { vi: "Nhập CSV", en: "Import CSV" },
  "admin.buyers.inviteBuyer": { vi: "Mời người mua", en: "Invite Buyer" },
  "admin.buyers.importSuccess": { vi: "Nhập thành công {count} người mua.", en: "Successfully imported {count} buyer(s)." },
  "admin.buyers.importNone": { vi: "Không có người mua nào được nhập.", en: "No buyers were imported." },
  "admin.buyers.importErrors": { vi: "{count} lỗi:", en: "{count} error(s):" },
  "admin.buyers.allTiers": { vi: "Tất cả hạng", en: "All tiers" },
  "admin.buyers.status": { vi: "Trạng thái", en: "Status" },
  "admin.buyers.tier": { vi: "Hạng", en: "Tier" },
  "admin.buyers.clearAll": { vi: "Xóa tất cả", en: "Clear all" },
  "admin.buyers.activate": { vi: "Kích hoạt", en: "Activate" },
  "admin.buyers.suspend": { vi: "Tạm ngừng", en: "Suspend" },
  "admin.buyers.archive": { vi: "Lưu trữ", en: "Archive" },
  "admin.buyers.colCompany": { vi: "Công ty", en: "Company" },
  "admin.buyers.colContact": { vi: "Liên hệ", en: "Contact" },
  "admin.buyers.colEmail": { vi: "Email", en: "Email" },
  "admin.buyers.colTier": { vi: "Hạng", en: "Tier" },
  "admin.buyers.colStatus": { vi: "Trạng thái", en: "Status" },
  "admin.buyers.colOrders": { vi: "Đơn hàng", en: "Orders" },
  "admin.buyers.colTotalSales": { vi: "Tổng doanh số", en: "Total Sales" },
  "admin.buyers.colLastLogin": { vi: "Đăng nhập gần nhất", en: "Last Login" },
  "admin.buyers.never": { vi: "Chưa bao giờ", en: "Never" },
  "admin.buyers.noTier": { vi: "Không có hạng", en: "No tier" },
  "admin.buyers.noBuyersFound": { vi: "Không tìm thấy người mua", en: "No buyers found" },
  "admin.buyers.tryAdjustFilters": { vi: "Hãy thử điều chỉnh tìm kiếm hoặc bộ lọc.", en: "Try adjusting your search or filters." },
  "admin.buyers.getStarted": { vi: "Bắt đầu bằng cách mời người mua đầu tiên.", en: "Get started by inviting your first buyer." },
  "admin.buyers.assignTierTitle": { vi: "Gán hạng cho {count} người mua", en: "Assign Tier to {count} Buyer(s)" },
  "admin.buyers.selectTier": { vi: "Chọn hạng", en: "Select Tier" },
  "admin.buyers.removeTier": { vi: "Xóa hạng (không có hạng)", en: "Remove tier (no tier)" },
  "admin.buyers.confirmSuspendTitle": { vi: "Tạm ngừng {count} người mua?", en: "Suspend {count} buyer(s)?" },
  "admin.buyers.confirmArchiveTitle": { vi: "Lưu trữ {count} người mua?", en: "Archive {count} buyer(s)?" },
  "admin.buyers.suspendDesc": { vi: "Người mua bị tạm ngừng sẽ tạm thời bị chặn không thể đăng nhập và đặt đơn hàng. Bạn có thể kích hoạt lại sau.", en: "Suspended buyers will be temporarily blocked from logging in and placing orders. You can reactivate them later." },
  "admin.buyers.archiveDesc": { vi: "Người mua đã lưu trữ sẽ bị ẩn vĩnh viễn khỏi danh sách hoạt động. Lịch sử đơn hàng của họ sẽ được giữ lại.", en: "Archived buyers will be permanently hidden from the active list. Their order history will be preserved." },
  "admin.buyers.inviteBuyerTitle": { vi: "Mời người mua", en: "Invite Buyer" },
  "admin.buyers.sendInvitation": { vi: "Gửi lời mời", en: "Send Invitation" },
  "admin.buyers.companyName": { vi: "Tên công ty", en: "Company Name" },
  "admin.buyers.firstName": { vi: "Tên", en: "First Name" },
  "admin.buyers.lastName": { vi: "Họ", en: "Last Name" },
  "admin.buyers.emailLabel": { vi: "Email", en: "Email" },
  "admin.buyers.phoneLabel": { vi: "Điện thoại", en: "Phone" },
  "admin.buyers.buyerTier": { vi: "Hạng người mua", en: "Buyer Tier" },
  "admin.buyers.paymentTerms": { vi: "Điều khoản thanh toán", en: "Payment Terms" },
  "admin.buyers.paymentDefault": { vi: "Mặc định", en: "Default" },
  "admin.buyers.paymentDueReceipt": { vi: "Thanh toán ngay", en: "Due on Receipt" },
  // Common UI
  "admin.common.save": { vi: "Lưu", en: "Save" },
  "admin.common.cancel": { vi: "Hủy", en: "Cancel" },
  "admin.common.delete": { vi: "Xóa", en: "Delete" },
  "admin.common.edit": { vi: "Sửa", en: "Edit" },
  "admin.common.create": { vi: "Tạo", en: "Create" },
  "admin.common.close": { vi: "Đóng", en: "Close" },
  "admin.common.apply": { vi: "Áp dụng", en: "Apply" },
  "admin.common.enable": { vi: "Bật", en: "Enable" },
  "admin.common.disable": { vi: "Tắt", en: "Disable" },
  "admin.common.notSet": { vi: "Chưa đặt", en: "Not Set" },
  "admin.common.view": { vi: "Xem", en: "View" },
  "admin.common.viewAll": { vi: "Xem tất cả", en: "View all" },
  "admin.common.clear": { vi: "Xóa", en: "Clear" },
  "admin.common.reset": { vi: "Đặt lại", en: "Reset" },
  "admin.common.all": { vi: "Tất cả", en: "All" },
  "admin.common.refresh": { vi: "Làm mới", en: "Refresh" },
  "admin.common.export": { vi: "Xuất", en: "Export" },
  "admin.common.import": { vi: "Nhập", en: "Import" },
  "admin.common.archive": { vi: "Lưu trữ", en: "Archive" },
  "admin.common.restore": { vi: "Khôi phục", en: "Restore" },
  "admin.common.update": { vi: "Cập nhật", en: "Update" },
  "admin.common.add": { vi: "Thêm", en: "Add" },
  "admin.common.remove": { vi: "Xóa bỏ", en: "Remove" },
  "admin.common.active": { vi: "Hoạt động", en: "Active" },
  "admin.common.inactive": { vi: "Không hoạt động", en: "Inactive" },
  "admin.common.archived": { vi: "Đã lưu trữ", en: "Archived" },
  "admin.common.pending": { vi: "Đang chờ", en: "Pending" },
  "admin.common.approved": { vi: "Đã duyệt", en: "Approved" },
  "admin.common.rejected": { vi: "Đã từ chối", en: "Rejected" },
  "admin.common.draft": { vi: "Bản nháp", en: "Draft" },
  "admin.common.details": { vi: "Chi tiết", en: "Details" },
  "admin.common.selected": { vi: "đã chọn", en: "selected" },
  "admin.common.name": { vi: "Tên", en: "Name" },
  "admin.common.email": { vi: "Email", en: "Email" },
  "admin.common.phone": { vi: "Điện thoại", en: "Phone" },
  "admin.common.address": { vi: "Địa chỉ", en: "Address" },
  "admin.common.status": { vi: "Trạng thái", en: "Status" },
  "admin.common.date": { vi: "Ngày", en: "Date" },
  "admin.common.created": { vi: "Đã tạo", en: "Created" },
  "admin.common.updated": { vi: "Đã cập nhật", en: "Updated" },
  "admin.common.notes": { vi: "Ghi chú", en: "Notes" },
  "admin.common.description": { vi: "Mô tả", en: "Description" },
  "admin.common.required": { vi: "Bắt buộc", en: "Required" },
  "admin.common.optional": { vi: "Tùy chọn", en: "Optional" },
  "admin.common.submit": { vi: "Gửi", en: "Submit" },
  "admin.common.back": { vi: "Quay lại", en: "Back" },
  "admin.common.results": { vi: "kết quả", en: "results" },
  "admin.common.noResults": { vi: "Không có kết quả", en: "No results" },
  "admin.common.noData": { vi: "Không có dữ liệu", en: "No data" },
  "admin.common.error": { vi: "Lỗi", en: "Error" },
  "admin.common.success": { vi: "Thành công", en: "Success" },
  "admin.common.warning": { vi: "Cảnh báo", en: "Warning" },
  "admin.common.info": { vi: "Thông tin", en: "Info" },
  "admin.orders.order": { vi: "Đơn hàng", en: "Order" },
  "admin.orders.buyer": { vi: "Người mua", en: "Buyer" },
  "admin.orders.subtitle": { vi: "Quản lý đơn bán buôn và bán lẻ", en: "Manage wholesale and retail orders" },
  "admin.orders.orderSingular": { vi: "đơn hàng", en: "order" },
  "admin.orders.ordersPlural": { vi: "đơn hàng", en: "orders" },
  "admin.orders.allOrders": { vi: "Tất cả đơn hàng", en: "All Orders" },
  "admin.orders.unsyncedTitle": {
    vi: "{count} đơn bán buôn chưa được đồng bộ với Sapo",
    en: "{count} wholesale order(s) not synced to Sapo",
  },
  "admin.orders.syncToSapoAction": {
    vi: "Đồng bộ {count} đơn hàng lên Sapo",
    en: "Sync {count} order(s) to Sapo",
  },
  "admin.orders.unsyncedDesc": {
    vi: "Các đơn hàng này được tạo trước khi bật đồng bộ Sapo.",
    en: "These orders were created before Sapo sync was enabled.",
  },
  "admin.orders.approve": { vi: "Duyệt", en: "Approve" },
  "admin.orders.reject": { vi: "Từ chối", en: "Reject" },
  "admin.orders.confirm": { vi: "Xác nhận", en: "Confirm" },
  "admin.orders.confirmed": { vi: "Đã xác nhận", en: "Confirmed" },
  "admin.orders.bulkUpdateStatus": { vi: "Cập nhật trạng thái hàng loạt...", en: "Bulk Update Status..." },
  "admin.orders.shipped": { vi: "Đã giao vận", en: "Shipped" },
  "admin.orders.delivered": { vi: "Đã giao", en: "Delivered" },
  "admin.orders.searchRetailPlaceholder": {
    vi: "Tìm theo mã đơn, email, hoặc khách hàng...",
    en: "Search by order #, email, or customer...",
  },
  "admin.orders.searchWholesalePlaceholder": {
    vi: "Tìm theo mã đơn, mã PO, hoặc công ty...",
    en: "Search by order #, PO #, or company...",
  },
  "admin.orders.hideFilters": { vi: "Ẩn bộ lọc", en: "Hide Filters" },
  "admin.orders.advancedFilters": { vi: "Bộ lọc nâng cao", en: "Advanced Filters" },
  "admin.orders.activeLabel": { vi: "đang áp dụng", en: "active" },
  "admin.orders.clearAllFilters": { vi: "Xóa tất cả bộ lọc", en: "Clear all filters" },
  "admin.orders.orderStatus": { vi: "Trạng thái đơn", en: "Order Status" },
  "admin.orders.allStatuses": { vi: "Tất cả trạng thái", en: "All statuses" },
  "admin.orders.submitted": { vi: "Đã gửi", en: "Submitted" },
  "admin.orders.underReview": { vi: "Đang xem xét", en: "Under Review" },
  "admin.orders.refunded": { vi: "Đã hoàn tiền", en: "Refunded" },
  "admin.orders.paid": { vi: "Đã thanh toán", en: "Paid" },
  "admin.orders.paymentStatus": { vi: "Trạng thái thanh toán", en: "Payment Status" },
  "admin.orders.allPaymentStatuses": { vi: "Tất cả trạng thái thanh toán", en: "All payment statuses" },
  "admin.orders.unpaid": { vi: "Chưa thanh toán", en: "Unpaid" },
  "admin.orders.partiallyPaid": { vi: "Thanh toán một phần", en: "Partially Paid" },
  "admin.orders.customerFilterPlaceholder": {
    vi: "Lọc theo tên khách hàng...",
    en: "Filter by customer name...",
  },
  "admin.orders.dateFrom": { vi: "Từ ngày", en: "Date From" },
  "admin.orders.dateTo": { vi: "Đến ngày", en: "Date To" },
  "admin.orders.amountRange": { vi: "Khoảng giá trị", en: "Amount Range" },
  "admin.orders.min": { vi: "Min", en: "Min" },
  "admin.orders.max": { vi: "Max", en: "Max" },
  "admin.orders.thisWeek": { vi: "Tuần này", en: "This Week" },
  "admin.orders.thisMonth": { vi: "Tháng này", en: "This Month" },
  "admin.orders.applyFilters": { vi: "Áp dụng bộ lọc", en: "Apply Filters" },
  "admin.orders.noMatchSearch": {
    vi: "Không có đơn hàng nào khớp với tìm kiếm hoặc bộ lọc của bạn.",
    en: "No orders match your search or filter.",
  },
  "admin.orders.channel": { vi: "Kênh", en: "Channel" },
  "admin.orders.items": { vi: "Sản phẩm", en: "Items" },
  "admin.common.confirm": { vi: "Xác nhận", en: "Confirm" },
  "admin.common.loading": { vi: "Đang tải...", en: "Loading..." },
  "admin.common.search": { vi: "Tìm kiếm", en: "Search" },
  "admin.common.filter": { vi: "Lọc", en: "Filter" },
  "admin.common.actions": { vi: "Hành động", en: "Actions" },
  "admin.common.enabled": { vi: "Đã bật", en: "Enabled" },
  "admin.common.disabled": { vi: "Đã tắt", en: "Disabled" },
  "admin.common.yes": { vi: "Có", en: "Yes" },
  "admin.common.no": { vi: "Không", en: "No" },
  "admin.common.next": { vi: "Tiếp", en: "Next" },
  "admin.common.previous": { vi: "Trước", en: "Previous" },
  "admin.common.page": { vi: "Trang", en: "Page" },
  "admin.common.of": { vi: "của", en: "of" },
  "admin.common.total": { vi: "tổng", en: "total" },

  // ─── Admin Quotes ───
  "admin.quotes.title": { vi: "Báo giá", en: "Quotes" },
  "admin.quotes.metaTitle": { vi: "Báo giá | Diolo Admin", en: "Quotes | Diolo Admin" },
  "admin.quotes.detailMetaTitle": { vi: "Chi tiết báo giá | Diolo Admin", en: "Quote Detail | Diolo Admin" },
  "admin.quotes.disabledTitle": { vi: "Báo giá đã bị tắt", en: "Quotes Disabled" },
  "admin.quotes.disabledMessage": {
    vi: "Bật báo giá trong Cài đặt > Tích hợp để cho phép người mua gửi yêu cầu báo giá.",
    en: "Enable quotes in Settings > Integrations to allow buyers to submit quote requests.",
  },
  "admin.quotes.searchPlaceholder": { vi: "Tìm kiếm báo giá...", en: "Search quotes..." },
  "admin.quotes.tabs.all": { vi: "Tất cả", en: "All" },
  "admin.quotes.tabs.new": { vi: "Mới", en: "New" },
  "admin.quotes.tabs.review": { vi: "Đang xem xét", en: "In Review" },
  "admin.quotes.tabs.quoted": { vi: "Đã báo giá", en: "Quoted" },
  "admin.quotes.tabs.accepted": { vi: "Đã chấp nhận", en: "Accepted" },
  "admin.quotes.tabs.closed": { vi: "Đã đóng", en: "Closed" },
  "admin.quotes.column.number": { vi: "Mã báo giá", en: "Quote #" },
  "admin.quotes.column.buyer": { vi: "Người mua", en: "Buyer" },
  "admin.quotes.column.status": { vi: "Trạng thái", en: "Status" },
  "admin.quotes.column.items": { vi: "Sản phẩm", en: "Items" },
  "admin.quotes.column.total": { vi: "Tổng cộng", en: "Total" },
  "admin.quotes.column.submitted": { vi: "Đã gửi", en: "Submitted" },
  "admin.quotes.column.expires": { vi: "Hết hạn", en: "Expires" },
  "admin.quotes.empty": {
    vi: "Không tìm thấy báo giá. Báo giá sẽ xuất hiện khi người mua gửi yêu cầu.",
    en: "No quotes found. Quotes appear when buyers submit requests.",
  },
  "admin.quotes.backLink": { vi: "\u2190 Báo giá", en: "\u2190 Quotes" },
  "admin.quotes.backLabel": { vi: "Quay lại Báo giá", en: "Back to Quotes" },
  "admin.quotes.errorTitle": { vi: "Lỗi", en: "Error" },
  "admin.quotes.convertedTitle": { vi: "Báo giá đã chuyển đổi", en: "Quote Converted" },
  "admin.quotes.convertedMessage": {
    vi: "Báo giá này đã được chuyển thành đơn hàng",
    en: "This quote was converted to order",
  },
  "admin.quotes.actionRequired": { vi: "Cần xử lý", en: "Action Required" },
  "admin.quotes.actionRequiredMessage": {
    vi: "Báo giá này đã được người mua gửi. Bắt đầu xem xét để đặt giá đề nghị.",
    en: "This quote has been submitted by the buyer. Start a review to set offered prices.",
  },
  "admin.quotes.startReview": { vi: "Bắt đầu xem xét", en: "Start Review" },
  "admin.quotes.readyToConvert": { vi: "Sẵn sàng chuyển đổi", en: "Ready to Convert" },
  "admin.quotes.readyToConvertMessage": {
    vi: "Người mua đã chấp nhận báo giá này. Chuyển thành đơn hàng bán sỉ để bắt đầu xử lý.",
    en: "The buyer has accepted this quote. Convert it to a wholesale order to begin fulfillment.",
  },
  "admin.quotes.convertToOrder": { vi: "Chuyển thành đơn hàng", en: "Convert to Order" },
  "admin.quotes.itemsCardTitle": { vi: "Sản phẩm báo giá", en: "Quote Items" },
  "admin.quotes.itemsSubtitle": { vi: "{count} sản phẩm", en: "{count} items" },
  "admin.quotes.itemsSubtitleOne": { vi: "{count} sản phẩm", en: "{count} item" },
  "admin.quotes.table.product": { vi: "Sản phẩm", en: "Product" },
  "admin.quotes.table.sku": { vi: "SKU", en: "SKU" },
  "admin.quotes.table.qty": { vi: "SL", en: "Qty" },
  "admin.quotes.table.unitPrice": { vi: "Đơn giá", en: "Unit Price" },
  "admin.quotes.table.requested": { vi: "Yêu cầu", en: "Requested" },
  "admin.quotes.table.offeredPrice": { vi: "Giá đề nghị", en: "Offered Price" },
  "admin.quotes.table.offered": { vi: "Đã đề nghị", en: "Offered" },
  "admin.quotes.table.lineTotal": { vi: "Thành tiền", en: "Line Total" },
  "admin.quotes.summaryTitle": { vi: "Tóm tắt báo giá", en: "Quote Summary" },
  "admin.quotes.summary.subtotal": { vi: "Tạm tính", en: "Subtotal" },
  "admin.quotes.summary.discount": { vi: "Giảm giá", en: "Discount" },
  "admin.quotes.summary.grandTotal": { vi: "Tổng cộng", en: "Grand Total" },
  "admin.quotes.sendFormTitle": { vi: "Gửi báo giá cho người mua", en: "Send Quote to Buyer" },
  "admin.quotes.merchantNotesLabel": {
    vi: "Ghi chú của người bán (bao gồm trong báo giá)",
    en: "Merchant Notes (included in quote)",
  },
  "admin.quotes.merchantNotesPlaceholder": {
    vi: "Thêm ghi chú hoặc điều khoản cho người mua...",
    en: "Add any notes or terms for the buyer...",
  },
  "admin.quotes.expiryDaysLabel": {
    vi: "Thời hạn báo giá (ngày kể từ hôm nay)",
    en: "Quote Expiry (days from today)",
  },
  "admin.quotes.sendQuote": { vi: "Gửi báo giá", en: "Send Quote" },
  "admin.quotes.detailsTitle": { vi: "Chi tiết báo giá", en: "Quote Details" },
  "admin.quotes.details.quoteNumber": { vi: "Mã báo giá", en: "Quote Number" },
  "admin.quotes.details.status": { vi: "Trạng thái", en: "Status" },
  "admin.quotes.details.currency": { vi: "Tiền tệ", en: "Currency" },
  "admin.quotes.details.submitted": { vi: "Đã gửi", en: "Submitted" },
  "admin.quotes.details.quoted": { vi: "Đã báo giá", en: "Quoted" },
  "admin.quotes.details.accepted": { vi: "Đã chấp nhận", en: "Accepted" },
  "admin.quotes.details.expires": { vi: "Hết hạn", en: "Expires" },
  "admin.quotes.details.created": { vi: "Đã tạo", en: "Created" },
  "admin.quotes.buyerTitle": { vi: "Người mua", en: "Buyer" },
  "admin.quotes.salesRepTitle": { vi: "Nhân viên bán hàng", en: "Sales Rep" },
  "admin.quotes.buyerNotesTitle": { vi: "Ghi chú của người mua", en: "Buyer Notes" },
  "admin.quotes.merchantNotesTitle": { vi: "Ghi chú của người bán", en: "Merchant Notes" },

  // ─── Admin Analytics (Wholesale) ───
  "admin.analytics.title": { vi: "Phân tích bán buôn", en: "Wholesale Analytics" },
  "admin.analytics.viewRetail": { vi: "Xem phân tích bán lẻ →", en: "View Retail Analytics →" },
  "admin.analytics.needRawExports": {
    vi: "Cần xuất dữ liệu thô? Truy cập Báo cáo →",
    en: "Need raw data exports? Go to Reports →",
  },
  "admin.analytics.rangeThisMonth": { vi: "Tháng này", en: "This Month" },
  "admin.analytics.rangeLastMonth": { vi: "Tháng trước", en: "Last Month" },
  "admin.analytics.rangeLast3Months": { vi: "3 tháng gần nhất", en: "Last 3 Months" },
  "admin.analytics.rangeLast6Months": { vi: "6 tháng gần nhất", en: "Last 6 Months" },
  "admin.analytics.rangeYearToDate": { vi: "Từ đầu năm", en: "Year to Date" },
  "admin.analytics.rangeLast12Months": { vi: "12 tháng gần nhất", en: "Last 12 Months" },
  "admin.analytics.from": { vi: "Từ:", en: "From:" },
  "admin.analytics.to": { vi: "Đến:", en: "To:" },
  "admin.analytics.days7": { vi: "7 ngày", en: "7 days" },
  "admin.analytics.days30": { vi: "30 ngày", en: "30 days" },
  "admin.analytics.days90": { vi: "90 ngày", en: "90 days" },
  "admin.analytics.thisYear": { vi: "Năm nay", en: "This year" },
  "admin.analytics.revenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.analytics.orders": { vi: "Đơn hàng", en: "Orders" },
  "admin.analytics.avgOrderValue": { vi: "Giá trị đơn hàng trung bình", en: "Avg Order Value" },
  "admin.analytics.vsPriorPeriod": { vi: "so với kỳ trước", en: "vs prior period" },
  "admin.analytics.orderPipeline": { vi: "Luồng đơn hàng", en: "Order Pipeline" },
  "admin.analytics.noOrdersYet": { vi: "Chưa có đơn hàng", en: "No orders yet" },
  "admin.analytics.topBuyers": { vi: "Người mua hàng đầu", en: "Top Buyers" },
  "admin.analytics.ordersCount": { vi: "{count} đơn hàng", en: "{count} orders" },
  "admin.analytics.noDataYet": { vi: "Chưa có dữ liệu", en: "No data yet" },
  "admin.analytics.topProducts": { vi: "Sản phẩm hàng đầu", en: "Top Products" },
  "admin.analytics.colProduct": { vi: "Sản phẩm", en: "Product" },
  "admin.analytics.colRevenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.analytics.colUnitsSold": { vi: "Số lượng bán", en: "Units Sold" },
  "admin.analytics.pageViews": { vi: "Lượt xem trang", en: "Page Views" },
  "admin.analytics.uniqueVisitors": { vi: "Khách truy cập duy nhất", en: "Unique Visitors" },
  "admin.analytics.conversionRate": { vi: "Tỷ lệ chuyển đổi", en: "Conversion Rate" },
  "admin.analytics.conversionRateHelp": {
    vi: "Đơn hàng / Khách truy cập duy nhất",
    en: "Orders / Unique Visitors",
  },
  "admin.analytics.topPages": { vi: "Trang hàng đầu", en: "Top Pages" },
  "admin.analytics.colPage": { vi: "Trang", en: "Page" },
  "admin.analytics.colViews": { vi: "Lượt xem", en: "Views" },
  "admin.analytics.noTrafficYet": { vi: "Chưa có dữ liệu lưu lượng", en: "No traffic data yet" },
  "admin.analytics.rewardsTitle": { vi: "Diolo Rewards", en: "Diolo Rewards" },
  "admin.analytics.rewardsSubtitle": {
    vi: "Chương trình thành viên 7 hạng dựa trên chi tiêu",
    en: "Spend-based 7-tier loyalty",
  },
  "admin.analytics.rewardsManage": { vi: "Quản lý →", en: "Manage →" },
  "admin.analytics.totalAccounts": { vi: "Tổng số tài khoản", en: "Total Accounts" },
  "admin.analytics.lifetimeSpend": { vi: "Chi tiêu trọn đời", en: "Lifetime Spend" },
  "admin.analytics.tierDistribution": { vi: "Phân bố hạng thành viên", en: "Tier Distribution" },
  "admin.analytics.recentTierUps": { vi: "Thăng hạng gần đây", en: "Recent Tier-Ups" },
  "admin.analytics.noTierChanges": { vi: "Chưa có thay đổi hạng.", en: "No tier changes yet." },
  "admin.analytics.errorTitle": { vi: "Đã xảy ra lỗi", en: "Something went wrong" },
  "admin.analytics.errorStatus": { vi: "Lỗi {status}", en: "{status} Error" },
  "admin.analytics.errorGeneric": {
    vi: "Đã xảy ra lỗi khi tải trang này.",
    en: "An error occurred loading this page.",
  },
  "admin.analytics.errorUnexpected": {
    vi: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.",
    en: "An unexpected error occurred. Please try again.",
  },

  // ─── Admin Retail Analytics ───
  "admin.retailAnalytics.title": { vi: "Phân tích bán lẻ", en: "Retail Analytics" },
  "admin.retailAnalytics.viewWholesale": {
    vi: "Xem phân tích bán buôn →",
    en: "View Wholesale Analytics →",
  },
  "admin.retailAnalytics.needRawExports": {
    vi: "Cần xuất dữ liệu thô? Truy cập Báo cáo →",
    en: "Need raw data exports? Go to Reports →",
  },
  "admin.retailAnalytics.from": { vi: "Từ:", en: "From:" },
  "admin.retailAnalytics.to": { vi: "Đến:", en: "To:" },
  "admin.retailAnalytics.days7": { vi: "7 ngày", en: "7 days" },
  "admin.retailAnalytics.days30": { vi: "30 ngày", en: "30 days" },
  "admin.retailAnalytics.days90": { vi: "90 ngày", en: "90 days" },
  "admin.retailAnalytics.thisYear": { vi: "Năm nay", en: "This year" },
  "admin.retailAnalytics.totalOrders": { vi: "Tổng đơn hàng", en: "Total Orders" },
  "admin.retailAnalytics.totalRevenue": { vi: "Tổng doanh thu", en: "Total Revenue" },
  "admin.retailAnalytics.avgOrderValue": { vi: "Giá trị đơn hàng trung bình", en: "Avg Order Value" },
  "admin.retailAnalytics.totalCustomers": { vi: "Tổng khách hàng", en: "Total Customers" },
  "admin.retailAnalytics.newCustomers": { vi: "Khách hàng mới", en: "New Customers" },
  "admin.retailAnalytics.ordersFromNew": { vi: "Đơn từ khách mới", en: "Orders from New" },
  "admin.retailAnalytics.ordersFromReturning": {
    vi: "Đơn từ khách quay lại",
    en: "Orders from Returning",
  },
  "admin.retailAnalytics.vsPriorPeriod": { vi: "so với kỳ trước", en: "vs prior period" },
  "admin.retailAnalytics.cartFunnel": { vi: "Phễu giỏ hàng", en: "Cart funnel" },
  "admin.retailAnalytics.cartFunnelSubtitle": {
    vi: "Nguồn: sự kiện trực tiếp của bạn (không phụ thuộc Facebook Pixel hoặc Clarity).",
    en: "Source: your own first-party events (no Facebook Pixel or Clarity dependency).",
  },
  "admin.retailAnalytics.funnelViewContent": { vi: "Xem sản phẩm", en: "View Content" },
  "admin.retailAnalytics.funnelAddToCart": { vi: "Thêm vào giỏ", en: "Add to Cart" },
  "admin.retailAnalytics.funnelInitiateCheckout": { vi: "Bắt đầu thanh toán", en: "Initiate Checkout" },
  "admin.retailAnalytics.funnelPurchase": { vi: "Mua hàng", en: "Purchase" },
  "admin.retailAnalytics.funnelSessions": { vi: "phiên", en: "sessions" },
  "admin.retailAnalytics.funnelFromPrev": { vi: "từ bước trước", en: "from prev" },
  "admin.retailAnalytics.funnelOverallConv": { vi: "Tỷ lệ chuyển đổi tổng (Xem → Mua)", en: "Overall conversion (View → Buy)" },
  "admin.retailAnalytics.funnelAtcConv": { vi: "Tỷ lệ ATC → Mua", en: "ATC → Buy" },
  "admin.retailAnalytics.revenueOverTime": { vi: "Doanh thu theo thời gian", en: "Revenue Over Time" },
  "admin.retailAnalytics.noRevenueData": {
    vi: "Không có dữ liệu doanh thu cho kỳ này",
    en: "No revenue data for this period",
  },
  "admin.retailAnalytics.ordersOverTime": { vi: "Đơn hàng theo thời gian", en: "Orders Over Time" },
  "admin.retailAnalytics.noOrderData": {
    vi: "Không có dữ liệu đơn hàng cho kỳ này",
    en: "No order data for this period",
  },
  "admin.retailAnalytics.tooltipDate": { vi: "Ngày: {date}", en: "Date: {date}" },
  "admin.retailAnalytics.legendRevenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.retailAnalytics.legendOrders": { vi: "Đơn hàng", en: "Orders" },
  "admin.retailAnalytics.topSellingProducts": { vi: "Sản phẩm bán chạy nhất", en: "Top Selling Products" },
  "admin.retailAnalytics.colProduct": { vi: "Sản phẩm", en: "Product" },
  "admin.retailAnalytics.colUnitsSold": { vi: "Số lượng bán", en: "Units Sold" },
  "admin.retailAnalytics.colRevenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.retailAnalytics.colAvgPrice": { vi: "Giá trung bình", en: "Avg Price" },
  "admin.retailAnalytics.noProductData": { vi: "Chưa có dữ liệu sản phẩm", en: "No product data yet" },
  "admin.retailAnalytics.revenueByPayment": {
    vi: "Doanh thu theo phương thức thanh toán",
    en: "Revenue by Payment Method",
  },
  "admin.retailAnalytics.noPaymentData": {
    vi: "Không có dữ liệu thanh toán cho kỳ này",
    en: "No payment data for this period",
  },
  "admin.retailAnalytics.recentOrders": { vi: "Đơn hàng gần đây", en: "Recent Orders" },
  "admin.retailAnalytics.colOrderNumber": { vi: "Mã đơn", en: "Order #" },
  "admin.retailAnalytics.colCustomer": { vi: "Khách hàng", en: "Customer" },
  "admin.retailAnalytics.colTotal": { vi: "Tổng", en: "Total" },
  "admin.retailAnalytics.colStatus": { vi: "Trạng thái", en: "Status" },
  "admin.retailAnalytics.colDate": { vi: "Ngày", en: "Date" },
  "admin.retailAnalytics.noOrdersYet": { vi: "Chưa có đơn hàng", en: "No orders yet" },
  "admin.retailAnalytics.pageViews": { vi: "Lượt xem trang", en: "Page Views" },
  "admin.retailAnalytics.uniqueVisitors": { vi: "Khách truy cập duy nhất", en: "Unique Visitors" },
  "admin.retailAnalytics.conversionRate": { vi: "Tỷ lệ chuyển đổi", en: "Conversion Rate" },
  "admin.retailAnalytics.conversionRateHelp": {
    vi: "Đơn hàng / Khách truy cập duy nhất",
    en: "Orders / Unique Visitors",
  },
  "admin.retailAnalytics.topPages": { vi: "Trang hàng đầu", en: "Top Pages" },
  "admin.retailAnalytics.colPage": { vi: "Trang", en: "Page" },
  "admin.retailAnalytics.colViews": { vi: "Lượt xem", en: "Views" },
  "admin.retailAnalytics.noTrafficYet": { vi: "Chưa có dữ liệu lưu lượng", en: "No traffic data yet" },
  "admin.retailAnalytics.rangeToday": { vi: "Hôm nay", en: "Today" },
  "admin.retailAnalytics.rangeYesterday": { vi: "Hôm qua", en: "Yesterday" },
  "admin.retailAnalytics.rangeLast7Days": { vi: "7 ngày gần nhất", en: "Last 7 Days" },
  "admin.retailAnalytics.rangeLast14Days": { vi: "14 ngày gần nhất", en: "Last 14 Days" },
  "admin.retailAnalytics.rangeLast30Days": { vi: "30 ngày gần nhất", en: "Last 30 Days" },
  "admin.retailAnalytics.rangeThisMonth": { vi: "Tháng này", en: "This Month" },
  "admin.retailAnalytics.rangeLastMonth": { vi: "Tháng trước", en: "Last Month" },
  "admin.retailAnalytics.rangeLast3Months": { vi: "3 tháng gần nhất", en: "Last 3 Months" },
  "admin.retailAnalytics.rangeLast6Months": { vi: "6 tháng gần nhất", en: "Last 6 Months" },
  "admin.retailAnalytics.rangeYearToDate": { vi: "Từ đầu năm", en: "Year to Date" },
  "admin.retailAnalytics.rangeLast12Months": { vi: "12 tháng gần nhất", en: "Last 12 Months" },
  "admin.retailAnalytics.liveOn": { vi: "Đang trực tiếp", en: "Live" },
  "admin.retailAnalytics.liveOff": { vi: "Bật trực tiếp", en: "Go Live" },
  "admin.retailAnalytics.liveOnHelp": {
    vi: "Tự động làm mới mỗi 30 giây. Nhấp để tắt.",
    en: "Auto-refreshes every 30 seconds. Click to disable.",
  },
  "admin.retailAnalytics.liveOffHelp": {
    vi: "Nhấp để bật làm mới tự động mỗi 30 giây.",
    en: "Click to enable auto-refresh every 30 seconds.",
  },
  "admin.retailAnalytics.errorTitle": { vi: "Đã xảy ra lỗi", en: "Something went wrong" },
  "admin.retailAnalytics.errorStatus": { vi: "Lỗi {status}", en: "{status} Error" },
  "admin.retailAnalytics.errorGeneric": {
    vi: "Đã xảy ra lỗi khi tải trang này.",
    en: "An error occurred loading this page.",
  },
  "admin.retailAnalytics.errorUnexpected": {
    vi: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.",
    en: "An unexpected error occurred. Please try again.",
  },

  // ─── Admin Attribution ───
  "admin.attribution.title": { vi: "Nguồn doanh thu theo kênh", en: "Channel Attribution" },
  "admin.attribution.subtitle": {
    vi: "Theo dõi kênh tiếp thị nào mang lại đơn hàng và doanh thu.",
    en: "Track which marketing channels drive orders and revenue.",
  },
  "admin.attribution.rangeLast7Days": { vi: "7 ngày gần nhất", en: "Last 7 days" },
  "admin.attribution.rangeLast30Days": { vi: "30 ngày gần nhất", en: "Last 30 days" },
  "admin.attribution.rangeLast90Days": { vi: "90 ngày gần nhất", en: "Last 90 days" },
  "admin.attribution.rangeYearToDate": { vi: "Từ đầu năm", en: "Year to date" },
  "admin.attribution.rangeLast12Months": { vi: "12 tháng gần nhất", en: "Last 12 months" },
  "admin.attribution.totalOrders": { vi: "Tổng đơn hàng", en: "Total Orders" },
  "admin.attribution.totalRevenue": { vi: "Tổng doanh thu", en: "Total Revenue" },
  "admin.attribution.fromNewCustomers": { vi: "Từ khách hàng mới", en: "From New Customers" },
  "admin.attribution.fromReturning": { vi: "Từ khách quay lại", en: "From Returning" },
  "admin.attribution.ordersByChannel": {
    vi: "Đơn hàng theo kênh (Mới vs Quay lại)",
    en: "Orders by Channel (New vs Returning)",
  },
  "admin.attribution.legendNewCustomers": { vi: "Khách hàng mới", en: "New Customers" },
  "admin.attribution.legendReturningCustomers": { vi: "Khách quay lại", en: "Returning Customers" },
  "admin.attribution.channelBreakdown": { vi: "Phân tích theo kênh", en: "Channel Breakdown" },
  "admin.attribution.colChannel": { vi: "Kênh", en: "Channel" },
  "admin.attribution.colType": { vi: "Loại", en: "Type" },
  "admin.attribution.colOrders": { vi: "Đơn hàng", en: "Orders" },
  "admin.attribution.colRevenue": { vi: "Doanh thu", en: "Revenue" },
  "admin.attribution.colAov": { vi: "AOV", en: "AOV" },
  "admin.attribution.colNew": { vi: "Mới", en: "New" },
  "admin.attribution.colReturning": { vi: "Quay lại", en: "Returning" },
  "admin.attribution.noAttributedOrders": {
    vi: 'Chưa có đơn hàng được gán nguồn. Đơn hàng sẽ xuất hiện ở đây khi khách truy cập qua liên kết có gắn UTM.',
    en: 'No attributed orders yet. Orders will appear here once customers visit via UTM-tagged links.',
  },
  "admin.attribution.topCampaigns": { vi: "Chiến dịch hàng đầu", en: "Top Campaigns" },
  "admin.attribution.colCampaign": { vi: "Chiến dịch", en: "Campaign" },
  "admin.attribution.howItWorksTitle": { vi: "Cách hoạt động:", en: "How it works:" },
  "admin.attribution.howItWorksBody": {
    vi: 'Đơn hàng được gán nguồn dựa trên các tham số UTM (utm_source, utm_medium, utm_campaign) được ghi lại khi khách hàng truy cập cửa hàng. Đơn hàng không có dữ liệu UTM sẽ được phân loại là "Trực tiếp".',
    en: 'Orders are attributed to channels based on UTM parameters (utm_source, utm_medium, utm_campaign) captured when customers visit your storefront. Orders without UTM data are categorized as "Direct".',
  },

  // ─── Admin Forecasting ───
  "admin.forecasting.title": { vi: "Dự báo nhu cầu", en: "Demand Forecasting" },
  "admin.forecasting.subtitle": {
    vi: "Hoạch định tồn kho, phân tích tốc độ bán và đề xuất đặt hàng lại",
    en: "Inventory planning, sales velocity analysis, and reorder recommendations",
  },
  "admin.forecasting.leadTime": { vi: "Thời gian chờ:", en: "Lead Time:" },
  "admin.forecasting.safetyStock": { vi: "Tồn kho an toàn:", en: "Safety Stock:" },
  "admin.forecasting.days": { vi: "{count} ngày", en: "{count} days" },
  "admin.forecasting.urgentReorder": { vi: "Đặt hàng gấp", en: "Urgent Reorder" },
  "admin.forecasting.urgentReorderHelp": {
    vi: "Tồn kho dưới 14 ngày",
    en: "Less than 14 days of supply",
  },
  "admin.forecasting.planReorder": { vi: "Lên kế hoạch đặt", en: "Plan Reorder" },
  "admin.forecasting.planReorderHelp": { vi: "Tồn kho 14-30 ngày", en: "14-30 days of supply" },
  "admin.forecasting.healthyStock": { vi: "Tồn kho tốt", en: "Healthy Stock" },
  "admin.forecasting.healthyStockHelp": { vi: "Từ 30 ngày tồn kho trở lên", en: "30+ days of supply" },
  "admin.forecasting.reorderRecs": { vi: "Đề xuất đặt hàng lại", en: "Reorder Recommendations" },
  "admin.forecasting.reorderRecsSubtitle": {
    vi: "Sản phẩm cần đặt lại, sắp xếp theo mức độ khẩn cấp",
    en: "Products needing reorder, sorted by urgency",
  },
  "admin.forecasting.allSufficient": {
    vi: "Tất cả sản phẩm đều có tồn kho đủ (30+ ngày).",
    en: "All products have sufficient stock coverage (30+ days).",
  },
  "admin.forecasting.colProduct": { vi: "Sản phẩm", en: "Product" },
  "admin.forecasting.colCurrentStock": { vi: "Tồn kho hiện tại", en: "Current Stock" },
  "admin.forecasting.colDailyVelocity": { vi: "Tốc độ bán/ngày", en: "Daily Velocity" },
  "admin.forecasting.colDaysOfSupply": { vi: "Số ngày tồn kho", en: "Days of Supply" },
  "admin.forecasting.colSuggestedReorder": { vi: "Số lượng đề xuất đặt", en: "Suggested Reorder Qty" },
  "admin.forecasting.colReorderBy": { vi: "Đặt trước ngày", en: "Reorder By" },
  "admin.forecasting.reorderNow": { vi: "Đặt ngay", en: "Reorder NOW" },
  "admin.forecasting.perDay": { vi: "{count}/ngày", en: "{count}/day" },
  "admin.forecasting.noSales": { vi: "Không có doanh số", en: "No sales" },
  "admin.forecasting.badgeDaysUrgent": { vi: "{count}d - Gấp", en: "{count}d - Urgent" },
  "admin.forecasting.badgeDaysPlan": { vi: "{count}d - Kế hoạch", en: "{count}d - Plan" },
  "admin.forecasting.badgeDays": { vi: "{count}d", en: "{count}d" },
  "admin.forecasting.fastestMoving": {
    vi: "Top 10 sản phẩm bán nhanh nhất",
    en: "Top 10 Fastest-Moving Products",
  },
  "admin.forecasting.fastestMovingSubtitle": {
    vi: "Theo tốc độ bán hàng ngày (30 ngày gần nhất)",
    en: "By daily sales velocity (last 30 days)",
  },
  "admin.forecasting.slowestMoving": {
    vi: "Top 10 sản phẩm bán chậm nhất",
    en: "Top 10 Slowest-Moving Products",
  },
  "admin.forecasting.slowestMovingSubtitle": {
    vi: "Sản phẩm có tốc độ thấp nhất (đã có doanh số)",
    en: "Products with lowest velocity (that have sold)",
  },
  "admin.forecasting.noSalesDataAvailable": {
    vi: "Không có dữ liệu doanh số.",
    en: "No sales data available.",
  },
  "admin.forecasting.unitsSold": { vi: "{count} đã bán", en: "{count} sold" },
  "admin.forecasting.unitsSold90": { vi: "{count} đã bán (90 ngày)", en: "{count} sold (90d)" },
  "admin.forecasting.trendForecast": { vi: "Xu hướng và Dự báo doanh số", en: "Sales Trend & Forecast" },
  "admin.forecasting.trendForecastSubtitle": {
    vi: "Doanh số hàng ngày với SMA 7 ngày và dự báo 30 ngày",
    en: "Historical daily sales with 7-day SMA and 30-day projection",
  },
  "admin.forecasting.selectProduct": { vi: "Chọn sản phẩm...", en: "Select a product..." },
  "admin.forecasting.selectProductPrompt": {
    vi: "Chọn một sản phẩm ở trên để xem xu hướng và dự báo.",
    en: "Select a product above to view its sales trend and forecast.",
  },
  "admin.forecasting.noProductSalesData": {
    vi: "Không có dữ liệu doanh số cho sản phẩm này.",
    en: "No sales data available for this product.",
  },
  "admin.forecasting.legendDailySales": { vi: "Doanh số hàng ngày", en: "Daily Sales" },
  "admin.forecasting.legendSma7": { vi: "SMA 7 ngày", en: "7-Day SMA" },
  "admin.forecasting.legendProjected": { vi: "Dự báo", en: "Projected" },
  "admin.forecasting.seasonalComparison": { vi: "So sánh theo mùa", en: "Seasonal Comparison" },
  "admin.forecasting.seasonalSubtitle": {
    vi: "Số lượng bán theo tháng: {current} so với {previous}",
    en: "Monthly units sold: {current} vs {previous}",
  },
  "admin.forecasting.fullStockTitle": {
    vi: "Tổng quan toàn bộ tồn kho",
    en: "Complete Stock Coverage Overview",
  },
  "admin.forecasting.fullStockSubtitle": {
    vi: "Tất cả sản phẩm bán lẻ đang hoạt động với tình trạng tồn kho",
    en: "All active retail products with inventory health",
  },
  "admin.forecasting.filter": { vi: "Bộ lọc:", en: "Filter:" },
  "admin.forecasting.sort": { vi: "Sắp xếp:", en: "Sort:" },
  "admin.forecasting.filterAll": { vi: "Tất cả ({count})", en: "All ({count})" },
  "admin.forecasting.filterUrgent": { vi: "Gấp ({count})", en: "Urgent ({count})" },
  "admin.forecasting.filterPlan": { vi: "Lên kế hoạch đặt ({count})", en: "Plan Reorder ({count})" },
  "admin.forecasting.filterHealthy": { vi: "Tốt ({count})", en: "Healthy ({count})" },
  "admin.forecasting.sortSupply": {
    vi: "Số ngày tồn kho (thấp trước)",
    en: "Days of Supply (low first)",
  },
  "admin.forecasting.sortVelocity": { vi: "Tốc độ bán (cao trước)", en: "Velocity (high first)" },
  "admin.forecasting.sortStock": { vi: "Tồn kho (thấp trước)", en: "Stock (low first)" },
  "admin.forecasting.colStock": { vi: "Tồn kho", en: "Stock" },
  "admin.forecasting.col30dSales": { vi: "Bán 30 ngày", en: "30d Sales" },
  "admin.forecasting.col60dSales": { vi: "Bán 60 ngày", en: "60d Sales" },
  "admin.forecasting.col90dSales": { vi: "Bán 90 ngày", en: "90d Sales" },
  "admin.forecasting.colVelocityPerDay": { vi: "Tốc độ/ngày", en: "Velocity/day" },
  "admin.forecasting.colSupply": { vi: "Tồn kho", en: "Supply" },
  "admin.forecasting.noMatchingProducts": {
    vi: "Không có sản phẩm nào khớp với bộ lọc này.",
    en: "No products match this filter.",
  },
  "admin.forecasting.backToForecasting": { vi: "Quay lại Dự báo", en: "Back to Forecasting" },
  "admin.forecasting.loadError": {
    vi: "Không thể tải dữ liệu dự báo. Vui lòng thử lại.",
    en: "Failed to load forecasting data. Please try again.",
  },

  // --- Admin Discounts (index + detail) ---
  "admin.discounts.title": { vi: "Giảm giá", en: "Discounts" },
  "admin.discounts.metaTitle": { vi: "Giảm giá | Diolo Admin", en: "Discounts | Diolo Admin" },
  "admin.discounts.detailMetaTitle": { vi: "Chi tiết giảm giá | Diolo Admin", en: "Discount Detail | Diolo Admin" },
  "admin.discounts.generateBulkCodes": { vi: "Tạo mã hàng loạt", en: "Generate Bulk Codes" },
  "admin.discounts.createDiscount": { vi: "Tạo giảm giá", en: "Create discount" },
  "admin.discounts.saveDiscount": { vi: "Lưu giảm giá", en: "Save discount" },
  "admin.discounts.searchPlaceholder": { vi: "Tìm giảm giá theo mã...", en: "Search discounts by code..." },
  "admin.discounts.tabAll": { vi: "Tất cả", en: "All" },
  "admin.discounts.tabActive": { vi: "Đang hoạt động", en: "Active" },
  "admin.discounts.tabExpired": { vi: "Hết hạn", en: "Expired" },
  "admin.discounts.tabScheduled": { vi: "Đã lên lịch", en: "Scheduled" },
  "admin.discounts.colCode": { vi: "Mã", en: "Code" },
  "admin.discounts.colType": { vi: "Loại", en: "Type" },
  "admin.discounts.colValue": { vi: "Giá trị", en: "Value" },
  "admin.discounts.colUsage": { vi: "Sử dụng", en: "Usage" },
  "admin.discounts.colChannel": { vi: "Kênh", en: "Channel" },
  "admin.discounts.colStatus": { vi: "Trạng thái", en: "Status" },
  "admin.discounts.colDates": { vi: "Ngày", en: "Dates" },
  "admin.discounts.typePercentage": { vi: "Phần trăm", en: "Percentage" },
  "admin.discounts.typeFixedAmount": { vi: "Số tiền cố định", en: "Fixed Amount" },
  "admin.discounts.typeFreeShipping": { vi: "Miễn phí vận chuyển", en: "Free Shipping" },
  "admin.discounts.typeBuyXGetY": { vi: "Mua X tặng Y", en: "Buy X Get Y" },
  "admin.discounts.typeFixedAmountLower": { vi: "Số tiền cố định", en: "Fixed amount" },
  "admin.discounts.typeFreeShippingLower": { vi: "Miễn phí vận chuyển", en: "Free shipping" },
  "admin.discounts.valueFreeShipping": { vi: "Miễn phí vận chuyển", en: "Free shipping" },
  "admin.discounts.valueBogo": { vi: "BOGO", en: "BOGO" },
  "admin.discounts.channelRetail": { vi: "Bán lẻ", en: "Retail" },
  "admin.discounts.channelWholesale": { vi: "Bán buôn", en: "Wholesale" },
  "admin.discounts.channelBoth": { vi: "Cả hai", en: "Both" },
  "admin.discounts.statusActive": { vi: "Hoạt động", en: "Active" },
  "admin.discounts.statusInactive": { vi: "Không hoạt động", en: "Inactive" },
  "admin.discounts.noEndDate": { vi: "Không có ngày kết thúc", en: "No end date" },
  "admin.discounts.activate": { vi: "Kích hoạt", en: "Activate" },
  "admin.discounts.deactivate": { vi: "Tạm ngừng", en: "Deactivate" },
  "admin.discounts.delete": { vi: "Xóa", en: "Delete" },
  "admin.discounts.duplicate": { vi: "Nhân bản", en: "Duplicate" },
  "admin.discounts.emptyState": {
    vi: "Không tìm thấy mã giảm giá nào. Tạo mã giảm giá đầu tiên để bắt đầu.",
    en: "No discounts found. Create your first discount to get started.",
  },
  "admin.discounts.confirmDelete": {
    vi: "Bạn có chắc chắn muốn xóa mã giảm giá này?",
    en: "Are you sure you want to delete this discount?",
  },
  "admin.discounts.bannerErrorTitle": { vi: "Lỗi", en: "Error" },
  "admin.discounts.bannerBulkSuccessTitle": { vi: "Đã tạo mã hàng loạt", en: "Bulk codes generated" },
  "admin.discounts.bannerBulkSuccessBody": {
    vi: "Đã tạo thành công {count} mã giảm giá.",
    en: "Successfully created {count} discount codes.",
  },
  "admin.discounts.bannerSavedTitle": { vi: "Đã lưu", en: "Saved" },
  "admin.discounts.bannerSavedBody": {
    vi: "Cập nhật mã giảm giá thành công.",
    en: "Discount updated successfully.",
  },
  "admin.discounts.usageUnlimitedSuffix": { vi: " / không giới hạn", en: " / unlimited" },
  // Bulk code modal
  "admin.discounts.bulkModalTitle": { vi: "Tạo mã giảm giá hàng loạt", en: "Generate Bulk Discount Codes" },
  "admin.discounts.bulkPreview": { vi: "Xem trước", en: "Preview" },
  "admin.discounts.bulkNumberOfCodes": { vi: "Số lượng mã", en: "Number of codes" },
  "admin.discounts.bulkNumberOfCodesHelp": { vi: "Tối đa 500 mã mỗi lần", en: "Max 500 at a time" },
  "admin.discounts.bulkPrefix": { vi: "Tiền tố (tùy chọn)", en: "Prefix (optional)" },
  "admin.discounts.bulkPrefixPlaceholder": { vi: "VD: SUMMER-", en: "e.g. SUMMER-" },
  "admin.discounts.bulkSuffixLength": { vi: "Độ dài hậu tố ngẫu nhiên", en: "Random suffix length" },
  "admin.discounts.bulkSuffixLengthHelp": {
    vi: "Số ký tự chữ và số ngẫu nhiên",
    en: "Number of random alphanumeric characters",
  },
  "admin.discounts.bulkDiscountType": { vi: "Loại giảm giá", en: "Discount type" },
  "admin.discounts.bulkValue": { vi: "Giá trị", en: "Value" },
  "admin.discounts.bulkValueHelpPercentage": { vi: "Phần trăm giảm", en: "Percentage off" },
  "admin.discounts.bulkValueHelpAmount": { vi: "Số tiền giảm", en: "Amount off" },
  "admin.discounts.bulkMaxUses": { vi: "Số lần sử dụng tối đa mỗi mã", en: "Max uses per code" },
  "admin.discounts.bulkMaxUsesPlaceholder": { vi: "Không giới hạn", en: "Unlimited" },
  "admin.discounts.bulkChannel": { vi: "Kênh", en: "Channel" },
  "admin.discounts.bulkStartDate": { vi: "Ngày bắt đầu", en: "Start date" },
  "admin.discounts.bulkEndDate": { vi: "Ngày kết thúc (tùy chọn)", en: "End date (optional)" },
  "admin.discounts.bulkCancel": { vi: "Hủy", en: "Cancel" },
  "admin.discounts.bulkGenerateN": { vi: "Tạo {count} mã", en: "Generate {count} codes" },
  // Discount detail form
  "admin.discounts.backToDiscounts": { vi: "Giảm giá", en: "Discounts" },
  "admin.discounts.sectionDetails": { vi: "Thông tin mã giảm giá", en: "Discount details" },
  "admin.discounts.discountCode": { vi: "Mã giảm giá", en: "Discount code" },
  "admin.discounts.discountCodePlaceholder": { vi: "VD: SUMMER20", en: "e.g. SUMMER20" },
  "admin.discounts.generateCode": { vi: "Tạo mã ngẫu nhiên", en: "Generate code" },
  "admin.discounts.discountType": { vi: "Loại giảm giá", en: "Discount type" },
  "admin.discounts.valueLabelPercentage": { vi: "Phần trăm giảm giá", en: "Discount percentage" },
  "admin.discounts.valueLabelFixedAmount": { vi: "Số tiền giảm giá", en: "Discount amount" },
  "admin.discounts.valueLabelDefault": { vi: "Giá trị", en: "Value" },
  "admin.discounts.valuePercentageHelp": {
    vi: "Nhập giá trị từ 0 đến 100",
    en: "Enter a value between 0 and 100",
  },
  "admin.discounts.sectionAppliesTo": { vi: "Áp dụng cho", en: "Applies to" },
  "admin.discounts.appliesToAll": { vi: "Tất cả sản phẩm", en: "All products" },
  "admin.discounts.appliesToSpecificProducts": { vi: "Sản phẩm cụ thể", en: "Specific products" },
  "admin.discounts.appliesToSpecificCollections": { vi: "Bộ sưu tập cụ thể", en: "Specific collections" },
  "admin.discounts.productIdsLabel": { vi: "Mã sản phẩm (IDs)", en: "Product IDs" },
  "admin.discounts.productIdsPlaceholder": {
    vi: "Nhập mã sản phẩm, phân cách bằng dấu phẩy",
    en: "Enter product IDs separated by commas",
  },
  "admin.discounts.productIdsHelp": {
    vi: "Danh sách mã sản phẩm, phân cách bằng dấu phẩy",
    en: "Comma-separated list of product IDs",
  },
  "admin.discounts.collectionIdsLabel": { vi: "Mã bộ sưu tập (IDs)", en: "Collection IDs" },
  "admin.discounts.collectionIdsPlaceholder": {
    vi: "Nhập mã bộ sưu tập, phân cách bằng dấu phẩy",
    en: "Enter collection IDs separated by commas",
  },
  "admin.discounts.collectionIdsHelp": {
    vi: "Danh sách mã bộ sưu tập, phân cách bằng dấu phẩy",
    en: "Comma-separated list of collection IDs",
  },
  "admin.discounts.sectionChannel": { vi: "Kênh", en: "Channel" },
  "admin.discounts.sectionMinimumRequirements": { vi: "Yêu cầu tối thiểu", en: "Minimum requirements" },
  "admin.discounts.minOrderAmount": { vi: "Giá trị đơn hàng tối thiểu ($)", en: "Minimum order amount ($)" },
  "admin.discounts.minPlaceholder": { vi: "Không tối thiểu", en: "No minimum" },
  "admin.discounts.minHelp": {
    vi: "Để trống nếu không yêu cầu tối thiểu",
    en: "Leave blank for no minimum",
  },
  "admin.discounts.minQuantity": { vi: "Số lượng tối thiểu", en: "Minimum quantity" },
  "admin.discounts.sectionUsageLimits": { vi: "Giới hạn sử dụng", en: "Usage limits" },
  "admin.discounts.maximumUses": { vi: "Số lần sử dụng tối đa", en: "Maximum uses" },
  "admin.discounts.maxUsesPlaceholder": { vi: "Không giới hạn", en: "Unlimited" },
  "admin.discounts.maxUsesHelp": {
    vi: "Để trống để không giới hạn số lần sử dụng",
    en: "Leave blank for unlimited uses",
  },
  "admin.discounts.usedLabel": { vi: "Đã dùng", en: "Used" },
  "admin.discounts.usedTimes": { vi: "{count} lần", en: "{count} times" },
  "admin.discounts.usedTime": { vi: "{count} lần", en: "{count} time" },
  "admin.discounts.sectionActiveDates": { vi: "Ngày hiệu lực", en: "Active dates" },
  "admin.discounts.startDate": { vi: "Ngày bắt đầu", en: "Start date" },
  "admin.discounts.endDateOptional": { vi: "Ngày kết thúc (tùy chọn)", en: "End date (optional)" },
  "admin.discounts.endDateHelp": {
    vi: "Để trống nếu không có ngày kết thúc",
    en: "Leave blank for no end date",
  },
  "admin.discounts.sectionStatus": { vi: "Trạng thái", en: "Status" },
  "admin.discounts.isActiveLabel": { vi: "Mã giảm giá đang hoạt động", en: "Discount is active" },
  "admin.discounts.isActiveHelp": {
    vi: "Tắt để tạm ngừng mã giảm giá mà không cần xóa",
    en: "Deactivate to temporarily disable this discount without deleting it",
  },
  "admin.discounts.errorBoundaryBackLabel": { vi: "Quay lại Giảm giá", en: "Back to Discounts" },
  // --- Admin Discount Analytics ---
  "admin.discountAnalytics.title": { vi: "Phân tích giảm giá", en: "Discount Analytics" },
  "admin.discountAnalytics.metaTitle": {
    vi: "Phân tích giảm giá | Diolo Admin",
    en: "Discount Analytics | Diolo Admin",
  },
  "admin.discountAnalytics.allChannels": { vi: "Tất cả kênh", en: "All Channels" },
  "admin.discountAnalytics.channelRetail": { vi: "Bán lẻ", en: "Retail" },
  "admin.discountAnalytics.channelWholesale": { vi: "Bán buôn", en: "Wholesale" },
  "admin.discountAnalytics.channelBoth": { vi: "Cả hai", en: "Both" },
  "admin.discountAnalytics.rangeThisMonth": { vi: "Tháng này", en: "This Month" },
  "admin.discountAnalytics.rangeLastMonth": { vi: "Tháng trước", en: "Last Month" },
  "admin.discountAnalytics.rangeLast3Months": { vi: "3 tháng gần nhất", en: "Last 3 Months" },
  "admin.discountAnalytics.rangeLast6Months": { vi: "6 tháng gần nhất", en: "Last 6 Months" },
  "admin.discountAnalytics.rangeYearToDate": { vi: "Từ đầu năm", en: "Year to Date" },
  "admin.discountAnalytics.rangeLast12Months": { vi: "12 tháng gần nhất", en: "Last 12 Months" },
  "admin.discountAnalytics.from": { vi: "Từ:", en: "From:" },
  "admin.discountAnalytics.to": { vi: "Đến:", en: "To:" },
  "admin.discountAnalytics.days7": { vi: "7 ngày", en: "7 days" },
  "admin.discountAnalytics.days30": { vi: "30 ngày", en: "30 days" },
  "admin.discountAnalytics.days90": { vi: "90 ngày", en: "90 days" },
  "admin.discountAnalytics.thisYear": { vi: "Năm nay", en: "This year" },
  "admin.discountAnalytics.kpiTotalUses": { vi: "Tổng lượt sử dụng", en: "Total Uses" },
  "admin.discountAnalytics.kpiTotalSavings": { vi: "Tổng tiết kiệm", en: "Total Savings" },
  "admin.discountAnalytics.kpiAvgDiscount": { vi: "Giảm giá trung bình", en: "Avg Discount" },
  "admin.discountAnalytics.kpiActiveCodes": { vi: "Mã đang hoạt động", en: "Active Codes" },
  "admin.discountAnalytics.topDiscountCodes": { vi: "Mã giảm giá hàng đầu", en: "Top Discount Codes" },
  "admin.discountAnalytics.colCode": { vi: "Mã", en: "Code" },
  "admin.discountAnalytics.colType": { vi: "Loại", en: "Type" },
  "admin.discountAnalytics.colChannel": { vi: "Kênh", en: "Channel" },
  "admin.discountAnalytics.colUses": { vi: "Lượt dùng", en: "Uses" },
  "admin.discountAnalytics.colTotalSavings": { vi: "Tổng tiết kiệm", en: "Total Savings" },
  "admin.discountAnalytics.typeFreeShip": { vi: "Miễn phí vận chuyển", en: "Free Ship" },
  "admin.discountAnalytics.emptyUsage": {
    vi: "Không có lượt dùng mã giảm giá trong giai đoạn này",
    en: "No discount usage in this period",
  },
  "admin.discountAnalytics.monthlyUsage": { vi: "Lượt sử dụng theo tháng", en: "Monthly Usage" },
  "admin.discountAnalytics.emptyMonthly": { vi: "Không có dữ liệu cho giai đoạn này", en: "No data for this period" },
  "admin.discountAnalytics.usesLabel": { vi: "{count} lượt", en: "{count} uses" },
  "admin.discountAnalytics.errorBoundaryBackLabel": {
    vi: "Quay lại Phân tích giảm giá",
    en: "Back to Discount Analytics",
  },

  // ─── Admin — Invoices ───
  "admin.invoices.title": { vi: "Hóa đơn", en: "Invoices" },
  "admin.invoices.subtitle": {
    vi: "Quản lý hóa đơn và thanh toán",
    en: "Manage invoices and payments",
  },
  "admin.invoices.metaTitle": { vi: "Hóa đơn | Diolo Admin", en: "Invoices | Diolo Admin" },
  "admin.invoices.detailMetaTitle": {
    vi: "Chi tiết hóa đơn | Diolo Admin",
    en: "Invoice Detail | Diolo Admin",
  },
  "admin.invoices.totalReceivables": { vi: "Tổng phải thu", en: "Total Receivables" },
  "admin.invoices.notYetDue": { vi: "Chưa đến hạn", en: "Not Yet Due" },
  "admin.invoices.overdue": { vi: "Quá hạn", en: "Overdue" },
  "admin.invoices.totalReceived": { vi: "Đã thu", en: "Total Received" },
  "admin.invoices.searchPlaceholder": {
    vi: "Tìm theo mã hóa đơn, công ty hoặc email...",
    en: "Search by invoice #, company, or email...",
  },
  "admin.invoices.tabAll": { vi: "Tất cả", en: "All" },
  "admin.invoices.tabUnpaid": { vi: "Chưa thanh toán", en: "Unpaid" },
  "admin.invoices.tabOverdue": { vi: "Quá hạn", en: "Overdue" },
  "admin.invoices.tabCleared": { vi: "Đã xử lý", en: "Cleared" },
  "admin.invoices.selectedCount": {
    vi: "Đã chọn {count} hóa đơn",
    en: "{count} invoice(s) selected",
  },
  "admin.invoices.markAsPaid": { vi: "Đánh dấu đã thanh toán", en: "Mark as Paid" },
  "admin.invoices.sendReminder": { vi: "Gửi nhắc nhở", en: "Send Reminder" },
  "admin.invoices.sendReminderEmail": {
    vi: "Gửi email nhắc thanh toán",
    en: "Send Reminder Email",
  },
  "admin.invoices.exportSelected": { vi: "Xuất đã chọn", en: "Export Selected" },
  "admin.invoices.clear": { vi: "Xóa chọn", en: "Clear" },
  "admin.invoices.bulkAction": { vi: "Thao tác hàng loạt", en: "Bulk Action" },
  "admin.invoices.bulkActionTitle": {
    vi: "{action} - {count} hóa đơn",
    en: "{action} - {count} invoice(s)",
  },
  "admin.invoices.colInvoiceNumber": { vi: "Mã hóa đơn", en: "Invoice #" },
  "admin.invoices.colStatus": { vi: "Trạng thái", en: "Status" },
  "admin.invoices.colBuyer": { vi: "Người mua", en: "Buyer" },
  "admin.invoices.colOrder": { vi: "Đơn hàng", en: "Order" },
  "admin.invoices.colTotal": { vi: "Tổng cộng", en: "Total" },
  "admin.invoices.colDue": { vi: "Còn phải trả", en: "Due" },
  "admin.invoices.colDueDate": { vi: "Hạn thanh toán", en: "Due Date" },
  "admin.invoices.colActions": { vi: "Thao tác", en: "Actions" },
  "admin.invoices.statusDraft": { vi: "Nháp", en: "Draft" },
  "admin.invoices.statusSent": { vi: "Đã gửi", en: "Sent" },
  "admin.invoices.statusViewed": { vi: "Đã xem", en: "Viewed" },
  "admin.invoices.statusPartiallyPaid": {
    vi: "Thanh toán một phần",
    en: "Partially Paid",
  },
  "admin.invoices.statusPaid": { vi: "Đã thanh toán", en: "Paid" },
  "admin.invoices.statusOverdue": { vi: "Quá hạn", en: "Overdue" },
  "admin.invoices.statusVoided": { vi: "Đã hủy bỏ", en: "Voided" },
  "admin.invoices.statusRefunded": { vi: "Đã hoàn tiền", en: "Refunded" },
  "admin.invoices.emptyAll": {
    vi: "Chưa có hóa đơn. Hóa đơn được tạo từ các đơn hàng đã duyệt.",
    en: "No invoices yet. Invoices are created from approved orders.",
  },
  "admin.invoices.emptyFiltered": {
    vi: "Không có hóa đơn nào phù hợp với bộ lọc hiện tại.",
    en: "No invoices match your current filters.",
  },
  "admin.invoices.markPaidTitle": {
    vi: "Đánh dấu hóa đơn đã thanh toán?",
    en: "Mark invoice as paid?",
  },
  "admin.invoices.markPaidBody": {
    vi: "Thao tác này sẽ ghi nhận thanh toán đầy đủ cho hóa đơn {invoiceNumber} và đánh dấu là đã thanh toán. Không thể hoàn tác.",
    en: "This will record a full payment for invoice {invoiceNumber} and mark it as paid. This action cannot be undone.",
  },
  "admin.invoices.bulkMarkPaidBody": {
    vi: "Thao tác này sẽ ghi nhận thanh toán đầy đủ cho {count} hóa đơn đã chọn và đánh dấu là đã thanh toán. Không thể hoàn tác.",
    en: "This will record full payment for {count} selected invoice(s) and mark them as paid. This action cannot be undone.",
  },
  "admin.invoices.bulkReminderBody": {
    vi: "Thao tác này sẽ gửi email nhắc thanh toán đến người mua của {count} hóa đơn đã chọn. Chỉ những hóa đơn chưa thanh toán hoặc quá hạn mới nhận được nhắc nhở.",
    en: "This will send a payment reminder email to the buyers of {count} selected invoice(s). Only invoices with unpaid or overdue status will receive reminders.",
  },
  "admin.invoices.bulkExportBody": {
    vi: "Thao tác này sẽ tải xuống tệp CSV chứa {count} hóa đơn đã chọn.",
    en: "This will download a CSV file containing {count} selected invoice(s).",
  },
  "admin.invoices.errorStatus": { vi: "Lỗi {status}", en: "{status} Error" },
  "admin.invoices.errorGeneric": {
    vi: "Đã có lỗi xảy ra",
    en: "Something went wrong",
  },
  "admin.invoices.errorLoad": {
    vi: "Đã xảy ra lỗi khi tải trang này.",
    en: "An error occurred loading this page.",
  },
  "admin.invoices.errorUnexpected": {
    vi: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.",
    en: "An unexpected error occurred. Please try again.",
  },
  "admin.invoices.breadcrumbInvoice": {
    vi: "Hóa đơn {number}",
    en: "Invoice {number}",
  },
  "admin.invoices.downloadPdf": { vi: "Tải PDF", en: "Download PDF" },
  "admin.invoices.sendInvoice": { vi: "Gửi hóa đơn", en: "Send Invoice" },
  "admin.invoices.deleteDraft": { vi: "Xóa bản nháp", en: "Delete Draft" },
  "admin.invoices.recordPayment": { vi: "Ghi nhận thanh toán", en: "Record Payment" },
  "admin.invoices.sendOverdueNotice": {
    vi: "Gửi thông báo quá hạn",
    en: "Send Overdue Notice",
  },
  "admin.invoices.void": { vi: "Hủy bỏ", en: "Void" },
  "admin.invoices.markStatus": {
    vi: "Đánh dấu {status}",
    en: "Mark {status}",
  },
  "admin.invoices.overdueBannerTitle": {
    vi: "Hóa đơn này đã quá hạn",
    en: "This invoice is overdue",
  },
  "admin.invoices.overdueBannerBody": {
    vi: "Hạn thanh toán đã qua vào ngày {date}.",
    en: "Payment was due on {date}.",
  },
  "admin.invoices.cardDetails": { vi: "Chi tiết hóa đơn", en: "Invoice Details" },
  "admin.invoices.cardLineItems": { vi: "Sản phẩm", en: "Line Items" },
  "admin.invoices.cardOrderSummary": { vi: "Tóm tắt đơn hàng", en: "Order Summary" },
  "admin.invoices.cardPaymentHistory": {
    vi: "Lịch sử thanh toán",
    en: "Payment History",
  },
  "admin.invoices.cardBuyer": { vi: "Người mua", en: "Buyer" },
  "admin.invoices.cardLinkedOrder": { vi: "Đơn hàng liên kết", en: "Linked Order" },
  "admin.invoices.cardKeyDates": { vi: "Các mốc thời gian", en: "Key Dates" },
  "admin.invoices.cardInvoiceNotes": { vi: "Ghi chú hóa đơn", en: "Invoice Notes" },
  "admin.invoices.cardInvoiceNotesSubtitle": {
    vi: "Hiển thị trên PDF gửi đến người mua.",
    en: "Shown on the PDF sent to the buyer.",
  },
  "admin.invoices.cardInternalNotes": { vi: "Ghi chú nội bộ", en: "Internal Notes" },
  "admin.invoices.fieldInvoiceNumber": { vi: "Mã hóa đơn", en: "Invoice Number" },
  "admin.invoices.fieldIssueDate": { vi: "Ngày xuất", en: "Issue Date" },
  "admin.invoices.fieldDueDate": { vi: "Hạn thanh toán", en: "Due Date" },
  "admin.invoices.fieldPaymentTerms": {
    vi: "Điều khoản thanh toán",
    en: "Payment Terms",
  },
  "admin.invoices.liProduct": { vi: "Sản phẩm", en: "Product" },
  "admin.invoices.liSku": { vi: "SKU", en: "SKU" },
  "admin.invoices.liQty": { vi: "SL", en: "Qty" },
  "admin.invoices.liUnitPrice": { vi: "Đơn giá", en: "Unit Price" },
  "admin.invoices.liTotal": { vi: "Thành tiền", en: "Total" },
  "admin.invoices.summarySubtotal": { vi: "Tạm tính", en: "Subtotal" },
  "admin.invoices.summaryDiscount": { vi: "Giảm giá", en: "Discount" },
  "admin.invoices.summaryShipping": { vi: "Vận chuyển", en: "Shipping" },
  "admin.invoices.summaryTax": { vi: "Thuế", en: "Tax" },
  "admin.invoices.summaryGrandTotal": { vi: "Tổng cộng", en: "Grand Total" },
  "admin.invoices.summaryAmountPaid": { vi: "Đã thanh toán", en: "Amount Paid" },
  "admin.invoices.summaryAmountDue": { vi: "Còn phải trả", en: "Amount Due" },
  "admin.invoices.paymentStatusPending": { vi: "Chờ duyệt", en: "Pending" },
  "admin.invoices.paymentStatusProcessing": { vi: "Đang xử lý", en: "Processing" },
  "admin.invoices.paymentStatusSucceeded": { vi: "Thành công", en: "Succeeded" },
  "admin.invoices.paymentStatusFailed": { vi: "Thất bại", en: "Failed" },
  "admin.invoices.paymentStatusRefunded": { vi: "Đã hoàn tiền", en: "Refunded" },
  "admin.invoices.paymentRef": { vi: "Mã tham chiếu: {ref}", en: "Ref: {ref}" },
  "admin.invoices.paymentReason": {
    vi: "Lý do: {reason}",
    en: "Reason: {reason}",
  },
  "admin.invoices.viewProof": {
    vi: "Xem chứng từ ({file})",
    en: "View Proof ({file})",
  },
  "admin.invoices.viewReceipt": { vi: "Xem biên lai", en: "View Receipt" },
  "admin.invoices.approve": { vi: "Duyệt", en: "Approve" },
  "admin.invoices.reject": { vi: "Từ chối", en: "Reject" },
  "admin.invoices.methodStripe": { vi: "Stripe", en: "Stripe" },
  "admin.invoices.methodBankTransfer": {
    vi: "Chuyển khoản ngân hàng",
    en: "Bank Transfer",
  },
  "admin.invoices.methodCheck": { vi: "Séc", en: "Check" },
  "admin.invoices.methodCod": {
    vi: "Thanh toán khi nhận hàng",
    en: "Cash on Delivery",
  },
  "admin.invoices.methodMomo": { vi: "MoMo", en: "MoMo" },
  "admin.invoices.methodZalopay": { vi: "ZaloPay", en: "ZaloPay" },
  "admin.invoices.methodOnepay": { vi: "OnePay", en: "OnePay" },
  "admin.invoices.methodOther": { vi: "Khác", en: "Other" },
  "admin.invoices.dateCreated": { vi: "Tạo", en: "Created" },
  "admin.invoices.dateIssued": { vi: "Xuất", en: "Issued" },
  "admin.invoices.dateDue": { vi: "Hạn", en: "Due" },
  "admin.invoices.datePaid": { vi: "Đã thanh toán", en: "Paid" },
  "admin.invoices.dateVoided": { vi: "Đã hủy bỏ", en: "Voided" },
  "admin.invoices.edit": { vi: "Sửa", en: "Edit" },
  "admin.invoices.save": { vi: "Lưu", en: "Save" },
  "admin.invoices.cancel": { vi: "Hủy", en: "Cancel" },
  "admin.invoices.noNotes": { vi: "Không có ghi chú", en: "No notes" },
  "admin.invoices.voidTitle": { vi: "Hủy bỏ hóa đơn này?", en: "Void this invoice?" },
  "admin.invoices.voidBody": {
    vi: "Việc hủy bỏ hóa đơn {invoiceNumber} không thể hoàn tác. Hóa đơn sẽ được đánh dấu đã hủy bỏ vĩnh viễn và không thể ghi nhận thêm thanh toán.",
    en: "Voiding invoice {invoiceNumber} cannot be undone. The invoice will be permanently marked as void and no further payments can be recorded against it.",
  },
  "admin.invoices.voidInvoice": { vi: "Hủy bỏ hóa đơn", en: "Void Invoice" },
  "admin.invoices.deleteTitle": {
    vi: "Xóa bản nháp hóa đơn này?",
    en: "Delete this draft invoice?",
  },
  "admin.invoices.deleteBody": {
    vi: "Thao tác này sẽ xóa vĩnh viễn bản nháp hóa đơn {invoiceNumber}. Không thể hoàn tác.",
    en: "This will permanently delete draft invoice {invoiceNumber}. This action cannot be undone.",
  },
  "admin.invoices.deleteInvoice": { vi: "Xóa hóa đơn", en: "Delete Invoice" },
  "admin.invoices.rejectPaymentTitle": { vi: "Từ chối thanh toán", en: "Reject Payment" },
  "admin.invoices.rejectReasonLabel": { vi: "Lý do từ chối", en: "Rejection Reason" },
  "admin.invoices.rejectReasonPlaceholder": {
    vi: "VD: Không xác minh được thanh toán, sai số tiền, v.v.",
    en: "e.g. Could not verify payment, wrong amount, etc.",
  },
  "admin.invoices.rejectReasonHelp": {
    vi: "Người mua sẽ thấy lý do này.",
    en: "The buyer will see this reason.",
  },
  "admin.invoices.amountLabel": { vi: "Số tiền", en: "Amount" },
  "admin.invoices.amountHelp": {
    vi: "Còn lại: {amount}",
    en: "Outstanding: {amount}",
  },
  "admin.invoices.paymentMethodLabel": {
    vi: "Phương thức thanh toán",
    en: "Payment Method",
  },
  "admin.invoices.referenceNumberLabel": {
    vi: "Số tham chiếu",
    en: "Reference Number",
  },
  "admin.invoices.referenceNumberPlaceholder": {
    vi: "VD: Số séc, mã chuyển khoản",
    en: "e.g. Check #, transfer ID",
  },
  "admin.invoices.referenceNumberHelp": {
    vi: "Tùy chọn. Dùng cho lưu trữ nội bộ.",
    en: "Optional. For your records.",
  },

  // ---------------------------------------------------------------------------
  // Admin: Settings
  // ---------------------------------------------------------------------------
  "admin.settings.title": { vi: "Cài đặt", en: "Settings" },
  "admin.settings.searchPlaceholder": { vi: "Tìm kiếm cài đặt...", en: "Search settings..." },
  "admin.settings.noMatches": { vi: "Không có cài đặt nào khớp với tìm kiếm của bạn.", en: "No settings match your search." },
  "admin.settings.unsavedChanges": { vi: "Bạn có thay đổi chưa lưu", en: "You have unsaved changes" },
  "admin.settings.discard": { vi: "Huỷ bỏ", en: "Discard" },
  "admin.settings.save": { vi: "Lưu", en: "Save" },
  "admin.settings.tabGeneral": { vi: "Chung", en: "General" },
  "admin.settings.tabWholesale": { vi: "Bán sỉ", en: "Wholesale" },
  "admin.settings.tabDisplay": { vi: "Hiển thị Cổng", en: "Portal Display" },
  "admin.settings.tabNotifications": { vi: "Thông báo", en: "Notifications" },
  "admin.settings.tabPayments": { vi: "Thanh toán", en: "Payments" },
  "admin.settings.tabTiers": { vi: "Bậc giá", en: "Pricing Tiers" },
  "admin.settings.tabShipping": { vi: "Vận chuyển", en: "Shipping" },
  "admin.settings.tabIntegrations": { vi: "Tích hợp", en: "Integrations" },
  "admin.settings.tabSupport": { vi: "Hỗ trợ & FAQ", en: "Support & FAQ" },
  "admin.settings.tabSessions": { vi: "Phiên đang hoạt động", en: "Active Sessions" },
  "admin.settings.companyInformation": { vi: "Thông tin công ty", en: "Company Information" },
  "admin.settings.companyName": { vi: "Tên công ty", en: "Company Name" },
  "admin.settings.contactEmail": { vi: "Email liên hệ", en: "Contact Email" },
  "admin.settings.companyLogoUrl": { vi: "URL logo công ty", en: "Company Logo URL" },
  "admin.settings.companyLogoHelp": { vi: "Nhập URL tới hình logo của công ty", en: "Enter a URL to your company logo image" },
  "admin.settings.currency": { vi: "Tiền tệ", en: "Currency" },
  "admin.settings.currencyHelp": { vi: "Tự động đồng bộ từ Sapo", en: "Auto-synced from Sapo" },
  "admin.settings.timezone": { vi: "Múi giờ", en: "Timezone" },
  "admin.settings.saveGeneralSettings": { vi: "Lưu cài đặt chung", en: "Save General Settings" },
  "admin.settings.companyAddress": { vi: "Địa chỉ công ty", en: "Company Address" },
  "admin.settings.addressLine1": { vi: "Địa chỉ dòng 1", en: "Address Line 1" },
  "admin.settings.addressLine2": { vi: "Địa chỉ dòng 2", en: "Address Line 2" },
  "admin.settings.city": { vi: "Thành phố", en: "City" },
  "admin.settings.provinceState": { vi: "Tỉnh / Bang", en: "Province / State" },
  "admin.settings.postalCode": { vi: "Mã bưu điện", en: "Postal Code" },
  "admin.settings.country": { vi: "Quốc gia", en: "Country" },
  "admin.settings.saveAddress": { vi: "Lưu địa chỉ", en: "Save Address" },
  "admin.settings.wholesaleSettings": { vi: "Cài đặt bán sỉ", en: "Wholesale Settings" },
  "admin.settings.enableWholesale": { vi: "Bật bán sỉ", en: "Enable Wholesale" },
  "admin.settings.defaultPaymentTerms": { vi: "Điều khoản thanh toán mặc định", en: "Default Payment Terms" },
  "admin.settings.invoicePrefix": { vi: "Tiền tố hóa đơn", en: "Invoice Prefix" },
  "admin.settings.invoicePrefixHelp": { vi: "Hoá đơn tiếp theo: {next}", en: "Next invoice: {next}" },
  "admin.settings.orderPrefix": { vi: "Tiền tố đơn hàng", en: "Order Prefix" },
  "admin.settings.orderPrefixHelp": { vi: "Đơn hàng tiếp theo: {next}", en: "Next order: {next}" },
  "admin.settings.saveWholesaleSettings": { vi: "Lưu cài đặt bán sỉ", en: "Save Wholesale Settings" },
  "admin.settings.invoiceDefaults": { vi: "Mặc định hoá đơn", en: "Invoice Defaults" },
  "admin.settings.defaultInvoiceNotes": { vi: "Ghi chú hoá đơn mặc định", en: "Default Invoice Notes" },
  "admin.settings.defaultInvoiceNotesHelp": { vi: "Hiển thị mặc định trên mọi hoá đơn", en: "Appears on every invoice by default" },
  "admin.settings.saveInvoiceSettings": { vi: "Lưu cài đặt hoá đơn", en: "Save Invoice Settings" },
  "admin.settings.quotesRfq": { vi: "Báo giá / RFQ", en: "Quotes / RFQ" },
  "admin.settings.enableQuotes": { vi: "Bật báo giá", en: "Enable Quotes" },
  "admin.settings.quotePrefix": { vi: "Tiền tố báo giá", en: "Quote Prefix" },
  "admin.settings.defaultExpiryDays": { vi: "Hạn mặc định (ngày)", en: "Default Expiry (days)" },
  "admin.settings.saveQuotesSettings": { vi: "Lưu cài đặt báo giá", en: "Save Quotes Settings" },
  "admin.settings.portalDisplaySettings": { vi: "Cài đặt hiển thị cổng", en: "Portal Display Settings" },
  "admin.settings.showProductSku": { vi: "Hiển thị SKU sản phẩm", en: "Show Product SKU" },
  "admin.settings.showStockInfo": { vi: "Hiển thị thông tin tồn kho", en: "Show Stock Information" },
  "admin.settings.showBasePrice": { vi: "Hiển thị giá gốc", en: "Show Base Price" },
  "admin.settings.showWholesalePrice": { vi: "Hiển thị giá sỉ", en: "Show Wholesale Price" },
  "admin.settings.hideSoldOutProducts": { vi: "Ẩn sản phẩm hết hàng", en: "Hide Sold Out Products" },
  "admin.settings.orderProcessingMode": { vi: "Chế độ xử lý đơn hàng", en: "Order Processing Mode" },
  "admin.settings.submitForReview": { vi: "Gửi để xét duyệt", en: "Submit for Review" },
  "admin.settings.autoApprove": { vi: "Tự động duyệt", en: "Auto-Approve" },
  "admin.settings.portalBrowseMode": { vi: "Chế độ duyệt cổng", en: "Portal Browse Mode" },
  "admin.settings.catalogBrowseOrder": { vi: "Danh mục (Duyệt & Đặt hàng)", en: "Catalog (Browse & Order)" },
  "admin.settings.lineSheetOnly": { vi: "Chỉ Line Sheet", en: "Line Sheet Only" },
  "admin.settings.brandingAccentColor": { vi: "Màu nhấn thương hiệu", en: "Branding Accent Color" },
  "admin.settings.brandingAccentColorHelp": { vi: "Mã màu hex cho cổng (vd: #111827)", en: "Hex color code for portal accent (e.g., #111827)" },
  "admin.settings.saveDisplaySettings": { vi: "Lưu cài đặt hiển thị", en: "Save Display Settings" },
  "admin.settings.featureToggles": { vi: "Bật/Tắt tính năng", en: "Feature Toggles" },
  "admin.settings.shipWindows": { vi: "Khung thời gian giao hàng", en: "Ship Windows" },
  "admin.settings.assortmentPlanning": { vi: "Lập kế hoạch tập hợp", en: "Assortment Planning" },
  "admin.settings.multiCurrency": { vi: "Đa tiền tệ", en: "Multi-Currency" },
  "admin.settings.newArrivalDays": { vi: "Số ngày Hàng mới về", en: "New Arrival Days" },
  "admin.settings.newArrivalDaysHelp": { vi: "Sản phẩm nhập trong khoảng thời gian này sẽ được đánh dấu là hàng mới", en: "Products imported within this many days are marked as new arrivals" },
  "admin.settings.saveFeatureToggles": { vi: "Lưu bật/tắt tính năng", en: "Save Feature Toggles" },
  "admin.settings.pricingTiers": { vi: "Bậc giá", en: "Pricing Tiers" },
  "admin.settings.addNewTier": { vi: "Thêm bậc mới", en: "Add New Tier" },
  "admin.settings.tierName": { vi: "Tên bậc", en: "Tier Name" },
  "admin.settings.tierNamePlaceholder": { vi: "VD: Khách hàng chiến lược", en: "e.g., Key Account" },
  "admin.settings.discountType": { vi: "Loại chiết khấu", en: "Discount Type" },
  "admin.settings.percentage": { vi: "Phần trăm", en: "Percentage" },
  "admin.settings.fixedAmount": { vi: "Số tiền cố định", en: "Fixed Amount" },
  "admin.settings.discountValue": { vi: "Giá trị chiết khấu", en: "Discount Value" },
  "admin.settings.addTier": { vi: "Thêm bậc", en: "Add Tier" },
  "admin.settings.tierBuyers": { vi: "{count} người mua", en: "{count} buyers" },
  "admin.settings.tierPercentOff": { vi: "Giảm {value}%", en: "{value}% off" },
  "admin.settings.tierFixedOff": { vi: "Giảm ${value}", en: "${value} off" },
  "admin.settings.edit": { vi: "Chỉnh sửa", en: "Edit" },
  "admin.settings.delete": { vi: "Xoá", en: "Delete" },
  "admin.settings.deleteTierConfirm": { vi: "Xoá bậc \"{name}\"?", en: "Delete tier \"{name}\"?" },
  "admin.settings.extraDiscountPercent": { vi: "Giảm thêm %", en: "Extra Discount %" },
  "admin.settings.shippingDiscount": { vi: "Chiết khấu vận chuyển", en: "Shipping Discount" },
  "admin.settings.storeDefault": { vi: "Mặc định của cửa hàng", en: "Store Default" },
  "admin.settings.flatRate": { vi: "Mức cố định", en: "Flat Rate" },
  "admin.settings.freeShipping": { vi: "Miễn phí vận chuyển", en: "Free Shipping" },
  "admin.settings.shippingFlatRate": { vi: "Mức vận chuyển cố định", en: "Shipping Flat Rate" },
  "admin.settings.dontCollectTax": { vi: "Không thu thuế", en: "Don't Collect Tax" },
  "admin.settings.cancel": { vi: "Huỷ", en: "Cancel" },
  "admin.settings.supportContact": { vi: "Liên hệ hỗ trợ", en: "Support Contact" },
  "admin.settings.supportEmail": { vi: "Email hỗ trợ", en: "Support Email" },
  "admin.settings.supportPhone": { vi: "Điện thoại hỗ trợ", en: "Support Phone" },
  "admin.settings.saveSupportSettings": { vi: "Lưu cài đặt hỗ trợ", en: "Save Support Settings" },
  "admin.settings.faq": { vi: "FAQ", en: "FAQ" },
  "admin.settings.question": { vi: "Câu hỏi", en: "Question" },
  "admin.settings.answer": { vi: "Câu trả lời", en: "Answer" },
  "admin.settings.addFaq": { vi: "Thêm FAQ", en: "Add FAQ" },
  "admin.settings.exchangeRates": { vi: "Tỷ giá hối đoái", en: "Exchange Rates" },
  "admin.settings.from": { vi: "Từ", en: "From" },
  "admin.settings.to": { vi: "Đến", en: "To" },
  "admin.settings.rate": { vi: "Tỷ giá", en: "Rate" },
  "admin.settings.addRate": { vi: "Thêm tỷ giá", en: "Add Rate" },
  "admin.settings.activeSessions": { vi: "Phiên đang hoạt động", en: "Active Sessions" },
  "admin.settings.activeSessionsSubtitle": { vi: "Quản lý các phiên đăng nhập admin", en: "Manage active admin login sessions" },
  "admin.settings.activeSessionsBody": { vi: "Quản lý phiên có sẵn trong cài đặt người dùng admin.", en: "Session management is available in the admin user settings." },
  "admin.settings.stripeIntegration": { vi: "Tích hợp Stripe", en: "Stripe Integration" },
  "admin.settings.stripeSecretKey": { vi: "Stripe Secret Key", en: "Stripe Secret Key" },
  "admin.settings.stripeSecretKeyHelp": { vi: "sk_live_... hoặc sk_test_...", en: "sk_live_... or sk_test_..." },
  "admin.settings.stripeWebhookSecret": { vi: "Stripe Webhook Secret", en: "Stripe Webhook Secret" },
  "admin.settings.stripeWebhookSecretHelp": { vi: "whsec_...", en: "whsec_..." },
  "admin.settings.saveStripeSettings": { vi: "Lưu cài đặt Stripe", en: "Save Stripe Settings" },
  "admin.settings.shippingZones": { vi: "Khu vực vận chuyển", en: "Shipping Zones" },
  "admin.settings.shippingFlatRateLabel": { vi: "Cố định: {amount}", en: "Flat rate: {amount}" },
  "admin.settings.shippingFree": { vi: "Miễn phí vận chuyển", en: "Free shipping" },
  "admin.settings.shippingWeightBased": { vi: "Theo trọng lượng", en: "Weight-based" },
  "admin.settings.shippingFreeOver": { vi: " (miễn phí trên {amount})", en: " (free over {amount})" },
  "admin.settings.addShippingZone": { vi: "Thêm khu vực vận chuyển", en: "Add Shipping Zone" },
  "admin.settings.zoneName": { vi: "Tên khu vực", en: "Zone Name" },
  "admin.settings.zoneNamePlaceholder": { vi: "VD: Nội địa", en: "e.g., Domestic" },
  "admin.settings.countries": { vi: "Quốc gia", en: "Countries" },
  "admin.settings.countriesPlaceholder": { vi: "US, CA, GB", en: "US, CA, GB" },
  "admin.settings.countriesHelp": { vi: "Mã quốc gia, phân tách bằng dấu phẩy", en: "Comma-separated country codes" },
  "admin.settings.rateType": { vi: "Loại phí", en: "Rate Type" },
  "admin.settings.rateTypeFlat": { vi: "Phí cố định", en: "Flat Rate" },
  "admin.settings.rateTypeWeight": { vi: "Theo trọng lượng", en: "Weight-Based" },
  "admin.settings.rateTypeFree": { vi: "Miễn phí", en: "Free" },
  "admin.settings.freeShippingMin": { vi: "Miễn phí tối thiểu", en: "Free Shipping Min." },
  "admin.settings.addZone": { vi: "Thêm khu vực", en: "Add Zone" },
  "admin.settings.orderNotifications": { vi: "Thông báo đơn hàng", en: "Order Notifications" },
  "admin.settings.orderNotificationEmail": { vi: "Email thông báo đơn hàng", en: "Order Notification Email" },
  "admin.settings.orderNotificationEmailHelp": { vi: "Nhận email khi có đơn hàng mới", en: "Receive email when new orders are placed" },
  "admin.settings.sendOrderConfirmation": { vi: "Gửi xác nhận đơn hàng cho người mua", en: "Send Order Confirmation to Buyers" },
  "admin.settings.saveNotificationSettings": { vi: "Lưu cài đặt thông báo", en: "Save Notification Settings" },
  "admin.settings.applicationNotifications": { vi: "Thông báo đăng ký", en: "Application Notifications" },
  "admin.settings.sendAppReceived": { vi: "Gửi email khi nhận đơn đăng ký", en: "Send Application Received Email" },
  "admin.settings.sendAppApproved": { vi: "Gửi email khi duyệt đơn đăng ký", en: "Send Application Approved Email" },
  "admin.settings.sendAppRejected": { vi: "Gửi email khi từ chối đơn đăng ký", en: "Send Application Rejected Email" },
  "admin.settings.sendTestEmail": { vi: "Gửi email thử", en: "Send Test Email" },
  "admin.settings.sendTestEmailSubtitle": { vi: "Gửi email thử để xác minh cấu hình thông báo đang hoạt động.", en: "Send a test email to verify your email notification configuration is working." },
  "admin.settings.recipientEmail": { vi: "Email người nhận", en: "Recipient Email" },
  "admin.settings.recipientEmailHelp": { vi: "Email thử sẽ được gửi đến email thông báo hoặc email cửa hàng của bạn", en: "Test email will be sent to your notification email or shop email" },
  "admin.settings.integrationHealthCheck": { vi: "Kiểm tra trạng thái tích hợp", en: "Integration Health Check" },
  "admin.settings.integrationHealthCheckSubtitle": { vi: "Kiểm tra trạng thái kết nối của các dịch vụ tích hợp.", en: "Check the connection status of all integrated services." },
  "admin.settings.sapoApi": { vi: "Sapo API", en: "Sapo API" },
  "admin.settings.stripe9pay": { vi: "Stripe (9Pay)", en: "Stripe (9Pay)" },
  "admin.settings.emailResend": { vi: "Email (Resend)", en: "Email (Resend)" },
  "admin.settings.connected": { vi: "Đã kết nối", en: "Connected" },
  "admin.settings.errorMessage": { vi: "Lỗi: {message}", en: "Error: {message}" },
  "admin.settings.notChecked": { vi: "Chưa kiểm tra", en: "Not checked" },
  "admin.settings.runHealthCheck": { vi: "Chạy kiểm tra", en: "Run Health Check" },
  "admin.settings.sapoConnection": { vi: "Kết nối Sapo", en: "Sapo Connection" },
  "admin.settings.notConnected": { vi: "Chưa kết nối", en: "Not Connected" },
  "admin.settings.sapoStoreDomain": { vi: "Tên miền cửa hàng Sapo", en: "Sapo Store Domain" },
  "admin.settings.sapoStoreDomainHelp": { vi: "Tên miền cửa hàng Sapo (vd: your-store.mysapo.net)", en: "Your Sapo store domain (e.g., your-store.mysapo.net)" },
  "admin.settings.privateAppAuth": { vi: "Xác thực Private App (Khuyến nghị)", en: "Private App Authentication (Recommended)" },
  "admin.settings.privateAppAuthHelp": { vi: "Tạo Private App trong Sapo Web admin → Applications → Personal app", en: "Create a Private App in your Sapo Web admin → Applications → Personal app" },
  "admin.settings.apiKey": { vi: "API Key", en: "API Key" },
  "admin.settings.apiKeyHelp": { vi: "Lấy từ Private App trong Sapo Web admin", en: "From your Private App in Sapo Web admin" },
  "admin.settings.apiSecret": { vi: "API Secret", en: "API Secret" },
  "admin.settings.apiSecretHelp": { vi: "Lấy từ Private App trong Sapo Web admin", en: "From your Private App in Sapo Web admin" },
  "admin.settings.oauthAccessToken": { vi: "OAuth Access Token (Thay thế)", en: "OAuth Access Token (Alternative)" },
  "admin.settings.sapoAccessToken": { vi: "Sapo Access Token", en: "Sapo Access Token" },
  "admin.settings.sapoAccessTokenHelp": { vi: "Lấy từ quy trình cấp quyền OAuth", en: "From OAuth app authorization flow" },
  "admin.settings.saveConnection": { vi: "Lưu kết nối", en: "Save Connection" },
  "admin.settings.sapoIntegration": { vi: "Tích hợp Sapo", en: "Sapo Integration" },
  "admin.settings.orderSyncMode": { vi: "Chế độ đồng bộ đơn hàng", en: "Order Sync Mode" },
  "admin.settings.none": { vi: "Không (Không đồng bộ)", en: "None (Don't sync)" },
  "admin.settings.createSapoOrder": { vi: "Tạo đơn hàng Sapo", en: "Create Sapo Order" },
  "admin.settings.inventoryReservation": { vi: "Giữ hàng tồn kho", en: "Inventory Reservation" },
  "admin.settings.neverReserve": { vi: "Không bao giờ giữ", en: "Never Reserve" },
  "admin.settings.onApproval": { vi: "Khi duyệt", en: "On Approval" },
  "admin.settings.onSubmission": { vi: "Khi gửi đơn", en: "On Submission" },
  "admin.settings.reservationDays": { vi: "Số ngày giữ hàng", en: "Reservation Days" },
  "admin.settings.syncFulfillmentToSapo": { vi: "Đồng bộ giao hàng với Sapo", en: "Sync Fulfillment to Sapo" },
  "admin.settings.saveIntegrationSettings": { vi: "Lưu cài đặt tích hợp", en: "Save Integration Settings" },
  "admin.settings.dueOnReceipt": { vi: "Thanh toán ngay", en: "Due on Receipt" },
  "admin.settings.net15": { vi: "Net 15", en: "Net 15" },
  "admin.settings.net30": { vi: "Net 30", en: "Net 30" },
  "admin.settings.net45": { vi: "Net 45", en: "Net 45" },
  "admin.settings.net60": { vi: "Net 60", en: "Net 60" },
  "admin.settings.net90": { vi: "Net 90", en: "Net 90" },

  // ---------------------------------------------------------------------------
  // Admin: Settings - Shipping Carriers
  // ---------------------------------------------------------------------------
  "admin.settingsShipping.breadcrumbSettings": { vi: "Cài đặt", en: "Settings" },
  "admin.settingsShipping.breadcrumbCarriers": { vi: "Đơn vị vận chuyển", en: "Shipping Carriers" },
  "admin.settingsShipping.title": { vi: "Đơn vị vận chuyển", en: "Shipping Carriers" },
  "admin.settingsShipping.subtitle": { vi: "Kết nối các đơn vị vận chuyển Việt Nam để có phí thời gian thực và tạo nhãn", en: "Connect Vietnamese shipping carriers for real-time rates and label generation" },
  "admin.settingsShipping.savedBanner": { vi: "Đã lưu cài đặt đơn vị vận chuyển.", en: "Carrier settings saved." },
  "admin.settingsShipping.ghnName": { vi: "GHN (Giao Hàng Nhanh)", en: "GHN (Giao Hang Nhanh)" },
  "admin.settingsShipping.ghnDescription": { vi: "Đơn vị vận chuyển phổ biến nhất Việt Nam. Giao nhanh, phủ rộng khắp.", en: "Most popular carrier in Vietnam. Fast delivery with extensive coverage." },
  "admin.settingsShipping.ghtkName": { vi: "GHTK (Giao Hàng Tiết Kiệm)", en: "GHTK (Giao Hang Tiet Kiem)" },
  "admin.settingsShipping.ghtkDescription": { vi: "Vận chuyển tiết kiệm với phí cạnh tranh trên toàn Việt Nam.", en: "Economy shipping with competitive rates across Vietnam." },
  "admin.settingsShipping.viettelPostName": { vi: "Viettel Post", en: "Viettel Post" },
  "admin.settingsShipping.viettelPostDescription": { vi: "Đơn vị vận chuyển nhà nước với độ phủ rộng nhất (hơn 8,200 điểm).", en: "Government-backed carrier with widest coverage (8,200+ service points)." },
  "admin.settingsShipping.fieldApiToken": { vi: "API Token", en: "API Token" },
  "admin.settingsShipping.fieldGhnShopId": { vi: "GHN Shop ID", en: "GHN Shop ID" },
  "admin.settingsShipping.fieldUseSandbox": { vi: "Dùng Sandbox (Kiểm thử)", en: "Use Sandbox (Testing)" },
  "admin.settingsShipping.fieldPartnerCode": { vi: "Mã đối tác (tùy chọn)", en: "Partner Code (optional)" },
  "admin.settingsShipping.active": { vi: "Hoạt động", en: "Active" },
  "admin.settingsShipping.disabled": { vi: "Đã tắt", en: "Disabled" },
  "admin.settingsShipping.disable": { vi: "Tắt", en: "Disable" },
  "admin.settingsShipping.enable": { vi: "Bật", en: "Enable" },
  "admin.settingsShipping.fieldPlaceholder": { vi: "Nhập {label}", en: "Enter {label}" },
  "admin.settingsShipping.update": { vi: "Cập nhật", en: "Update" },
  "admin.settingsShipping.connect": { vi: "Kết nối", en: "Connect" },
  "admin.settingsShipping.cancel": { vi: "Huỷ", en: "Cancel" },
  "admin.settingsShipping.connectedSingle": { vi: "Đã kết nối với {count} thông tin xác thực.", en: "Carrier connected with {count} credential configured." },
  "admin.settingsShipping.connectedMulti": { vi: "Đã kết nối với {count} thông tin xác thực.", en: "Carrier connected with {count} credentials configured." },
  "admin.settingsShipping.editCredentials": { vi: "Sửa thông tin xác thực", en: "Edit Credentials" },
  "admin.settingsShipping.errorLoading": { vi: "Xảy ra lỗi khi tải cài đặt vận chuyển.", en: "An error occurred loading shipping settings." },

  // ---------------------------------------------------------------------------
  // Admin: Staff Settings
  // ---------------------------------------------------------------------------
  "admin.staffSettings.breadcrumbSettings": { vi: "Cài đặt", en: "Settings" },
  "admin.staffSettings.breadcrumbStaff": { vi: "Nhân viên & Phân quyền", en: "Staff & Permissions" },
  "admin.staffSettings.title": { vi: "Nhân viên & Phân quyền", en: "Staff & Permissions" },
  "admin.staffSettings.subtitle": { vi: "Quản lý người dùng admin và mức truy cập của họ", en: "Manage admin users and their access levels" },
  "admin.staffSettings.addStaffMember": { vi: "Thêm nhân viên", en: "Add Staff Member" },
  "admin.staffSettings.columnName": { vi: "Tên", en: "Name" },
  "admin.staffSettings.columnRole": { vi: "Vai trò", en: "Role" },
  "admin.staffSettings.columnStatus": { vi: "Trạng thái", en: "Status" },
  "admin.staffSettings.columnLastLogin": { vi: "Đăng nhập gần nhất", en: "Last Login" },
  "admin.staffSettings.statusActive": { vi: "Đang hoạt động", en: "Active" },
  "admin.staffSettings.statusInactive": { vi: "Ngưng hoạt động", en: "Inactive" },
  "admin.staffSettings.never": { vi: "Chưa bao giờ", en: "Never" },
  "admin.staffSettings.permissions": { vi: "Phân quyền", en: "Permissions" },
  "admin.staffSettings.deactivate": { vi: "Ngưng kích hoạt", en: "Deactivate" },
  "admin.staffSettings.activate": { vi: "Kích hoạt", en: "Activate" },
  "admin.staffSettings.remove": { vi: "Xoá", en: "Remove" },
  "admin.staffSettings.removeConfirm": { vi: "Xoá {name}?", en: "Remove {name}?" },
  "admin.staffSettings.staffActivity": { vi: "Hoạt động nhân viên", en: "Staff Activity" },
  "admin.staffSettings.lastLoginLabel": { vi: "Đăng nhập gần nhất: {date}", en: "Last login: {date}" },
  "admin.staffSettings.actionsThisWeek": { vi: "{count} hành động tuần này", en: "{count} actions this week" },
  "admin.staffSettings.noRecentActivity": { vi: "Không có hoạt động gần đây", en: "No recent activity" },
  "admin.staffSettings.add": { vi: "Thêm", en: "Add" },
  "admin.staffSettings.firstName": { vi: "Tên", en: "First Name" },
  "admin.staffSettings.lastName": { vi: "Họ", en: "Last Name" },
  "admin.staffSettings.email": { vi: "Email", en: "Email" },
  "admin.staffSettings.password": { vi: "Mật khẩu", en: "Password" },
  "admin.staffSettings.passwordHelp": { vi: "Tối thiểu 10 ký tự, 1 chữ hoa, 1 chữ thường, 1 số", en: "Min 10 chars, 1 uppercase, 1 lowercase, 1 number" },
  "admin.staffSettings.role": { vi: "Vai trò", en: "Role" },
  "admin.staffSettings.roleAdmin": { vi: "Admin — Toàn quyền (có thể chỉnh)", en: "Admin — Full access (editable)" },
  "admin.staffSettings.roleEditor": { vi: "Editor — Mặc định chỉ xem (có thể chỉnh)", en: "Editor — View-only by default (editable)" },
  "admin.staffSettings.editPermissions": { vi: "Sửa phân quyền", en: "Edit Permissions" },
  "admin.staffSettings.savePermissions": { vi: "Lưu phân quyền", en: "Save Permissions" },
  "admin.staffSettings.permissionsHelp": { vi: "Đặt mức truy cập cho từng khu vực. \"Full\" = đọc & ghi, \"View\" = chỉ đọc, \"None\" = ẩn.", en: "Set access level for each area. \"Full\" = read & write, \"View\" = read only, \"None\" = hidden." },
  "admin.staffSettings.levelFull": { vi: "full", en: "full" },
  "admin.staffSettings.levelView": { vi: "view", en: "view" },
  "admin.staffSettings.levelNone": { vi: "none", en: "none" },
  "admin.staffSettings.saved": { vi: "Đã lưu", en: "Saved" },

  // ---------------------------------------------------------------------------
  // Admin: Support
  // ---------------------------------------------------------------------------
  "admin.support.title": { vi: "Phiếu hỗ trợ", en: "Support Tickets" },
  "admin.support.countSingular": { vi: "{count} phiếu", en: "{count} ticket" },
  "admin.support.countPlural": { vi: "{count} phiếu", en: "{count} tickets" },
  "admin.support.filterByStatus": { vi: "Lọc theo trạng thái", en: "Filter by Status" },
  "admin.support.allTickets": { vi: "Tất cả phiếu", en: "All Tickets" },
  "admin.support.statusOpen": { vi: "Đang mở", en: "Open" },
  "admin.support.statusResponded": { vi: "Đã phản hồi", en: "Responded" },
  "admin.support.statusClosed": { vi: "Đã đóng", en: "Closed" },
  "admin.support.emptyFiltered": { vi: "Không có phiếu nào khớp bộ lọc.", en: "No tickets match the selected filter." },
  "admin.support.emptyAll": { vi: "Chưa có phiếu hỗ trợ nào. Phiếu sẽ xuất hiện khi người mua gửi yêu cầu qua cổng.", en: "No support tickets yet. Tickets appear when buyers submit support requests through the portal." },
  "admin.support.columnTicketId": { vi: "Mã phiếu", en: "Ticket ID" },
  "admin.support.columnSubject": { vi: "Tiêu đề", en: "Subject" },
  "admin.support.columnBuyer": { vi: "Người mua", en: "Buyer" },
  "admin.support.columnOrder": { vi: "Đơn hàng", en: "Order" },
  "admin.support.columnStatus": { vi: "Trạng thái", en: "Status" },
  "admin.support.columnCreated": { vi: "Ngày tạo", en: "Created" },
  "admin.support.errorTitle": { vi: "Đã xảy ra lỗi", en: "Something went wrong" },
  "admin.support.errorBody": { vi: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.", en: "An unexpected error occurred. Please try again." },
  "admin.support.errorGeneric": { vi: "Đã xảy ra lỗi khi tải trang.", en: "An error occurred loading this page." },
  "admin.support.backLink": { vi: "Phiếu hỗ trợ", en: "Support Tickets" },
  "admin.support.backToSupport": { vi: "Quay lại Hỗ trợ", en: "Back to Support" },
  "admin.support.originalMessage": { vi: "Tin nhắn gốc", en: "Original Message" },
  "admin.support.replyTitle": { vi: "Trả lời", en: "Reply" },
  "admin.support.yourResponse": { vi: "Nội dung phản hồi", en: "Your response" },
  "admin.support.replyPlaceholder": { vi: "Nhập nội dung trả lời người mua...", en: "Type your reply to the buyer..." },
  "admin.support.sendReply": { vi: "Gửi phản hồi", en: "Send Reply" },
  "admin.support.replyHelp": { vi: "Người mua sẽ nhận email thông báo với phản hồi của bạn. Trạng thái phiếu sẽ chuyển sang \"Đã phản hồi\".", en: "The buyer will receive an email notification with your response. The ticket status will be updated to \"Responded\"." },
  "admin.support.actions": { vi: "Thao tác", en: "Actions" },
  "admin.support.closeTicket": { vi: "Đóng phiếu", en: "Close Ticket" },
  "admin.support.reopenTicket": { vi: "Mở lại phiếu", en: "Reopen Ticket" },
  "admin.support.ticketClosedTitle": { vi: "Phiếu đã đóng", en: "This ticket is closed" },
  "admin.support.ticketClosedBody": { vi: "Phiếu hỗ trợ này đã được đóng. Bạn có thể mở lại nếu cần xử lý thêm.", en: "This support ticket has been closed. You can reopen it if further follow-up is needed." },
  "admin.support.errorBanner": { vi: "Lỗi", en: "Error" },
  "admin.support.ticketIdLabel": { vi: "Mã phiếu", en: "Ticket ID" },
  "admin.support.statusLabel": { vi: "Trạng thái", en: "Status" },
  "admin.support.createdLabel": { vi: "Ngày tạo", en: "Created" },
  "admin.support.updatedLabel": { vi: "Cập nhật", en: "Updated" },
  "admin.support.buyer": { vi: "Người mua", en: "Buyer" },
  "admin.support.linkedOrder": { vi: "Đơn hàng liên kết", en: "Linked Order" },
  "admin.support.total": { vi: "Tổng", en: "Total" },
  "admin.support.errorMissingTicket": { vi: "Thiếu mã phiếu", en: "Missing ticket ID" },
  "admin.support.errorReplyRequired": { vi: "Nội dung phản hồi là bắt buộc", en: "Reply message is required" },
  "admin.support.errorNotFound": { vi: "Không tìm thấy phiếu", en: "Ticket not found" },
  "admin.support.errorUnknownAction": { vi: "Hành động không xác định", en: "Unknown action" },
  "admin.support.replySentSuccess": { vi: "Đã gửi phản hồi thành công", en: "Reply sent successfully" },
  "admin.support.ticketClosed": { vi: "Đã đóng phiếu", en: "Ticket closed" },
  "admin.support.ticketReopened": { vi: "Đã mở lại phiếu", en: "Ticket reopened" },

  // ---------------------------------------------------------------------------
  // Admin: Notifications
  // ---------------------------------------------------------------------------
  "admin.notifications.title": { vi: "Thông báo", en: "Notifications" },
  "admin.notifications.unreadBadge": { vi: "{count} chưa đọc", en: "{count} unread" },
  "admin.notifications.markAllAsRead": { vi: "Đánh dấu tất cả đã đọc", en: "Mark all as read" },
  "admin.notifications.marking": { vi: "Đang đánh dấu...", en: "Marking..." },
  "admin.notifications.tabAll": { vi: "Tất cả", en: "All" },
  "admin.notifications.tabOrders": { vi: "Đơn hàng", en: "Orders" },
  "admin.notifications.tabInventory": { vi: "Hàng tồn kho", en: "Inventory" },
  "admin.notifications.tabSystem": { vi: "Hệ thống", en: "System" },
  "admin.notifications.emptyAll": { vi: "Chưa có thông báo nào.", en: "No notifications yet." },
  "admin.notifications.emptyFiltered": { vi: "Không có thông báo {filter}.", en: "No {filter} notifications." },
  "admin.notifications.emptyHelp": { vi: "Bạn sẽ được thông báo về đơn hàng, thanh toán và các hoạt động khác.", en: "You will be notified about orders, payments, and other activity." },
  "admin.notifications.groupToday": { vi: "Hôm nay", en: "Today" },
  "admin.notifications.groupYesterday": { vi: "Hôm qua", en: "Yesterday" },
  "admin.notifications.groupThisWeek": { vi: "Tuần này", en: "This Week" },
  "admin.notifications.groupEarlier": { vi: "Trước đó", en: "Earlier" },
  "admin.notifications.viewDetails": { vi: "Xem chi tiết", en: "View details" },
  "admin.notifications.showing": { vi: "Hiển thị {from} - {to} trên {total} thông báo", en: "Showing {from} - {to} of {total} notifications" },
  "admin.notifications.showingSingle": { vi: "Hiển thị {count} trên {total} thông báo", en: "Showing {count} of {total} notifications" },
  "admin.notifications.previous": { vi: "Trước", en: "Previous" },
  "admin.notifications.next": { vi: "Tiếp", en: "Next" },
  "admin.notifications.backToNotifications": { vi: "Quay lại Thông báo", en: "Back to Notifications" },
  "admin.notifications.filterOrders": { vi: "đơn hàng", en: "orders" },
  "admin.notifications.filterInventory": { vi: "hàng tồn kho", en: "inventory" },
  "admin.notifications.filterSystem": { vi: "hệ thống", en: "system" },

  // ---------------------------------------------------------------------------
  // Admin: Audit Log
  // ---------------------------------------------------------------------------
  "admin.auditLog.title": { vi: "Nhật ký kiểm toán", en: "Audit Log" },
  "admin.auditLog.subtitle": { vi: "Theo dõi mọi hành động và thay đổi của admin", en: "Track all admin actions and changes" },
  "admin.auditLog.searchPlaceholder": { vi: "Tìm nhật ký...", en: "Search logs..." },
  "admin.auditLog.tabAll": { vi: "Tất cả", en: "All" },
  "admin.auditLog.empty": { vi: "Không có mục nhật ký nào.", en: "No audit log entries found." },
  "admin.auditLog.columnDate": { vi: "Ngày", en: "Date" },
  "admin.auditLog.columnEntity": { vi: "Đối tượng", en: "Entity" },
  "admin.auditLog.columnAction": { vi: "Hành động", en: "Action" },
  "admin.auditLog.columnDescription": { vi: "Mô tả", en: "Description" },
  "admin.auditLog.columnBy": { vi: "Bởi", en: "By" },
  "admin.auditLog.backToAuditLog": { vi: "Quay lại Nhật ký kiểm toán", en: "Back to Audit Log" },
  "admin.auditLog.entityBuyer": { vi: "Buyer", en: "Buyer" },
  "admin.auditLog.entityInvoice": { vi: "Invoice", en: "Invoice" },
  "admin.auditLog.entityTier": { vi: "Tier", en: "Tier" },
  "admin.auditLog.entityPriceList": { vi: "PriceList", en: "PriceList" },
  "admin.auditLog.entitySettings": { vi: "Settings", en: "Settings" },
  "admin.auditLog.entityOrder": { vi: "Order", en: "Order" },
  "admin.auditLog.entityProduct": { vi: "Product", en: "Product" },
  "admin.auditLog.entityQuote": { vi: "Quote", en: "Quote" },
};

/**
 * Get a translated string.
 * Supports {placeholder} interpolation via optional params.
 *
 * @example
 *   t("addToCart", "vi") // "Thêm vào giỏ hàng"
 *   t("freeShipping", "en", { amount: "1,500,000₫" }) // "COMPLIMENTARY SHIPPING ON ORDERS OVER 1,500,000₫"
 */
export function t(
  key: string,
  locale: Locale,
  params?: Record<string, string | number>
): string {
  const entry = translations[key];
  if (!entry) return key;
  let text = entry[locale] || entry["en"] || key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(`{${k}}`, String(v));
    }
  }
  return text;
}

/**
 * T3.7: pluralization helper.
 *
 * English distinguishes singular/plural; Vietnamese does not (same form
 * for 0, 1, N items). Callers pick whichever variant of the translation
 * key matches the count and we resolve the right label.
 *
 * Convention: define two keys `<name>` (singular) and `<name>s` (plural).
 * If only a plural key exists, we use it for both counts.
 *
 * Example:
 *   t("product",  "en") // "product"
 *   t("products", "en") // "products"
 *   plural("product", 1, "en") // "product"
 *   plural("product", 5, "en") // "products"
 *   plural("product", 0, "vi") // "sản phẩm"
 */
export function plural(
  baseKey: string,
  count: number,
  locale: Locale,
  params?: Record<string, string | number>,
): string {
  // In Vietnamese, singular and plural are the same word — always fall back
  // to the singular key if present. In English, use the "s" variant for
  // anything other than exactly 1.
  const useSingular = locale === "vi" || count === 1;
  const key = useSingular ? baseKey : `${baseKey}s`;
  const merged = { count, ...(params || {}) };
  const result = t(key, locale, merged);
  // If the plural key doesn't exist, fall back to the singular
  if (result === key && !useSingular) return t(baseKey, locale, merged);
  return result;
}

/**
 * Get the announcements array for a given locale.
 *
 * `formattedShippingAmount` may be `null` for markets where we can't
 * confidently express the free-shipping threshold in the customer's
 * currency (e.g. an international market where the merchant hasn't
 * set a per-market `freeShippingThreshold`). In that case the shipping
 * announcement is dropped from rotation rather than misrepresented.
 */
export function getAnnouncements(
  locale: Locale,
  formattedShippingAmount: string | null,
  alwaysFree = false,
): string[] {
  const out: string[] = [];
  if (alwaysFree) {
    out.push(t("freeShippingAlways", locale));
  } else if (formattedShippingAmount) {
    out.push(t("freeShipping", locale, { amount: formattedShippingAmount }));
  }
  // (else: no shipping announcement — non-VN market without explicit threshold)
  // The EQL-era rotation ("new arrivals — S/S 2026", "easy returns", "10% off
  // your first order — join Diolo Rewards") is gone (2026-09-08): Diolo takes
  // exchanges only, issues no discounts, and retired points. What remains is
  // the shipping line here, plus the Club line and the admin-set announcement
  // the storefront appends.
  return out;
}
